import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Lock, Shield, Users, DollarSign, TrendingUp, Calendar, FileText, Target,
  Send, Plus, Trash2, Edit2, MapPin, Phone, Mail, Globe, BarChart3, PieChart,
  Briefcase, MessageCircle, Image, ChevronDown, ChevronUp, Star, CheckCircle,
  XCircle, Clock, ArrowUpRight, ArrowDownRight, Megaphone, Lightbulb, X,
  LogOut, Download, Smartphone, Flame, Activity, Award, ChevronRight, Zap,
  Layers, Landmark, UserCheck, Square, CheckSquare, Video, Bell, Eye, Heart,
  Linkedin, Upload, Camera, GripVertical, KeyRound, Code2, Server, RefreshCw, Info, Loader2
} from 'lucide-react';
import AdminLayout from '../../components/layout/AdminLayout';
import CheatSheet from '../../components/admin/CheatSheet';
import LiveTractionCard from '../../components/admin/LiveTractionCard';
import VelocityHeartbeat from '../../components/admin/VelocityHeartbeat';
import {
  OffersInstitutionalBrief,
  AcquisitionsInstitutionalBrief,
  FundraiseInstitutionalBrief,
  PRInstitutionalBrief,
} from '../../components/admin/VaultInstitutionalBriefs';
import { InvestorTelemetryChip, VaultTelemetryLeaderboard, MondayTelemetryDigestCard, ConversionFunnelRibbon, VaultDailyPulseCard } from '../../components/admin/InvestorTelemetry';
import { B2BContractEngine, RecommendOfferButton } from '../../components/admin/B2BContractEngine';
import { RoundsPlanCard } from '../../components/investors/RoundsPlanCard';
import { InstrumentsPanel } from '../../components/admin/InstrumentsPanel';
import { InvestorInsightPanel } from '../../components/admin/InvestorInsightPanel';
import { DiligenceLibrary } from '../../components/admin/DiligenceLibrary';
import { QualificationInbox } from '../../components/vault/QualificationInbox';
import { StrategicRoadmap } from '../../components/admin/StrategicRoadmap';
import { LiquidityHorizonPanel } from '../../components/admin/LiquidityHorizonPanel';
import { IRTimelinePanel } from '../../components/admin/IRTimelinePanel';
import { ReferralActivityCard } from '../../components/admin/ReferralActivityCard';
import InvestorPersonaCharts from '../../components/admin/InvestorPersonaCharts';
import CoOperatorCard from '../../components/admin/CoOperatorCard';
import VaultSamplePreview, {
  SAMPLES_INVESTORS, SAMPLES_MILESTONES, SAMPLES_KPIS, SAMPLES_FUNDRAISE,
  SAMPLES_ACQUISITIONS, SAMPLES_CONTACT_LOG, SAMPLES_NOTES, SAMPLES_CALENDAR,
  SAMPLES_NASDAQ, SAMPLES_PR_SOCIAL, SAMPLES_DATA_ROOM, SAMPLES_CAP_TABLE,
} from '../../components/admin/VaultSamplePreview';
import { apiClient } from '../../api/client';
import VaultMarginsPanel from '../../components/vault/VaultMarginsPanel';
import FinanceExpensesPanel from '../../components/vault/FinanceExpensesPanel';
import VaultTeamPanel from '../../components/vault/VaultTeamPanel';

// ─── Util: compact "x minutes/hours/days ago" string for grid cards ────────
const fmtRelTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diffMin = Math.max(0, Math.floor((Date.now() - d.getTime()) / 60000));
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h ago`;
    const diffDay = Math.floor(diffMin / 1440);
    if (diffDay < 30) return `${diffDay}d ago`;
    return d.toLocaleDateString();
  } catch {
    return '';
  }
};

// ─── TOTP Gate ───────────────────────────────────────────────────────────────

const TOTPGate = ({ onUnlock }) => {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    const init = async () => {
      // Trust the API. Do NOT pre-check apiClient.getToken() — multiple race
      // conditions can transiently empty localStorage while the cookie/JWT is
      // still valid. Just make the call: if the request reaches the server
      // and the server says "authenticated", we proceed; if not, we surface
      // the error properly. This avoids ghosting the user with a "session
      // missing" message when the session is actually fine.
      try {
        const session = await apiClient.request('/api/vault/session-check');
        if (session.active) { onUnlock(); return; }
      } catch {}
      try {
        const s = await apiClient.request('/api/vault/totp/setup');
        setSetup(s);
      } catch (e) {
        const detail = e?.body?.detail || e?.message || '';
        // 401 truly means the server rejected the credentials. Show the
        // banner so the user can re-sign-in by explicit click.
        if (detail) {
          setError(detail);
        } else {
          setError('We could not initialise the Vault. Please retry or sign in again.');
        }
      }
      setLoading(false);
    };
    init();
  }, [onUnlock]);

  const handleVerify = async () => {
    if (code.length !== 6) return;
    setVerifying(true);
    setError('');
    try {
      const res = await apiClient.request('/api/vault/totp/verify', {
        method: 'POST', body: JSON.stringify({ code }),
      });
      if (res.success) onUnlock();
    } catch (e) {
      const detail = e?.body?.detail || '';
      setError(detail || 'Invalid code. Please try again.');
    }
    setVerifying(false);
  };

  if (loading) return <AdminLayout><div className="flex items-center justify-center h-96"><div className="text-gray-600">Verifying access...</div></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="flex items-center justify-center min-h-[70vh] p-4">
        <div className="w-full max-w-md" data-testid="vault-gate">
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-gray-900 to-gray-700 flex items-center justify-center mx-auto mb-4 shadow-xl">
              <Lock size={32} className="text-amber-400" />
            </div>
            <h1 className="text-2xl font-black text-gray-900">Secure Vault</h1>
            <p className="text-sm text-gray-500 mt-1">Owner-only business intelligence</p>
          </div>

          {setup && !setup.setup_complete && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-6" data-testid="totp-setup">
              <p className="text-sm font-bold text-amber-800 mb-3">First-Time Setup</p>
              <p className="text-xs text-amber-700 mb-3">Scan this QR code with Google Authenticator or Authy:</p>
              {setup.qr_code && <img src={setup.qr_code} alt="QR Code" className="mx-auto rounded-lg border border-amber-300 mb-3" />}
              <p className="text-xs text-amber-600 text-center break-all">Manual key: {setup.secret}</p>
            </div>
          )}

          {!setup && error && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-6" data-testid="vault-init-error">
              <p className="text-sm font-bold text-red-800 mb-2">We couldn&apos;t initialise the Vault</p>
              <p className="text-xs text-red-700 mb-3">{error}</p>
              <div className="bg-red-100 p-3 rounded mb-3" data-testid="vault-init-diagnostic-block">
                <p className="text-xs font-bold text-red-900 mb-1">Diagnostic snapshot</p>
                <pre className="text-xs text-red-900 overflow-x-auto whitespace-pre-wrap" data-testid="vault-init-diagnostic">
{`localStorage hibff_auth_token : ${localStorage.getItem('hibff_auth_token') ? localStorage.getItem('hibff_auth_token').slice(0,18) + '… (' + localStorage.getItem('hibff_auth_token').length + ' chars)' : 'EMPTY'}
sessionStorage hibff_auth_token: ${sessionStorage.getItem('hibff_auth_token') ? sessionStorage.getItem('hibff_auth_token').slice(0,18) + '… (' + sessionStorage.getItem('hibff_auth_token').length + ' chars)' : 'EMPTY'}
apiClient.getToken() returns   : ${apiClient.getToken() ? 'NON-EMPTY token (' + apiClient.getToken().length + ' chars)' : 'NULL'}
Path                          : ${window.location.pathname}
Backend base                  : ${apiClient.baseUrl}
Document cookie keys          : ${document.cookie ? document.cookie.split(';').map(c => c.split('=')[0].trim()).join(', ') : '(none visible to JS)'}`}
                </pre>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => window.location.reload()}
                  className="px-3 py-1.5 rounded-lg text-xs font-bold bg-red-600 text-white hover:bg-red-700 transition-colors"
                  data-testid="vault-init-retry"
                >
                  Retry
                </button>
                <a
                  href="/api/auth/admin-direct"
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-white text-red-700 border border-red-300 hover:bg-red-50 transition-colors"
                  data-testid="vault-init-relogin"
                >
                  Re-sign in to Command Centre
                </a>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-lg p-6">
            <p className="text-sm font-medium text-gray-700 mb-3">Enter your 6-digit authenticator code:</p>
            <input
              type="text" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              className="w-full text-center text-3xl font-mono tracking-[0.5em] px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
              placeholder="000000" maxLength={6} autoFocus
              onKeyDown={e => e.key === 'Enter' && handleVerify()}
              data-testid="totp-code-input"
            />
            {error && <p className="text-sm text-red-500 mt-2 text-center">{error}</p>}
            <button
              onClick={handleVerify} disabled={code.length !== 6 || verifying}
              className={`w-full mt-4 py-3 text-white font-semibold rounded-xl disabled:opacity-50 hover:shadow-lg transition-all ${
                code.length === 6
                  ? 'bg-gradient-to-r from-emerald-600 to-green-500 ring-2 ring-emerald-300 shadow-md'
                  : 'bg-gradient-to-r from-gray-900 to-gray-700'
              }`}
              data-testid="vault-unlock-btn"
            >
              {verifying ? 'Verifying...' : 'Unlock Vault'}
            </button>
          </div>

          {/* Persistent re-pair option — pulls the canonical secret from .env.
              Use this once after a deploy / DB restore to re-pair your phone. */}
          <RepairAuthenticator />
        </div>
      </div>
    </AdminLayout>
  );
};

// ─── Re-pair Authenticator ──────────────────────────────────────────────────
// Renders a "Re-pair my authenticator" disclosure under the verify form.
// Click → hits GET /api/vault/totp/show-qr which returns the QR for the
// canonical MASTER_ADMIN_TOTP_SECRET in backend/.env. Pair ONCE after a
// fresh deploy / DB restore — your authenticator stays paired forever.
const RepairAuthenticator = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchQr = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.request('/api/vault/totp/show-qr');
      setData(res);
    } catch (e) {
      setError(e?.body?.detail || e?.message || 'Could not load the QR code.');
    }
    setLoading(false);
  };

  return (
    <div className="mt-4 text-center" data-testid="repair-auth-block">
      {!open && (
        <button
          type="button"
          onClick={() => { setOpen(true); fetchQr(); }}
          className="text-xs text-gray-600 hover:text-amber-600 underline transition-colors"
          data-testid="repair-auth-toggle"
        >
          Lost access to your authenticator? Re-pair here →
        </button>
      )}
      {open && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-2xl p-5 text-left" data-testid="repair-auth-panel">
          <p className="text-sm font-bold text-amber-800 mb-1">Re-pair Authenticator</p>
          <p className="text-xs text-amber-700 mb-3">
            This QR is bound to your canonical, persistent secret. Pair once and
            your codes will work across every deploy, restore, and re-seed.
          </p>
          {loading && <p className="text-xs text-amber-700 text-center py-4">Loading…</p>}
          {error && <p className="text-xs text-red-600 text-center py-2" data-testid="repair-auth-error">{error}</p>}
          {data?.qr_code && (
            <>
              <img src={data.qr_code} alt="Canonical QR" className="mx-auto rounded-lg border border-amber-300 mb-3" data-testid="repair-auth-qr" />
              <p className="text-xs text-amber-700 text-center break-all"><span className="font-bold">Manual key:</span> {data.secret}</p>
              <p className="text-xs text-amber-600 text-center mt-2 italic">{data.note}</p>
              <ol className="text-xs text-amber-700 mt-3 list-decimal ml-5 space-y-1">
                <li>Delete any existing &quot;HiBFF Vault&quot; entry in your authenticator app.</li>
                <li>Tap <strong>Add account → Scan QR</strong> and scan the code above.</li>
                <li>Enter the 6-digit code below and unlock.</li>
              </ol>
            </>
          )}
          <button
            type="button"
            onClick={() => { setOpen(false); setData(null); setError(''); }}
            className="mt-3 w-full py-1.5 text-xs font-semibold text-amber-800 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors"
            data-testid="repair-auth-close"
          >
            Hide
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Main Vault Dashboard ────────────────────────────────────────────────────

const TABS = [
  { key: 'overview',     label: 'Overview',     icon: BarChart3,  gradient: 'linear-gradient(135deg, #6366F1 0%, #4338CA 100%)' },
  { key: 'narrative',    label: 'Narrative',    icon: FileText,   gradient: 'linear-gradient(135deg, #F472B6 0%, #DB2777 100%)' },
  { key: 'investors',    label: 'Investors',    icon: Users,      gradient: 'linear-gradient(135deg, #10B981 0%, #047857 100%)' },
  { key: 'captable',     label: 'Cap Table',    icon: PieChart,   gradient: 'linear-gradient(135deg, #0EA5E9 0%, #075985 100%)' },
  { key: 'team',         label: 'Team',         icon: Users,      gradient: 'linear-gradient(135deg, #F5831F 0%, #C2410C 100%)' },
  { key: 'contactlog',   label: 'Contact Log',  icon: MessageCircle, gradient: 'linear-gradient(135deg, #F97316 0%, #C2410C 100%)' },
  { key: 'financials',   label: 'Financials',   icon: DollarSign, gradient: 'linear-gradient(135deg, #F59E0B 0%, #B45309 100%)' },
  { key: 'kpis',         label: 'KPIs',         icon: Activity,   gradient: 'linear-gradient(135deg, #06B6D4 0%, #0E7490 100%)' },
  { key: 'offers',       label: 'Offers',       icon: TrendingUp, gradient: 'linear-gradient(135deg, #84CC16 0%, #4D7C0F 100%)' },
  { key: 'acquisitions', label: 'Acquisitions', icon: Target,     gradient: 'linear-gradient(135deg, #14B8A6 0%, #0F766E 100%)' },
  { key: 'milestones',   label: 'Milestones',   icon: Award,      gradient: 'linear-gradient(135deg, #0EA5E9 0%, #0369A1 100%)' },
  { key: 'fundraise',    label: 'Fundraise',    icon: Layers,     gradient: 'linear-gradient(135deg, #8B5CF6 0%, #5B21B6 100%)' },
  { key: 'nasdaq',       label: 'NASDAQ',       icon: Landmark,   gradient: 'linear-gradient(135deg, #475569 0%, #1E293B 100%)' },
  { key: 'dataroom',     label: 'Data Room',    icon: Globe,      gradient: 'linear-gradient(135deg, #C084FC 0%, #7E22CE 100%)' },
  { key: 'pr',           label: 'PR & Social',  icon: Megaphone,  gradient: 'linear-gradient(135deg, #EC4899 0%, #BE185D 100%)' },
  { key: 'notes',        label: 'Notes',        icon: FileText,   gradient: 'linear-gradient(135deg, #A8A29E 0%, #44403C 100%)' },
  { key: 'calendar',     label: 'Calendar',     icon: Calendar,   gradient: 'linear-gradient(135deg, #2563EB 0%, #1E3A8A 100%)' },
  { key: 'pitchdeck',    label: 'Pitch Deck',   icon: FileText,   gradient: 'linear-gradient(135deg, #7C3AED 0%, #4C1D95 100%)' },
  { key: 'schools',      label: 'Schools',      icon: Award,      gradient: 'linear-gradient(135deg, #A855F7 0%, #6D28D9 100%)' },
  { key: 'settings',     label: 'Settings',     icon: Bell,       gradient: 'linear-gradient(135deg, #64748B 0%, #334155 100%)' },
];

const SecureVault = () => {
  const [unlocked, setUnlocked] = useState(false);
  const [tab, setTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchDashboard = useCallback(async () => {
    try {
      const d = await apiClient.request('/api/vault/dashboard');
      setDashboard(d);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { if (unlocked) fetchDashboard(); }, [unlocked, fetchDashboard]);

  // Cross-component bridge — RecommendOfferButton fires this to switch the
  // founder to the Offers tab so the prefilled configurator is visible.
  useEffect(() => {
    const onSwitch = (e) => {
      const target = (e && e.detail && e.detail.tab) || null;
      if (target) setTab(target);
    };
    window.addEventListener('hibff:vault:switch-tab', onSwitch);
    return () => window.removeEventListener('hibff:vault:switch-tab', onSwitch);
  }, []);

  // Session auto-lock: any vault child reporting a 401 flips us back to the TOTP gate.
  useEffect(() => {
    const onExpired = () => setUnlocked(false);
    window.addEventListener('hibff:vault:expired', onExpired);
    return () => window.removeEventListener('hibff:vault:expired', onExpired);
  }, []);

  const handleLockVault = async () => {
    try {
      await apiClient.request('/api/vault/lock', { method: 'POST' });
      setUnlocked(false);
    } catch {}
  };

  if (!unlocked) return <TOTPGate onUnlock={() => setUnlocked(true)} />;

  return (
    <AdminLayout>
      <CheatSheet pageId="vault" />
      {/* Sprint 176 — global readability uplift, scoped strictly to the
          Secure Vault subtree. Bumps font sizes (xs→13px, sm→14.5px) and
          darkens the muted greys so the founder can read the long tables
          (KPIs, Financials, Contact Log, Data Room expired list, etc.)
          without the rest of the admin shell being affected. */}
      <style>{`
        .vault-readable .text-xs { font-size: 13px !important; line-height: 1.45 !important; }
        .vault-readable .text-\\[10px\\] { font-size: 12px !important; line-height: 1.4 !important; }
        .vault-readable .text-sm { font-size: 14.5px !important; line-height: 1.55 !important; }
        .vault-readable .text-gray-400, .vault-readable .text-gray-500 { color: #4B5563 !important; } /* gray-600 */
        .vault-readable .text-gray-600 { color: #374151 !important; } /* gray-700 */
        .vault-readable table th, .vault-readable thead th { color: #1F2937 !important; font-weight: 700 !important; font-size: 13px !important; }
        .vault-readable table td { color: #1F2937 !important; font-size: 14px !important; }
        /* Don't touch coloured status pills / badges / buttons — they already
           carry their own brand-coloured text classes that override above. */
      `}</style>
      <div className="p-4 sm:p-6 vault-readable" data-testid="secure-vault">
        {/* Header — primary actions plus the HiBFF Gives entry button */}
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-900 to-gray-700 flex items-center justify-center">
              <Shield size={20} className="text-amber-400" />
            </div>
            <div>
              <h1 className="text-xl font-black text-gray-900">Secure Vault</h1>
              <p className="text-xs text-gray-600 uppercase tracking-wider">Owner Business Intelligence</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => setTab('schools')}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg transition-colors text-white"
              style={{ background: 'linear-gradient(135deg, #7B4FD8 0%, #5933A8 100%)' }}
              data-testid="vault-schools-engine-btn">
              <Heart size={14} /> Schools Programme
            </button>
            <button onClick={() => setTab('pitchdeck')}
              className="flex items-center gap-1.5 px-3 py-2 bg-[#7B4FD8]/10 text-[#7B4FD8] text-xs font-semibold rounded-lg hover:bg-[#7B4FD8]/20 transition-colors"
              data-testid="export-docs-btn">
              <Download size={14} /> Pitch Deck
            </button>
            <button onClick={handleLockVault}
              className="flex items-center gap-1.5 px-3 py-2 bg-red-50 text-red-600 text-xs font-semibold rounded-lg hover:bg-red-100 transition-colors"
              data-testid="lock-vault-btn">
              <LogOut size={14} /> Lock Vault
            </button>
          </div>
        </div>

        {/* Tabs as button-grid — spread across multiple rows so every section is
            visible at a glance instead of hidden behind a horizontal scroll.
            Per-tile gradients give each Command-Centre section its own colour;
            the active tab inverts to a solid dark fill so the current section
            remains unmistakable and high-contrast. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-6" data-testid="vault-tabs">
          {TABS.map(t => {
            const isActive = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                aria-pressed={isActive}
                style={isActive ? undefined : { background: t.gradient }}
                className={`flex flex-col items-center justify-center gap-1.5 px-3 py-3 rounded-xl text-xs font-semibold transition-all border ${
                  isActive
                    ? 'bg-gray-900 text-white border-gray-900 shadow-md ring-2 ring-offset-2 ring-gray-900/40'
                    : 'text-white border-transparent shadow-sm hover:shadow-md hover:brightness-110 hover:-translate-y-0.5'
                }`}
                data-testid={`tab-${t.key}`}
              >
                <t.icon size={18} /> <span>{t.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {tab === 'overview' && <OverviewTab dashboard={dashboard} loading={loading} />}
        {tab === 'narrative' && <NarrativeTab />}
        {tab === 'investors' && <InvestorsTab onRefresh={fetchDashboard} />}
        {tab === 'captable' && <CapTableTab />}
        {tab === 'team' && <VaultTeamPanel />}
        {tab === 'contactlog' && <ContactLogTab />}
        {tab === 'financials' && <FinancialsTab />}
        {tab === 'kpis' && <KPITab />}
        {tab === 'offers' && <OffersTab />}
        {tab === 'acquisitions' && <AcquisitionsTab />}
        {tab === 'milestones' && <MilestonesTab />}
        {tab === 'fundraise' && <FundraiseTab />}
        {tab === 'nasdaq' && <NasdaqTab />}
        {tab === 'dataroom' && <DataRoomTab />}
        {tab === 'pr' && <PRTrackerTab />}
        {tab === 'notes' && <NotesTab />}
        {tab === 'calendar' && <CalendarTab />}
        {tab === 'settings' && <SettingsTab />}
        {tab === 'pitchdeck' && <PitchDeckTab />}
        {tab === 'schools' && <SchoolsTab />}
      </div>
    </AdminLayout>
  );
};

// ─── OVERVIEW TAB ────────────────────────────────────────────────────────────

const OverviewTab = ({ dashboard, loading }) => {
  const [smsText, setSmsText] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [smsResult, setSmsResult] = useState(null);
  const [codeMetrics, setCodeMetrics] = useState(null);
  const [codeRefreshing, setCodeRefreshing] = useState(false);

  const loadCodeMetrics = useCallback((force = false) => {
    setCodeRefreshing(true);
    apiClient.request(`/api/vault/codebase-metrics${force ? '?force=1' : ''}`)
      .then(setCodeMetrics)
      .catch(() => setCodeMetrics(null))
      .finally(() => setCodeRefreshing(false));
  }, []);

  useEffect(() => { loadCodeMetrics(false); }, [loadCodeMetrics]);

  const handleQuickSMS = async () => {
    if (!smsText.trim()) return;
    setSmsSending(true); setSmsResult(null);
    try {
      const res = await apiClient.request('/api/vault/quick-sms', { method: 'POST', body: JSON.stringify({ message: smsText }) });
      setSmsResult(res.success ? 'sent' : 'failed');
      if (res.success) setSmsText('');
    } catch { setSmsResult('failed'); }
    setSmsSending(false);
    setTimeout(() => setSmsResult(null), 3000);
  };

  const handleExportCSV = async (type) => {
    try {
      const res = await apiClient.request(`/api/vault/export/${type}`);
      const blob = new Blob([res.csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `hibff_${type}_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch {}
  };

  if (loading || !dashboard) return <div className="text-center py-12 text-gray-600">Loading dashboard...</div>;
  const d = dashboard;
  const valuation = d.valuation?.valuation || 0;

  return (
    <div className="space-y-5" data-testid="overview-tab">
      {/* Velocity Heartbeat — 24h rolling KPI ribbon (above everything) */}
      <VelocityHeartbeat />

      {/* Live investor telemetry leaderboard — "Who's circling the Vault" */}
      <VaultTelemetryLeaderboard />

      {/* Reviewed → Sent conversion ribbon — Sprint 169 deal-pacing conscience */}
      <ConversionFunnelRibbon />

      {/* Monday Morning Telemetry Digest — preview + on-demand Postmark send */}
      <MondayTelemetryDigestCard />

      {/* Vault Daily Pulse — Slack/Telegram morning briefing */}
      <VaultDailyPulseCard />

      {/* Live Traction — real platform data for investor visibility */}
      <LiveTractionCard surface="vault" />

      {/* Top Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-4 gap-3">
        <VaultCard icon={Users} color="#3B82F6" label="Investors" value={d.investors.count} sub={`${d.investors.total_shares.toLocaleString()} total shares`} />
        <VaultCard icon={DollarSign} color="#10B981" label="Total Revenue" value={`$${(d.financials.total_revenue / 1000).toFixed(0)}k`} sub={`$${(d.financials.total_costs / 1000).toFixed(0)}k costs`} />
        <VaultCard icon={TrendingUp} color="#F59E0B" label="Valuation" value={valuation > 0 ? `$${(valuation / 1000000).toFixed(1)}M` : 'Not set'} sub={d.valuation?.basis || ''} />
        <VaultCard icon={Target} color="#8B5CF6" label="Pending Offers" value={d.offers.pending_count} sub={`${d.offers.list.length} total`} />
      </div>

      {/* Live Codebase Metrics — single source of truth, computed from the
          running system so the totals can never drift or go stale. PD-ready. */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm" data-testid="vault-codebase-metrics">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Code2 size={18} className="text-indigo-600" />
            <div>
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider">Codebase — Live Breakdown</h3>
              <p className="text-[11px] text-gray-400">
                {codeMetrics?.computed_at ? `Recounted ${new Date(codeMetrics.computed_at).toLocaleString()}` : 'Computing…'}
              </p>
              {codeMetrics?.growth && (codeMetrics.growth.lines_added !== 0 || codeMetrics.growth.endpoints_added !== 0) && (
                <p className="text-[11px] font-bold text-emerald-600" data-testid="vault-codebase-growth">
                  ▲ {codeMetrics.growth.lines_added >= 0 ? '+' : ''}{Number(codeMetrics.growth.lines_added).toLocaleString()} lines · {codeMetrics.growth.endpoints_added >= 0 ? '+' : ''}{codeMetrics.growth.endpoints_added} endpoints since last release ({new Date(codeMetrics.growth.since).toLocaleDateString('en-GB')})
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={() => loadCodeMetrics(true)}
            disabled={codeRefreshing}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-semibold disabled:opacity-50"
            data-testid="vault-codebase-refresh"
          >
            <RefreshCw size={13} className={codeRefreshing ? 'animate-spin' : ''} />
            {codeRefreshing ? 'Recounting…' : 'Refresh'}
          </button>
        </div>

        {!codeMetrics ? (
          <p className="text-xs text-gray-500" data-testid="vault-codebase-loading">Counting the codebase…</p>
        ) : (
          <>
            {/* Headline totals */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div className="bg-indigo-50 rounded-lg p-3">
                <p className="text-2xl font-black text-indigo-700" data-testid="vault-loc-total">{codeMetrics.totals?.everything?.lines?.toLocaleString()}</p>
                <p className="text-[11px] text-gray-600 uppercase tracking-wider font-semibold">Full project · lines</p>
                <p className="text-[10px] text-gray-500 mt-0.5">code + templates + data + mobile + docs</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-2xl font-black text-gray-900">{codeMetrics.totals?.active_code?.lines?.toLocaleString()}</p>
                <p className="text-[11px] text-gray-600 uppercase tracking-wider font-semibold">Active code · lines</p>
                <p className="text-[10px] text-gray-500 mt-0.5">live code, excl. archive & docs</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-2xl font-black text-gray-900" data-testid="vault-endpoints-total">{codeMetrics.live_endpoints?.toLocaleString() || '—'}</p>
                <p className="text-[11px] text-gray-600 uppercase tracking-wider font-semibold">Live API endpoints</p>
                <p className="text-[10px] text-gray-500 mt-0.5">{codeMetrics.api_route_files} route modules</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-2xl font-black text-gray-900">{codeMetrics.totals?.everything?.files?.toLocaleString()}</p>
                <p className="text-[11px] text-gray-600 uppercase tracking-wider font-semibold">Source files</p>
                <p className="text-[10px] text-gray-500 mt-0.5">{codeMetrics.service_files} services · {codeMetrics.worker_files} workers</p>
              </div>
            </div>

            {/* Itemised breakdown */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="vault-codebase-table">
                <thead>
                  <tr className="text-left text-[11px] text-gray-400 uppercase tracking-wider border-b border-gray-100">
                    <th className="py-2 pr-3 font-semibold">Component</th>
                    <th className="py-2 pr-3 font-semibold hidden sm:table-cell">What it is</th>
                    <th className="py-2 pr-3 font-semibold text-right">Files</th>
                    <th className="py-2 font-semibold text-right">Lines</th>
                  </tr>
                </thead>
                <tbody>
                  {codeMetrics.breakdown?.map((b) => (
                    <tr key={b.key} className="border-b border-gray-50" data-testid={`vault-codebase-row-${b.key}`}>
                      <td className="py-2 pr-3">
                        <span className="font-semibold text-gray-900">{b.label}</span>
                        <span className="block text-[10px] text-gray-400">{b.group}</span>
                      </td>
                      <td className="py-2 pr-3 text-xs text-gray-500 hidden sm:table-cell">{b.description}</td>
                      <td className="py-2 pr-3 text-right tabular-nums text-gray-700">{b.files?.toLocaleString()}</td>
                      <td className="py-2 text-right tabular-nums font-semibold text-gray-900">{b.lines?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="text-gray-700">
                    <td className="py-2 pr-3 font-bold" colSpan={3}>Active codebase (excl. archive & docs)</td>
                    <td className="py-2 text-right tabular-nums font-bold">{codeMetrics.totals?.active_code?.lines?.toLocaleString()}</td>
                  </tr>
                  <tr className="text-gray-700">
                    <td className="py-1 pr-3" colSpan={3}>Including legacy / archived code</td>
                    <td className="py-1 text-right tabular-nums">{codeMetrics.totals?.with_legacy?.lines?.toLocaleString()}</td>
                  </tr>
                  <tr className="text-indigo-700 border-t-2 border-indigo-100">
                    <td className="py-2 pr-3 font-black" colSpan={3}>FULL PROJECT TOTAL</td>
                    <td className="py-2 text-right tabular-nums font-black" data-testid="vault-codebase-grandtotal">{codeMetrics.totals?.everything?.lines?.toLocaleString()}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Quick SMS + Export Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <div className="sm:col-span-2 bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-5" data-testid="quick-sms">
          <h3 className="text-sm font-bold text-white mb-2 flex items-center gap-2"><Smartphone size={14} className="text-amber-400" /> Text Me a Reminder</h3>
          <p className="text-xs text-gray-600 mb-3">Quick SMS to +44...834</p>
          <div className="flex gap-2">
            <input type="text" value={smsText} onChange={e => setSmsText(e.target.value)}
              placeholder="e.g. Call Sarah about bridge round terms"
              className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-sm text-white placeholder-gray-400 focus:border-amber-500 focus:ring-1 focus:ring-amber-500"
              onKeyDown={e => e.key === 'Enter' && handleQuickSMS()}
              data-testid="quick-sms-input" />
            <button onClick={handleQuickSMS} disabled={smsSending || !smsText.trim()}
              className="px-4 py-2 bg-amber-500 text-gray-900 font-semibold rounded-lg text-sm disabled:opacity-50 hover:bg-amber-400 transition-colors flex items-center gap-1.5"
              data-testid="quick-sms-btn">
              <Send size={14} /> {smsSending ? '...' : 'Send'}
            </button>
          </div>
          {smsResult && <p className={`text-xs mt-2 ${smsResult === 'sent' ? 'text-green-400' : 'text-red-400'}`}>{smsResult === 'sent' ? 'SMS sent to your phone!' : 'Failed to send. Check Twilio config.'}</p>}
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2"><Download size={14} /> Export Data</h3>
          <div className="space-y-2">
            <button onClick={() => handleExportCSV('investors')} className="w-full py-2 bg-gray-100 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-200 transition-colors" data-testid="export-investors-btn">Investors CSV</button>
            <button onClick={() => handleExportCSV('financials')} className="w-full py-2 bg-gray-100 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-200 transition-colors" data-testid="export-financials-btn">Financials CSV</button>
          </div>
        </div>
      </div>

      {/* Revenue vs Costs Chart */}
      {d.financials.records.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="revenue-chart">
          <h3 className="text-sm font-bold text-gray-900 mb-3">Revenue vs Costs</h3>
          <div className="flex items-end gap-1 h-40">
            {d.financials.records.slice(-12).map((r, i) => {
              const maxVal = Math.max(...d.financials.records.slice(-12).map(x => Math.max(x.revenue || 0, x.costs || 0)), 1);
              const revH = ((r.revenue || 0) / maxVal) * 100;
              const costH = ((r.costs || 0) / maxVal) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                  <div className="w-full flex gap-0.5 items-end h-32">
                    <div className="flex-1 bg-emerald-400 rounded-t transition-all" style={{ height: `${revH}%`, minHeight: '2px' }} title={`Revenue: $${r.revenue}`} />
                    <div className="flex-1 bg-red-400 rounded-t transition-all" style={{ height: `${costH}%`, minHeight: '2px' }} title={`Costs: $${r.costs}`} />
                  </div>
                  <span className="text-xs text-gray-600">{r.month?.slice(0, 3)}</span>
                </div>
              );
            })}
          </div>
          <div className="flex gap-4 mt-2 text-xs">
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-400 rounded-full" /> Revenue</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 bg-red-400 rounded-full" /> Costs</span>
            <span className="ml-auto font-bold text-emerald-600">Net: ${(d.financials.net).toLocaleString()}</span>
          </div>
        </div>
      )}

      {/* Phase D · Block 3 — Persona MRR Split + ARPU Premium */}
      <InvestorPersonaCharts />

      {/* Total Investment Raised */}
      {d.investors.total_investment > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 p-5 flex items-center justify-between" data-testid="investment-raised">
          <div>
            <p className="text-xs uppercase tracking-wider font-bold text-blue-600 mb-1">Total Capital Raised</p>
            <p className="text-3xl font-black text-gray-900">${(d.investors.total_investment / 1000).toFixed(0)}k</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">{d.investors.count} investors</p>
            <p className="text-xs text-gray-500">{d.investors.total_shares.toLocaleString()} shares issued</p>
          </div>
        </div>
      )}

      {/* Investor Pipeline Funnel */}
      {d.investor_pipeline && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="investor-pipeline">
          <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2"><Users size={14} /> Investor Pipeline</h3>
          <div className="flex items-center gap-2">
            {[
              { label: 'Prospect', count: d.investor_pipeline.prospect, color: 'bg-gray-200 text-gray-700', width: '100%' },
              { label: 'Confirmed', count: d.investor_pipeline.confirmed, color: 'bg-amber-100 text-amber-700', width: '70%' },
              { label: 'Active', count: d.investor_pipeline.active, color: 'bg-emerald-100 text-emerald-700', width: '45%' },
            ].map((stage, i) => (
              <React.Fragment key={stage.label}>
                {i > 0 && <ChevronRight size={16} className="text-gray-300 flex-shrink-0" />}
                <div className={`${stage.color} rounded-xl px-4 py-3 text-center flex-1`} style={{ maxWidth: stage.width }}>
                  <p className="text-2xl font-black">{stage.count}</p>
                  <p className="text-xs font-semibold uppercase tracking-wider">{stage.label}</p>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Milestones Timeline */}
      {d.milestones && d.milestones.total > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="milestones-overview">
          <h3 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-2"><Award size={14} /> Milestones</h3>
          <p className="text-xs text-gray-600 mb-3">{d.milestones.achieved} of {d.milestones.total} achieved</p>
          <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
            <div className="bg-gradient-to-r from-amber-400 to-emerald-500 h-full rounded-full transition-all" style={{ width: `${(d.milestones.achieved / Math.max(d.milestones.total, 1)) * 100}%` }} />
          </div>
        </div>
      )}

      {/* Upcoming Events + Recent Contacts */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2"><Calendar size={14} /> Upcoming Events</h3>
          {d.upcoming_events.length > 0 ? d.upcoming_events.map((e, i) => (
            <div key={i} className="flex items-center gap-2 py-2 border-b border-gray-50 last:border-0">
              <span className="text-xs text-gray-600 w-16">{e.date}</span>
              <span className="text-xs font-medium text-gray-900">{e.title}</span>
            </div>
          )) : <p className="text-xs text-gray-600">No upcoming events</p>}
        </div>
        <div className="bg-white rounded-2xl border border-gray-200 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2"><MessageCircle size={14} /> Recent Contacts</h3>
          {d.recent_contacts.length > 0 ? d.recent_contacts.map((c, i) => (
            <div key={i} className="flex items-center gap-2 py-2 border-b border-gray-50 last:border-0">
              <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${c.method === 'sms' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{c.method}</span>
              <span className="text-xs text-gray-900 truncate flex-1">{c.subject || c.to}</span>
            </div>
          )) : <p className="text-xs text-gray-600">No recent contacts</p>}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-3">
        <VaultCard icon={Target} color="#EC4899" label="Acquisitions" value={d.acquisitions_count} />
        <VaultCard icon={Megaphone} color="#6366F1" label="PR Activities" value={d.pr_activities_count} />
        <VaultCard icon={FileText} color="#14B8A6" label="Notes" value={d.notes_count} />
      </div>
    </div>
  );
};

// ─── NARRATIVE TAB ───────────────────────────────────────────────────────────
//   Robert's working "founder voice" canvas — three columns each storing the
//   one-liner that he'd use in different audiences. Persisted to /vault/notes
//   under a fixed slot so it round-trips across sessions without a new model.

// ─── Investor Photo Uploader ────────────────────────────────────────────────
// Live preview + drag-or-click upload. Hits POST /api/vault/investors/{id}/photo
// when an investor_id exists. For new investors (no id yet) it falls back to
// a raw URL input so the founder can paste a CDN link and save first.
const InvestorPhotoUploader = ({ investorId, currentUrl, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [showUrl, setShowUrl] = useState(false);

  const handleFile = async (file) => {
    setError('');
    if (!file) return;
    if (!investorId) {
      setError('Save the investor first, then upload a photo.');
      return;
    }
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(file.type)) {
      setError('PNG, JPG or WEBP only.');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      setError('Max 4 MB.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = apiClient.getToken();
      const res = await fetch(`${apiClient.baseUrl}/api/vault/investors/${investorId}/photo`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data?.detail || 'Upload failed.');
      } else {
        onChange(data.photo_url);
      }
    } catch {
      setError('Network error. Try again.');
    } finally {
      setUploading(false);
    }
  };

  const fullUrl = currentUrl && currentUrl.startsWith('/api/')
    ? `${apiClient.baseUrl}${currentUrl}`
    : currentUrl;

  return (
    <div className="flex items-start gap-4 mb-4" data-testid="investor-photo-uploader">
      <div className="relative">
        {fullUrl ? (
          <img src={fullUrl} alt="Investor avatar" className="w-20 h-20 rounded-full object-cover border-2 border-gray-200" data-testid="investor-photo-preview" />
        ) : (
          <div className="w-20 h-20 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center" data-testid="investor-photo-placeholder">
            <Camera size={22} className="text-gray-600" />
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-white/80 rounded-full flex items-center justify-center">
            <Clock size={18} className="animate-pulse text-gray-700" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <label className="text-xs font-medium text-gray-600 uppercase block mb-1">Profile Photo</label>
        <div className="flex flex-wrap gap-2">
          <label
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors ${investorId ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 cursor-not-allowed'}`}
            title={investorId ? 'Upload from device' : 'Save the investor first to enable upload'}
            data-testid="investor-photo-upload-btn"
          >
            <Upload size={12} />
            {uploading ? 'Uploading…' : 'Upload Photo'}
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              className="hidden"
              disabled={!investorId || uploading}
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="investor-photo-file-input"
            />
          </label>
          <button
            type="button"
            onClick={() => setShowUrl(s => !s)}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
            data-testid="investor-photo-url-toggle"
          >
            {showUrl ? 'Hide URL' : 'Paste URL instead'}
          </button>
          {currentUrl && (
            <button
              type="button"
              onClick={() => onChange('')}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
              data-testid="investor-photo-clear-btn"
            >
              Clear
            </button>
          )}
        </div>
        {showUrl && (
          <input
            type="url"
            value={currentUrl || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://example.com/avatar.jpg"
            className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg text-xs"
            data-testid="investor-photo-url-input"
          />
        )}
        {error && <p className="text-xs text-red-600 mt-1" data-testid="investor-photo-error">{error}</p>}
        <p className="text-xs text-gray-600 mt-1">PNG/JPG/WEBP · max 4 MB. {!investorId && 'Save the investor first to enable upload.'}</p>
      </div>
    </div>
  );
};

const NARRATIVE_SLOTS = [
  {
    key: 'narrative-elevator',
    title: 'Elevator pitch',
    hint: '15 seconds. Mayor at a coffee shop.',
    accent: '#F5831F',
    defaultBody: "HiBFF is an institutional-grade behavioral wellness platform built from the ground up to guide teens through digital-to-physical character challenges. By pairing multi-modal AI avatar customization with zero-negligence safeguarding — including a customized Claude Sonnet 4.5 safety wrapper and real-time clinical crisis triage routing — we protect teens end-to-end while offering families a legally pending framework to counter tech dependency and build authentic, real-world confidence.",
  },
  {
    key: 'narrative-investor',
    title: 'Investor one-liner',
    hint: 'What we do, who for, why now.',
    accent: '#7B4FD8',
    defaultBody: "HiBFF builds a highly defensive, COPPA-compliant digital safety layer for the next billion teens — a secure private space for life, with a proprietary 180-day countdown reward vault anchoring a $159 contracted LTV floor per member and eliminating systemic platform liability for incoming venture capital.",
  },
  {
    key: 'narrative-mayor',
    title: 'Mayor letter opener',
    hint: 'First two sentences of the letter.',
    accent: '#2EAA5C',
    defaultBody: "Dear Mayor, I am writing to introduce HiBFF Gives, a fully funded behavioral infrastructure initiative designed to support teen mental wellness and character building within your city's local schools at zero cost to your municipal budget. Through our specialized five-year public partnership framework, we provide disadvantaged families with immediate, protected access to elite social-skills training tools to bridge the digital safety divide in your community.",
  },
  {
    key: 'narrative-press',
    title: 'Press boilerplate',
    hint: 'For media kit & footer.',
    accent: '#3B82F6',
    defaultBody: "HiBFF (Hi Brave Friends Forever) is a category-defining technology platform dedicated to engineering the safety layer for the next generation online. Operating via a highly sophisticated, SOC2 audit-ready architecture, HiBFF pairs personalized AI practice companions with structured parent-teen milestone collaboration charters to foster courage, build confidence, and strengthen real-world character. Headquartered with complete end-to-end data custody protections, HiBFF's proprietary social-impact model ensures safe digital spaces are universally accessible to families and educational institutions worldwide.",
  },
];

const NarrativeTab = () => {
  // Founder-voice defaults — visible immediately even before the founder has
  // saved anything. Persisted to /vault/notes on first blur so they survive
  // future sessions and can be edited inline.
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(null);
  const [hydrated, setHydrated] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const d = await apiClient.request('/api/vault/notes');
      const map = {};
      for (const n of (d.notes || [])) {
        if (NARRATIVE_SLOTS.find(s => s.key === n.title)) map[n.title] = n.body || '';
      }
      // Pre-fill any slot the founder hasn't saved yet with the default copy.
      // Auto-persist defaults in the background so the next visit reads the
      // saved row (idempotent — no overwrite of edits).
      for (const s of NARRATIVE_SLOTS) {
        if (!map[s.key] || !map[s.key].trim()) {
          map[s.key] = s.defaultBody;
          // fire-and-forget persist; never block the UI.
          apiClient.request('/api/vault/notes', {
            method: 'POST',
            body: JSON.stringify({ title: s.key, body: s.defaultBody, tags: ['narrative', 'default'] }),
          }).catch(() => {});
        }
      }
      setValues(map);
      setHydrated(true);
    } catch {
      // Even on fetch failure, render the defaults so the tab is never empty.
      const fallback = {};
      for (const s of NARRATIVE_SLOTS) fallback[s.key] = s.defaultBody;
      setValues(fallback);
      setHydrated(true);
    }
   
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const persist = async (slot, body) => {
    setSaving(slot);
    try {
      await apiClient.request('/api/vault/notes', {
        method: 'POST',
        body: JSON.stringify({ title: slot, body, tags: ['narrative'] }),
      });
    } catch {}
    setSaving(null);
  };

  return (
    <div className="space-y-3" data-testid="narrative-tab">
      <div className="bg-white border border-gray-100 rounded-2xl p-5">
        <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#F5831F' }}>Founder Voice</p>
        <h3 className="text-lg font-bold mb-1" style={{ color: '#1A1209' }}>Your story, four ways</h3>
        <p className="text-sm text-gray-600">Keep these tight. One paragraph each, max. Used as the source-of-truth for letters, decks and press. Defaults are pre-populated — edit inline and changes auto-save.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {NARRATIVE_SLOTS.map(s => (
          <div key={s.key} className="bg-white border border-gray-100 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-sm font-bold" style={{ color: s.accent }}>{s.title}</p>
                <p className="text-xs text-gray-600">{s.hint}</p>
              </div>
              {saving === s.key && <Clock size={14} className="text-gray-600 animate-pulse" />}
            </div>
            <textarea
              rows={6}
              value={hydrated ? (values[s.key] ?? s.defaultBody) : ''}
              onChange={(e) => setValues(v => ({ ...v, [s.key]: e.target.value }))}
              onBlur={(e) => persist(s.key, e.target.value)}
              placeholder={`Write the ${s.title.toLowerCase()} here…`}
              className="w-full text-sm rounded-lg border border-gray-200 p-3 focus:border-gray-400 focus:ring-2 focus:ring-gray-200 outline-none leading-relaxed"
              data-testid={`narrative-${s.key}`}
            />
          </div>
        ))}
      </div>
      <p className="text-xs text-gray-600 text-center pt-2">Auto-saves on blur · backed by /vault/notes</p>
    </div>
  );
};


// ─── INVESTORS TAB ───────────────────────────────────────────────────────────

const SIGNAL_TIER_STYLE = {
  Hot:   { bg: 'linear-gradient(90deg, #F99B7D 0%, #F26B43 100%)', text: '#fff',    label: 'Hot' },
  Warm:  { bg: 'linear-gradient(90deg, #FFD3B6 0%, #FFAE7C 100%)', text: '#5A2F18', label: 'Warm' },
  Cool:  { bg: 'linear-gradient(90deg, #C7D8F2 0%, #9BB8ED 100%)', text: '#1E3A6F', label: 'Cool' },
  Cold:  { bg: 'linear-gradient(90deg, #E5E7EB 0%, #CBD5E1 100%)', text: '#374151', label: 'Cold' },
};

// Signal-score banner sits on top of each investor card. Click to expand the
// 4-component breakdown so Robert can see WHY the algorithm ranked the lead.
const SignalScoreBanner = ({ score, tier, components, investorId }) => {
  const [open, setOpen] = useState(false);
  const style = SIGNAL_TIER_STYLE[tier] || SIGNAL_TIER_STYLE.Cold;
  return (
    <div className="select-none" data-testid={`signal-banner-${investorId}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-3 py-1.5 flex items-center justify-between gap-2 text-xs font-bold"
        style={{ background: style.bg, color: style.text }}
        data-testid={`signal-toggle-${investorId}`}
      >
        <span className="inline-flex items-center gap-1.5">
          <Zap size={11} /> {style.label} · Signal {score}
        </span>
        <span className="text-xs font-semibold opacity-90">{open ? 'Hide' : 'Why?'}</span>
      </button>
      {open && components && (
        <div className="bg-gray-50 border-b border-gray-200 px-3 py-2 space-y-1.5" data-testid={`signal-breakdown-${investorId}`}>
          {[
            { key: 'firm',    label: 'Firm tier' },
            { key: 'ticket',  label: 'Ticket band' },
            { key: 'recency', label: 'Recency' },
            { key: 'vault',   label: 'Vault activity' },
          ].map(({ key, label }) => {
            const c = components[key];
            if (!c) return null;
            const w = Math.max(0, Math.min(100, c.score));
            return (
              <div key={key} className="text-xs">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-gray-700">
                    <strong>{label}</strong> · {c.label} <span className="text-gray-600">({Math.round(c.weight * 100)}% wt)</span>
                  </span>
                  <span className="font-bold text-gray-900">{c.score}</span>
                </div>
                <div className="w-full h-1.5 rounded-full bg-gray-200 overflow-hidden">
                  <div className="h-full" style={{ width: `${w}%`, background: '#F99B7D' }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const InvestorsTab = ({ onRefresh }) => {
  const [investors, setInvestors] = useState([]);
  const [dilution, setDilution] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [msgModal, setMsgModal] = useState(null);
  const [msgBody, setMsgBody] = useState('');
  const [msgSubject, setMsgSubject] = useState('');
  const [msgMethod, setMsgMethod] = useState('email');
  const [sending, setSending] = useState(false);
  const [contactLog, setContactLog] = useState([]);
  const [showLog, setShowLog] = useState(false);
  const [zoomUrl, setZoomUrl] = useState('');
  const [showZoomSetup, setShowZoomSetup] = useState(false);
  const [zoomInput, setZoomInput] = useState('');
  const [invSearch, setInvSearch] = useState('');
  const [drPanelFor, setDrPanelFor] = useState(null); // investor_id of open Data Room panel
  const [drData, setDrData] = useState({}); // { [investor_id]: { passcode, room_id, deep_link_path, ... } }
  const [drBusy, setDrBusy] = useState(null); // investor_id currently loading
  const [instrumentsByEmail, setInstrumentsByEmail] = useState({});
  const [pcPanelFor, setPcPanelFor] = useState(null); // investor_id with open passcode panel
  const [pcData, setPcData] = useState({}); // { [investor_id]: { passcode, emailed_to } }
  const [pcBusy, setPcBusy] = useState(null);

  const issuePortalPasscode = async (inv, sendEmail) => {
    const msg = sendEmail
      ? `Generate a new vault passcode for ${inv.name} and EMAIL it to ${inv.email}? Any previous passcode stops working immediately.`
      : `Generate a new vault passcode for ${inv.name} (shown to you only — no email sent)? Any previous passcode stops working immediately.`;
    if (!window.confirm(msg)) return;
    setPcBusy(inv.investor_id);
    try {
      const r = await apiClient.request(`/api/vault/investors/${inv.investor_id}/portal-passcode`, {
        method: 'POST', body: JSON.stringify({ send_email: sendEmail }),
      });
      setPcData(prev => ({ ...prev, [inv.investor_id]: r }));
      setPcPanelFor(inv.investor_id);
    } catch (_e) { /* best-effort */ }
    setPcBusy(null);
  };

  const openDataRoom = async (inv) => {
    setDrBusy(inv.investor_id);
    try {
      const r = await apiClient.request(`/api/vault/datarooms/investor/${inv.investor_id}/passcode-6digit`, { method: 'POST' });
      setDrData(prev => ({ ...prev, [inv.investor_id]: r }));
      setDrPanelFor(inv.investor_id);
    } catch (_e) {
      // best-effort
    }
    setDrBusy(null);
  };

  const rotateDataRoom = async (inv) => {
    if (!window.confirm(`Rotate the 6-digit Data Room passcode for ${inv.name}? The current code will stop working immediately.`)) return;
    setDrBusy(inv.investor_id);
    try {
      const r = await apiClient.request(`/api/vault/datarooms/investor/${inv.investor_id}/passcode-6digit?rotate=true`, { method: 'POST' });
      setDrData(prev => ({ ...prev, [inv.investor_id]: r }));
    } catch (_e) {}
    setDrBusy(null);
  };

  const fetchData = useCallback(async () => {
    const [invWithSignal, dil, log, settings, instRes] = await Promise.all([
      apiClient.request('/api/vault/investors/with-signal').catch(() => null),
      apiClient.request('/api/vault/dilution'),
      apiClient.request('/api/vault/contact-log'),
      apiClient.request('/api/vault/settings').catch(() => ({})),
      apiClient.request('/api/vault/instruments/').catch(() => null),
    ]);
    const byEmail = {};
    (instRes?.instruments || []).forEach(it => {
      const k = (it.investor_email || '').trim().toLowerCase();
      if (k) (byEmail[k] = byEmail[k] || []).push(it);
    });
    setInstrumentsByEmail(byEmail);
    let investorList = [];
    if (Array.isArray(invWithSignal)) {
      investorList = invWithSignal;
    } else {
      const inv = await apiClient.request('/api/vault/investors');
      investorList = inv.investors || [];
    }
    setInvestors(investorList);
    setDilution(dil);
    setContactLog(log.logs);
    if (settings.zoom_url) setZoomUrl(settings.zoom_url);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    const method = editId ? 'PUT' : 'POST';
    const url = editId ? `/api/vault/investors/${editId}` : '/api/vault/investors';
    await apiClient.request(url, { method, body: JSON.stringify(form) });
    setShowForm(false); setEditId(null); setForm({});
    fetchData(); onRefresh();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this investor?')) return;
    await apiClient.request(`/api/vault/investors/${id}`, { method: 'DELETE' });
    fetchData(); onRefresh();
  };

  const handleSendMsg = async () => {
    if (!msgModal || !msgBody.trim()) return;
    setSending(true);
    const to = msgMethod === 'sms' ? msgModal.phone : msgModal.email;
    await apiClient.request('/api/vault/send-message', {
      method: 'POST',
      body: JSON.stringify({ investor_id: msgModal.investor_id, method: msgMethod, to, subject: msgSubject, body: msgBody }),
    });
    setMsgModal(null); setMsgBody(''); setMsgSubject(''); setSending(false);
    fetchData();
  };

  const startEdit = (inv) => { setForm(inv); setEditId(inv.investor_id); setShowForm(true); };
  const [pulseToggling, setPulseToggling] = useState(null);
  const togglePulse = async (inv, subscribed) => {
    setPulseToggling(inv.investor_id);
    try {
      await apiClient.request(`/api/vault/investors/${inv.investor_id}/pulse`, {
        method: 'PUT', body: JSON.stringify({ subscribed }),
      });
      setInvestors(prev => prev.map(i => i.investor_id === inv.investor_id ? { ...i, pulse_subscribed: subscribed } : i));
    } catch (e) { console.error('Pulse toggle failed:', e); }
    setPulseToggling(null);
  };
  const FORM_DEFAULTS = { name: '', email: '', phone: '', postal_address: '', country: '', city: '', photo_url: '', shares: 0, share_class: 'ordinary', investment_amount: 0, notes: '', status: 'prospect' };

  return (
    <div className="space-y-5" data-testid="investors-tab">
      {/* Dilution Chart */}
      {dilution && dilution.dilution.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="dilution-chart">
          <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2"><PieChart size={14} /> Share Dilution</h3>
          <div className="flex flex-wrap gap-2">
            {dilution.dilution.map((d, i) => {
              const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#6366F1'];
              return (
                <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: colors[i % colors.length] }} />
                  <div>
                    <p className="text-xs font-semibold text-gray-900">{d.name}</p>
                    <p className="text-xs text-gray-500">{d.shares.toLocaleString()} shares ({d.percentage}%)</p>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-gray-600 mt-2">Total: {dilution.total_shares.toLocaleString()} shares</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => { setForm(FORM_DEFAULTS); setEditId(null); setShowForm(true); }}
          className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-xl" data-testid="add-investor-btn">
          <Plus size={14} /> Add Investor
        </button>
        <button onClick={() => setShowLog(!showLog)}
          className="flex items-center gap-1.5 px-4 py-2 bg-gray-100 text-gray-700 text-sm font-semibold rounded-xl" data-testid="contact-log-btn">
          <MessageCircle size={14} /> Contact Log ({contactLog.length})
        </button>
        <button onClick={() => { setZoomInput(zoomUrl); setShowZoomSetup(!showZoomSetup); }}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl ${zoomUrl ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`} data-testid="zoom-setup-btn">
          <Video size={14} /> {zoomUrl ? 'Zoom Connected' : 'Set Zoom Link'}
        </button>
      </div>

      {/* Search — find any investor or funnel lead instantly by name/email/firm/status */}
      <div className="relative">
        <input type="text" value={invSearch} onChange={e => setInvSearch(e.target.value)}
          placeholder="Search investors & leads — name, email, firm or status…"
          className="w-full pl-4 pr-10 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#7B4FD8]"
          data-testid="investor-search-input" />
        {invSearch && (
          <button onClick={() => setInvSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs font-bold"
            data-testid="investor-search-clear">✕</button>
        )}
      </div>

      {/* Referral Activity — Investor One-Pager PDF magic-link tracker */}
      <ReferralActivityCard />

      {/* Zoom URL Setup */}
      {showZoomSetup && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4" data-testid="zoom-setup-panel">
          <p className="text-xs font-bold text-blue-800 mb-2">Your Personal Zoom Meeting Room</p>
          <p className="text-xs text-blue-600 mb-3">Paste your personal meeting link. This will appear as a one-click call button on every investor card.</p>
          <div className="flex gap-2">
            <input type="url" value={zoomInput} onChange={e => setZoomInput(e.target.value)}
              placeholder="https://zoom.us/j/1234567890" className="flex-1 px-3 py-2 border border-blue-300 rounded-lg text-sm bg-white" data-testid="zoom-url-input" />
            <button onClick={async () => {
              await apiClient.request('/api/vault/settings', { method: 'POST', body: JSON.stringify({ zoom_url: zoomInput }) });
              setZoomUrl(zoomInput); setShowZoomSetup(false);
            }} className="px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg" data-testid="zoom-save-btn">Save</button>
          </div>
        </div>
      )}

      {/* Investor Form */}
      {showForm && (
        <div className="bg-white rounded-2xl border-2 border-gray-300 p-5 shadow-sm" data-testid="investor-form">
          <h3 className="font-bold text-gray-900 mb-3">{editId ? 'Edit' : 'Add'} Investor</h3>

          {/* Photo uploader (sits on top so it feels like a real profile) */}
          <InvestorPhotoUploader
            investorId={editId}
            currentUrl={form.photo_url}
            onChange={(url) => setForm(p => ({ ...p, photo_url: url }))}
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { k: 'name', l: 'Full Name', type: 'text' }, { k: 'email', l: 'Email', type: 'email' },
              { k: 'phone', l: 'Phone', type: 'tel' }, { k: 'firm', l: 'Firm / Fund', type: 'text' },
              { k: 'postal_address', l: 'Postal Address', type: 'text' },
              { k: 'country', l: 'Country', type: 'text' }, { k: 'city', l: 'City/Town', type: 'text' },
              { k: 'shares', l: 'Shares', type: 'number' },
              { k: 'investment_amount', l: 'Investment (USD)', type: 'number' },
            ].map(f => (
              <div key={f.k}>
                <label className="text-xs font-medium text-gray-600 uppercase">{f.l}</label>
                <input type={f.type} value={form[f.k] || ''} onChange={e => setForm(p => ({ ...p, [f.k]: f.type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid={`inv-${f.k}`} />
              </div>
            ))}
            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-gray-600 uppercase flex items-center gap-1.5">
                <Linkedin size={11} /> LinkedIn URL
              </label>
              <input
                type="url"
                value={form.linkedin_url || ''}
                onChange={e => setForm(p => ({ ...p, linkedin_url: e.target.value }))}
                placeholder="https://www.linkedin.com/in/…"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5"
                data-testid="inv-linkedin_url"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 uppercase">Status</label>
              <select value={form.status || 'prospect'} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="inv-status">
                <option value="prospect">Prospect</option><option value="confirmed">Confirmed</option><option value="active">Active</option>
              </select>
            </div>
          </div>
          <textarea value={form.notes || ''} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
            placeholder="Notes..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-3" rows={2} data-testid="inv-notes" />
          <div className="flex gap-2 mt-3">
            <button onClick={() => setShowForm(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm">Cancel</button>
            <button onClick={handleSave} className="flex-1 py-2 bg-gray-900 text-white font-semibold rounded-lg text-sm" data-testid="save-investor-btn">Save</button>
          </div>
        </div>
      )}

      {/* Contact Log */}
      {showLog && contactLog.length > 0 && (
        <div className="bg-gray-50 rounded-2xl border border-gray-200 p-4 max-h-60 overflow-y-auto" data-testid="contact-log-list">
          {contactLog.map((c, i) => (
            <div key={i} className="flex items-center gap-2 py-2 border-b border-gray-100 last:border-0 text-xs">
              <span className={`px-1.5 py-0.5 rounded font-bold text-xs ${c.method === 'sms' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{c.method}</span>
              <span className="text-gray-500 w-20 flex-shrink-0">{c.created_at?.slice(0, 10)}</span>
              <span className="text-gray-900 truncate flex-1">{c.subject || c.body?.slice(0, 50)}</span>
              {c.success ? <CheckCircle size={12} className="text-green-500" /> : <XCircle size={12} className="text-red-500" />}
            </div>
          ))}
        </div>
      )}

      {/* Investor Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="investor-grid">
        {investors.filter(inv => {
          if (!invSearch.trim()) return true;
          const q = invSearch.trim().toLowerCase();
          return [inv.name, inv.email, inv.firm, inv.status, inv.source]
            .some(v => (v || '').toLowerCase().includes(q));
        }).map(inv => (
          <div key={inv.investor_id} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-testid={`investor-${inv.investor_id}`}>
            {/* Signal Score banner — present only when scored (with-signal endpoint) */}
            {typeof inv.signal_score === 'number' && (
              <SignalScoreBanner score={inv.signal_score} tier={inv.signal_tier} components={inv.signal_components} investorId={inv.investor_id} />
            )}
            <div className="p-4">
              <div className="flex items-center gap-3 mb-3">
                {inv.photo_url ? (
                  <img
                    src={inv.photo_url.startsWith('/api/') ? `${apiClient.baseUrl}${inv.photo_url}` : inv.photo_url}
                    alt={inv.name}
                    className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
                    data-testid={`investor-card-photo-${inv.investor_id}`}
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 font-bold text-lg">{inv.name?.charAt(0)}</div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-900 truncate" data-testid={`investor-card-name-${inv.investor_id}`}>{inv.name}</p>
                  {inv.firm && (
                    <p className="text-xs text-gray-700 truncate flex items-center gap-1" data-testid={`investor-card-firm-${inv.investor_id}`}>
                      <Briefcase size={9} /> {inv.firm}
                    </p>
                  )}
                  <div className="flex items-center gap-1 text-xs text-gray-600">
                    <MapPin size={8} /> {inv.city}{inv.city && inv.country ? ', ' : ''}{inv.country}
                  </div>
                  {(inv.last_interest_at || inv.created_at) && (
                    <p className="text-xs text-gray-500 mt-0.5" data-testid={`investor-card-ts-${inv.investor_id}`} title={inv.last_interest_at || inv.created_at}>
                      Last activity {fmtRelTime(inv.last_interest_at || inv.created_at)}
                      {inv.source === 'pdf_request' ? ' · via One-Pager download' : inv.source === 'public_gateway' ? ' · via Interest form' : ''}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold whitespace-nowrap ${
                    inv.status === 'active' ? 'bg-green-100 text-green-700' : inv.status === 'confirmed' ? 'bg-blue-100 text-blue-700'
                    : (inv.status === 'prospect_lead' || inv.status === 'engaged') ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                  }`} data-testid={`investor-status-${inv.investor_id}`}>
                    {inv.status === 'prospect_lead' ? 'Prospect Lead' : inv.status}
                  </span>
                  {(() => {
                    const receiving = inv.pulse_subscribed === true || (inv.pulse_subscribed == null && ['active', 'confirmed'].includes(inv.status));
                    return (
                      <button
                        onClick={() => togglePulse(inv, !receiving)}
                        disabled={pulseToggling === inv.investor_id}
                        title={receiving ? 'Receives the weekly Investor Pulse — click to unsubscribe' : 'Not receiving the weekly Investor Pulse — click to subscribe'}
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap flex items-center gap-1 disabled:opacity-50 ${receiving ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}
                        data-testid={`investor-pulse-${inv.investor_id}`}
                      >
                        {pulseToggling === inv.investor_id ? <Loader2 size={9} className="animate-spin" /> : <Send size={9} />}
                        Pulse {receiving ? '✓' : '✗'}
                      </button>
                    );
                  })()}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs mb-3">
                <div className="bg-gray-50 rounded-lg p-2"><p className="text-xs text-gray-600">Shares</p><p className="font-bold text-gray-900">{(inv.shares || 0).toLocaleString()}</p></div>
                <div className="bg-gray-50 rounded-lg p-2"><p className="text-xs text-gray-600">Invested</p><p className="font-bold text-gray-900">${(inv.investment_amount || 0).toLocaleString()}</p></div>
              </div>
              {inv.syndicate_members > 0 && (
                <div className="flex items-center gap-1.5 rounded-lg px-3 py-2 mb-3 bg-purple-50 border border-purple-200" data-testid={`investor-syndicate-${inv.investor_id}`}>
                  <Users size={12} className="text-[#7B4FD8] flex-shrink-0" />
                  <p className="text-[11px] font-bold text-[#5B32C8]">Syndicate Lead · {inv.syndicate_members} member{inv.syndicate_members === 1 ? '' : 's'} · ${Number(inv.syndicate_total || 0).toLocaleString()} tracked</p>
                </div>
              )}
              {(instrumentsByEmail[(inv.email || '').trim().toLowerCase()] || []).length > 0 && (
                <div className="rounded-lg p-2 mb-3 space-y-1.5" style={{ backgroundColor: '#ECFDF5', border: '1px solid #A7F3D0' }} data-testid={`investor-instruments-${inv.investor_id}`}>
                  <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#047857' }}>Instruments — live conversion</p>
                  {instrumentsByEmail[(inv.email || '').trim().toLowerCase()].map(it => (
                    <div key={it.instrument_id} className="flex items-center gap-1.5 text-xs flex-wrap" data-testid={`investor-instrument-${it.instrument_id}`}>
                      <span className="px-1.5 py-0.5 rounded font-black text-[9px] text-white"
                        style={{ background: it.type === 'safe' ? '#2563EB' : it.type === 'asa' ? '#7B4FD8' : '#B45309' }}
                        title={it.label}>
                        {it.type?.toUpperCase()}
                      </span>
                      <span className="font-bold text-gray-900">${Number(it.amount_usd || 0).toLocaleString()}</span>
                      <span className="text-gray-500">· {it.discount_pct}%</span>
                      {it.current && (
                        <span className="ml-auto font-bold" style={{ color: '#047857' }}>
                          → ${Math.round(it.current.value || 0).toLocaleString()} ({it.current.multiple}x)
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {inv.email && <p className="text-xs text-gray-600 flex items-center gap-1 truncate"><Mail size={8} /> {inv.email}</p>}
              {inv.phone && <p className="text-xs text-gray-600 flex items-center gap-1 mt-0.5"><Phone size={8} /> {inv.phone}</p>}
              {inv.linkedin_url && (
                <a
                  href={inv.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-700 hover:text-blue-900 hover:underline flex items-center gap-1 mt-0.5 truncate"
                  data-testid={`linkedin-${inv.investor_id}`}
                >
                  <Linkedin size={9} /> LinkedIn profile
                </a>
              )}
              {inv.firm && <p className="text-xs text-gray-600 flex items-center gap-1 mt-0.5"><Briefcase size={8} /> {inv.firm}</p>}
              <InvestorTelemetryChip investorId={inv.investor_id} />
              {inv.passcode && (
                <div className="inline-flex items-center gap-1 mt-1.5 px-2 py-1 rounded-full text-[10px] font-bold"
                  style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
                  data-testid={`pc-chip-${inv.investor_id}`}>
                  <KeyRound size={9} />
                  Portal access active{inv.portal_passcode_set_at ? ` since ${inv.portal_passcode_set_at.slice(0, 10)}` : ''}
                  <span className="font-black">· {inv.portal_access_count || 0} login{(inv.portal_access_count || 0) === 1 ? '' : 's'}</span>
                </div>
              )}
              <RecommendOfferButton investorId={inv.investor_id} investorName={inv.name} />
              <IRTimelinePanel investorId={inv.investor_id} />
              <div className="flex gap-1.5 mt-3">
                <button onClick={() => setMsgModal(inv)} className="flex-1 py-1.5 bg-gray-900 text-white text-xs font-semibold rounded-lg flex items-center justify-center gap-1" data-testid={`msg-${inv.investor_id}`}><Send size={10} /> Message</button>
                {zoomUrl && (
                  <button onClick={() => window.open(zoomUrl, '_blank')} className="px-2.5 py-1.5 bg-blue-500 text-white rounded-lg flex items-center gap-1" title="Start Zoom Call" data-testid={`zoom-${inv.investor_id}`}><Video size={12} /></button>
                )}
                <button onClick={() => startEdit(inv)} className="px-2 py-1.5 bg-gray-100 text-gray-600 rounded-lg"><Edit2 size={12} /></button>
                <button onClick={() => handleDelete(inv.investor_id)} className="px-2 py-1.5 bg-red-50 text-red-500 rounded-lg"><Trash2 size={12} /></button>
              </div>

              {/* Data Room — visible only for confirmed/active investors. Issues a
                  6-digit numeric passcode the investor uses at /dataroom to access
                  HiBFF confidential materials. Prospects do NOT see this control. */}
              {(inv.status === 'active' || inv.status === 'confirmed') && (
                <div className="mt-3 pt-3 border-t border-gray-100" data-testid={`dr-section-${inv.investor_id}`}>
                  {drPanelFor !== inv.investor_id ? (
                    <button onClick={() => openDataRoom(inv)} disabled={drBusy === inv.investor_id}
                      className="w-full py-2 bg-purple-50 text-purple-700 text-xs font-semibold rounded-lg flex items-center justify-center gap-1.5 hover:bg-purple-100 disabled:opacity-60"
                      data-testid={`dr-open-${inv.investor_id}`}>
                      <Lock size={11} /> {drBusy === inv.investor_id ? 'Loading…' : 'Data Room — 6-digit code'}
                    </button>
                  ) : (
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 space-y-2" data-testid={`dr-panel-${inv.investor_id}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wider text-purple-800">Active Investor Data Room</span>
                        <button onClick={() => setDrPanelFor(null)} className="text-purple-700 hover:text-purple-900" data-testid={`dr-close-${inv.investor_id}`}><X size={12} /></button>
                      </div>
                      <div className="flex items-center justify-between bg-white rounded-md px-3 py-2 font-mono text-lg tracking-[0.3em] text-purple-900 font-bold" data-testid={`dr-code-${inv.investor_id}`}>
                        <span>{(drData[inv.investor_id] || {}).passcode || '------'}</span>
                        <button
                          onClick={() => navigator.clipboard?.writeText((drData[inv.investor_id] || {}).passcode || '')}
                          className="text-xs font-sans font-semibold text-purple-600 hover:text-purple-800"
                          title="Copy code"
                          data-testid={`dr-copy-${inv.investor_id}`}>
                          Copy
                        </button>
                      </div>
                      <p className="text-xs text-purple-700 leading-relaxed">
                        Share this 6-digit code with <strong>{inv.name}</strong>. They enter it at <code className="bg-white px-1 rounded">/dataroom</code> to access HiBFF confidential materials. Prospects cannot see these materials.
                      </p>
                      <div className="flex gap-1.5">
                        <a href={(drData[inv.investor_id] || {}).deep_link_path || '#'} target="_blank" rel="noopener noreferrer"
                          className="flex-1 text-center py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded"
                          data-testid={`dr-test-${inv.investor_id}`}>
                          Test link
                        </a>
                        <button onClick={() => rotateDataRoom(inv)} disabled={drBusy === inv.investor_id}
                          className="flex-1 py-1.5 bg-white border border-purple-300 text-purple-700 text-xs font-semibold rounded hover:bg-purple-50 disabled:opacity-60"
                          data-testid={`dr-rotate-${inv.investor_id}`}>
                          {drBusy === inv.investor_id ? '…' : 'Rotate code'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Investor Vault Passcode — set & email portal access from the card. */}
              {(inv.status === 'active' || inv.status === 'confirmed') && (
                <div className="mt-2" data-testid={`pc-section-${inv.investor_id}`}>
                  {pcPanelFor !== inv.investor_id ? (
                    <button onClick={() => issuePortalPasscode(inv, !!inv.email)} disabled={pcBusy === inv.investor_id}
                      className="w-full py-2 bg-amber-50 text-amber-800 text-xs font-semibold rounded-lg flex items-center justify-center gap-1.5 hover:bg-amber-100 disabled:opacity-60"
                      data-testid={`pc-issue-${inv.investor_id}`}>
                      <KeyRound size={11} />
                      {pcBusy === inv.investor_id ? 'Working…' : inv.passcode ? 'Investor Vault — rotate & email passcode' : 'Investor Vault — set & email passcode'}
                    </button>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2" data-testid={`pc-panel-${inv.investor_id}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold uppercase tracking-wider text-amber-800">Investor Vault Passcode</span>
                        <button onClick={() => setPcPanelFor(null)} className="text-amber-700 hover:text-amber-900" data-testid={`pc-close-${inv.investor_id}`}><X size={12} /></button>
                      </div>
                      <div className="flex items-center justify-between bg-white rounded-md px-3 py-2 font-mono text-sm tracking-[0.12em] text-amber-900 font-bold" data-testid={`pc-code-${inv.investor_id}`}>
                        <span>{(pcData[inv.investor_id] || {}).passcode || '————'}</span>
                        <button
                          onClick={() => navigator.clipboard?.writeText((pcData[inv.investor_id] || {}).passcode || '')}
                          className="text-xs font-sans font-semibold text-amber-600 hover:text-amber-800"
                          title="Copy passcode"
                          data-testid={`pc-copy-${inv.investor_id}`}>
                          Copy
                        </button>
                      </div>
                      <p className="text-xs text-amber-800 leading-relaxed" data-testid={`pc-note-${inv.investor_id}`}>
                        {(pcData[inv.investor_id] || {}).emailed_to
                          ? <>Emailed to <strong>{(pcData[inv.investor_id] || {}).emailed_to}</strong> with their vault link. They sign in at <code className="bg-white px-1 rounded">/investor-vault</code>.</>
                          : <>No email sent — share this passcode with <strong>{inv.name}</strong> yourself. They sign in at <code className="bg-white px-1 rounded">/investor-vault</code> with their email + this passcode.</>}
                      </p>
                      <button onClick={() => issuePortalPasscode(inv, !!inv.email)} disabled={pcBusy === inv.investor_id}
                        className="w-full py-1.5 bg-white border border-amber-300 text-amber-700 text-xs font-semibold rounded hover:bg-amber-50 disabled:opacity-60"
                        data-testid={`pc-rotate-${inv.investor_id}`}>
                        {pcBusy === inv.investor_id ? '…' : 'Rotate & re-send'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Vault Activity + Claude AI read of the investor's behaviour. */}
              {(inv.status === 'active' || inv.status === 'confirmed') && (
                <InvestorInsightPanel investorId={inv.investor_id} investorName={inv.name} />
              )}
            </div>
          </div>
        ))}
        {investors.length < 3 && <div className="col-span-full"><VaultSamplePreview accent="#1A1209" title="Investor pipeline with signal scoring" addLabel="Click '+ Add Investor' above" samples={SAMPLES_INVESTORS} dataTestid="investors-sample" /></div>}
      </div>

      {/* Message Modal */}
      {msgModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl" data-testid="msg-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-900">Message {msgModal.name}</h3>
              <button onClick={() => setMsgModal(null)}><X size={18} className="text-gray-600" /></button>
            </div>
            <div className="flex gap-2 mb-3">
              <button onClick={() => setMsgMethod('email')} className={`flex-1 py-2 rounded-lg text-xs font-semibold ${msgMethod === 'email' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`} data-testid="msg-email-tab">Email</button>
              <button onClick={() => setMsgMethod('sms')} className={`flex-1 py-2 rounded-lg text-xs font-semibold ${msgMethod === 'sms' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`} data-testid="msg-sms-tab">SMS</button>
            </div>
            <p className="text-xs text-gray-600 mb-2">To: {msgMethod === 'sms' ? msgModal.phone || 'No phone' : msgModal.email || 'No email'}</p>
            {msgMethod === 'email' && (
              <input type="text" value={msgSubject} onChange={e => setMsgSubject(e.target.value)} placeholder="Subject" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2" data-testid="msg-subject" />
            )}
            <textarea value={msgBody} onChange={e => setMsgBody(e.target.value)} placeholder="Your message..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={4} data-testid="msg-body" />
            <button onClick={handleSendMsg} disabled={sending || !msgBody.trim()} className="w-full mt-3 py-2.5 bg-gray-900 text-white font-semibold rounded-xl text-sm disabled:opacity-50" data-testid="send-msg-btn">
              {sending ? 'Sending...' : `Send ${msgMethod === 'sms' ? 'SMS' : 'Email'}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── FINANCIALS TAB ──────────────────────────────────────────────────────────

const FinancialsTab = () => {
  const [data, setData] = useState(null);
  const [burnData, setBurnData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showCashForm, setShowCashForm] = useState(false);
  const [form, setForm] = useState({ month: '', year: new Date().getFullYear(), revenue: 0, costs: 0, notes: '' });
  const [cashForm, setCashForm] = useState({ cash_in_bank: 0, notes: '' });
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const fetchAll = async () => {
    const [fin, burn] = await Promise.all([
      apiClient.request('/api/vault/financials').catch(() => null),
      apiClient.request('/api/vault/burn-rate').catch(() => null),
    ]);
    if (fin) setData(fin);
    if (burn) { setBurnData(burn); setCashForm({ cash_in_bank: burn.cash_in_bank || 0, notes: '' }); }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleSave = async () => {
    await apiClient.request('/api/vault/financials', { method: 'POST', body: JSON.stringify(form) });
    setShowForm(false); fetchAll();
  };
  const handleCashSave = async () => {
    await apiClient.request('/api/vault/burn-rate/cash', { method: 'POST', body: JSON.stringify(cashForm) });
    setShowCashForm(false); fetchAll();
  };

  if (!data) return <div className="text-center py-12 text-gray-600">Loading...</div>;

  return (
    <div className="space-y-5" data-testid="financials-tab">
      {/* Margins & Cost Ledger — subscriptions, salaries, COGS, CAC */}
      <VaultMarginsPanel />

      {/* Expenses & Revenue Ledger — merged Finance Vault (feeds Monday's Investor Pulse) */}
      <FinanceExpensesPanel />

      <div className="grid grid-cols-3 gap-3">
        <VaultCard icon={ArrowUpRight} color="#10B981" label="Total Revenue" value={`$${data.total_revenue.toLocaleString()}`} />
        <VaultCard icon={ArrowDownRight} color="#EF4444" label="Total Costs" value={`$${data.total_costs.toLocaleString()}`} />
        <VaultCard icon={DollarSign} color={data.net >= 0 ? '#10B981' : '#EF4444'} label="Net P&L" value={`$${data.net.toLocaleString()}`} />
      </div>

      {/* Burn Rate Calculator */}
      {burnData && (
        <div className={`rounded-2xl border-2 p-5 ${burnData.runway_months && burnData.runway_months < 6 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`} data-testid="burn-rate-card">
          <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2"><Flame size={14} className={burnData.runway_months && burnData.runway_months < 6 ? 'text-red-500' : 'text-emerald-600'} /> Burn Rate & Runway</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <div className="bg-white rounded-xl p-3 text-center shadow-sm">
              <p className="text-xs text-gray-600 uppercase">Cash in Bank</p>
              <p className="text-xl font-black text-gray-900">${(burnData.cash_in_bank / 1000).toFixed(0)}k</p>
              <button onClick={() => setShowCashForm(true)} className="text-xs text-blue-500 font-semibold mt-1" data-testid="update-cash-btn">Update</button>
            </div>
            <div className="bg-white rounded-xl p-3 text-center shadow-sm">
              <p className="text-xs text-gray-600 uppercase">Monthly Burn</p>
              <p className={`text-xl font-black ${burnData.burn_rate > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                {burnData.burn_rate > 0 ? `-$${(burnData.burn_rate / 1000).toFixed(1)}k` : `+$${(Math.abs(burnData.burn_rate) / 1000).toFixed(1)}k`}
              </p>
              <p className="text-xs text-gray-600">avg last 6mo</p>
            </div>
            <div className="bg-white rounded-xl p-3 text-center shadow-sm">
              <p className="text-xs text-gray-600 uppercase">Runway</p>
              <p className={`text-xl font-black ${!burnData.runway_months ? 'text-emerald-600' : burnData.runway_months < 6 ? 'text-red-600' : burnData.runway_months < 12 ? 'text-amber-600' : 'text-emerald-600'}`}>
                {burnData.runway_months ? `${burnData.runway_months}mo` : 'Profitable'}
              </p>
              <p className="text-xs text-gray-600">{burnData.runway_months && burnData.runway_months < 6 ? 'Raise soon!' : burnData.runway_months ? 'at current rate' : 'net positive'}</p>
              {burnData.ledger_monthly_costs > 0 && (
                <p className="text-[10px] text-gray-500 mt-1" data-testid="ledger-runway-note">
                  incl. cost ledger (£{burnData.ledger_monthly_costs.toLocaleString()}/mo): {burnData.runway_with_ledger ? `${burnData.runway_with_ledger}mo` : '—'}
                </p>
              )}
            </div>
            <div className="bg-white rounded-xl p-3 text-center shadow-sm">
              <p className="text-xs text-gray-600 uppercase">Break-Even</p>
              <p className="text-xl font-black text-gray-900">{burnData.break_even_date || 'N/A'}</p>
              <p className="text-xs text-gray-600">projected</p>
            </div>
          </div>
          {burnData.runway_months && burnData.runway_months > 0 && (
            <div className="w-full bg-white rounded-full h-3 overflow-hidden shadow-sm">
              <div className={`h-full rounded-full transition-all ${burnData.runway_months < 6 ? 'bg-gradient-to-r from-red-500 to-red-300' : burnData.runway_months < 12 ? 'bg-gradient-to-r from-amber-500 to-amber-300' : 'bg-gradient-to-r from-emerald-500 to-emerald-300'}`}
                style={{ width: `${Math.min((burnData.runway_months / 24) * 100, 100)}%` }} />
            </div>
          )}
        </div>
      )}

      {showCashForm && (
        <div className="bg-white rounded-2xl border-2 border-gray-300 p-5" data-testid="cash-form">
          <h3 className="font-bold text-gray-900 mb-3">Update Cash Reserves</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-gray-500 uppercase">Cash in Bank (USD)</label>
              <input type="number" value={cashForm.cash_in_bank} onChange={e => setCashForm(p => ({ ...p, cash_in_bank: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="cash-amount" /></div>
            <div className="flex items-end gap-2"><button onClick={() => setShowCashForm(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm">Cancel</button><button onClick={handleCashSave} className="flex-1 py-2 bg-gray-900 text-white font-semibold rounded-lg text-sm" data-testid="save-cash-btn">Save</button></div>
          </div>
        </div>
      )}

      <button onClick={() => setShowForm(true)} className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-xl" data-testid="add-financial-btn"><Plus size={14} /> Add Record</button>
      {showForm && (
        <div className="bg-white rounded-2xl border-2 border-gray-300 p-5" data-testid="financial-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Month</label>
              <select value={form.month} onChange={e => setForm(p => ({ ...p, month: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="fin-month">
                <option value="">Select</option>{MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Year</label>
              <input type="number" value={form.year} onChange={e => setForm(p => ({ ...p, year: parseInt(e.target.value) }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="fin-year" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Revenue (USD)</label>
              <input type="number" value={form.revenue} onChange={e => setForm(p => ({ ...p, revenue: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="fin-revenue" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Costs (USD)</label>
              <input type="number" value={form.costs} onChange={e => setForm(p => ({ ...p, costs: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="fin-costs" />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={() => setShowForm(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm">Cancel</button>
            <button onClick={handleSave} disabled={!form.month} className="flex-1 py-2 bg-gray-900 text-white font-semibold rounded-lg text-sm disabled:opacity-50" data-testid="save-financial-btn">Save</button>
          </div>
        </div>
      )}
      {data.records.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="bg-gray-50 text-gray-500 text-left"><th className="p-3">Period</th><th className="p-3">Revenue</th><th className="p-3">Costs</th><th className="p-3">Net</th></tr></thead>
            <tbody>{data.records.map((r, i) => (
              <tr key={i} className="border-t border-gray-100"><td className="p-3 font-medium">{r.month} {r.year}</td><td className="p-3 text-green-600">${(r.revenue || 0).toLocaleString()}</td><td className="p-3 text-red-500">${(r.costs || 0).toLocaleString()}</td>
                <td className={`p-3 font-bold ${(r.revenue - r.costs) >= 0 ? 'text-green-600' : 'text-red-500'}`}>${((r.revenue || 0) - (r.costs || 0)).toLocaleString()}</td></tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── OFFERS TAB ──────────────────────────────────────────────────────────────

const OffersTab = () => {
  const [data, setData] = useState(null);
  const [valData, setValData] = useState(null);
  const [showOfferForm, setShowOfferForm] = useState(false);
  const [showValForm, setShowValForm] = useState(false);
  const [offerForm, setOfferForm] = useState({ buyer_name: '', offer_amount: 0, offer_date: '', terms: '', status: 'pending', notes: '' });
  const [valForm, setValForm] = useState({ valuation: 0, basis: '', notes: '' });

  const fetchData = async () => {
    const [o, v] = await Promise.all([apiClient.request('/api/vault/offers'), apiClient.request('/api/vault/valuation')]);
    setData(o); setValData(v);
  };
  useEffect(() => { fetchData(); }, []);

  const handleAddOffer = async () => {
    await apiClient.request('/api/vault/offers', { method: 'POST', body: JSON.stringify(offerForm) });
    setShowOfferForm(false); fetchData();
  };
  const handleSetVal = async () => {
    await apiClient.request('/api/vault/valuation', { method: 'POST', body: JSON.stringify(valForm) });
    setShowValForm(false); fetchData();
  };

  if (!data) return <div className="text-center py-12 text-gray-600">Loading...</div>;

  return (
    <div className="space-y-5" data-testid="offers-tab">
      <OffersInstitutionalBrief />
      <B2BContractEngine />
      {/* Valuation Card */}
      <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-2xl border-2 border-amber-200 p-6 text-center" data-testid="valuation-card">
        <p className="text-xs uppercase tracking-wider font-bold text-amber-600 mb-1">Current Valuation</p>
        <p className="text-4xl font-black text-gray-900">{data.current_valuation > 0 ? `$${(data.current_valuation / 1000000).toFixed(2)}M` : 'Not Set'}</p>
        {valData?.current?.basis && <p className="text-xs text-gray-500 mt-1">{valData.current.basis}</p>}
        <button onClick={() => setShowValForm(true)} className="mt-3 px-4 py-1.5 bg-amber-500 text-white text-xs font-semibold rounded-lg" data-testid="update-valuation-btn">Update Valuation</button>
      </div>
      {showValForm && (
        <div className="bg-white rounded-2xl border-2 border-gray-300 p-5" data-testid="valuation-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            <div><label className="text-xs font-medium text-gray-500 uppercase">Valuation (USD)</label><input type="number" value={valForm.valuation} onChange={e => setValForm(p => ({ ...p, valuation: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="val-amount" /></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">Basis</label><input type="text" value={valForm.basis} onChange={e => setValForm(p => ({ ...p, basis: e.target.value }))} placeholder="e.g. 10x ARR" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="val-basis" /></div>
            <div className="flex items-end gap-2"><button onClick={() => setShowValForm(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm">Cancel</button><button onClick={handleSetVal} className="flex-1 py-2 bg-gray-900 text-white font-semibold rounded-lg text-sm" data-testid="save-valuation-btn">Save</button></div>
          </div>
        </div>
      )}

      {/* Offers vs Valuation */}
      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setShowOfferForm(true)} className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-xl" data-testid="add-offer-btn"><Plus size={14} /> Add Offer</button>
      </div>
      {showOfferForm && (
        <div className="bg-white rounded-2xl border-2 border-gray-300 p-5" data-testid="offer-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            <div><label className="text-xs font-medium text-gray-500 uppercase">Buyer Name</label><input type="text" value={offerForm.buyer_name} onChange={e => setOfferForm(p => ({ ...p, buyer_name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="offer-buyer" /></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">Offer Amount (USD)</label><input type="number" value={offerForm.offer_amount} onChange={e => setOfferForm(p => ({ ...p, offer_amount: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="offer-amount" /></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">Status</label>
              <select value={offerForm.status} onChange={e => setOfferForm(p => ({ ...p, status: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="offer-status">
                <option value="pending">Pending</option><option value="accepted">Accepted</option><option value="rejected">Rejected</option><option value="countered">Countered</option>
              </select></div>
          </div>
          <textarea value={offerForm.terms} onChange={e => setOfferForm(p => ({ ...p, terms: e.target.value }))} placeholder="Terms & conditions..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-3" rows={2} data-testid="offer-terms" />
          <div className="flex gap-2 mt-3"><button onClick={() => setShowOfferForm(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm">Cancel</button><button onClick={handleAddOffer} disabled={!offerForm.buyer_name} className="flex-1 py-2 bg-gray-900 text-white font-semibold rounded-lg text-sm disabled:opacity-50" data-testid="save-offer-btn">Save</button></div>
        </div>
      )}

      {/* Offers List with Valuation Comparison */}
      {data.offers.length > 0 && (
        <div className="space-y-2">{data.offers.map((o, i) => {
          const diff = data.current_valuation > 0 ? ((o.offer_amount / data.current_valuation) * 100).toFixed(0) : 0;
          return (
            <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4" data-testid={`offer-${i}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-gray-900">{o.buyer_name}</p>
                <p className="text-xs text-gray-500">{o.terms?.slice(0, 80)}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-lg font-black text-gray-900">${(o.offer_amount / 1000000).toFixed(2)}M</p>
                {data.current_valuation > 0 && <p className={`text-xs font-bold ${parseFloat(diff) >= 100 ? 'text-green-600' : 'text-red-500'}`}>{diff}% of valuation</p>}
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                o.status === 'accepted' ? 'bg-green-100 text-green-700' : o.status === 'rejected' ? 'bg-red-100 text-red-700' : o.status === 'countered' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
              }`}>{o.status}</span>
            </div>
          );
        })}</div>
      )}
    </div>
  );
};

// ─── ACQUISITIONS TAB ────────────────────────────────────────────────────────

const AcquisitionsTab = () => {
  const [targets, setTargets] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ company_name: '', website: '', turnover: 0, client_count: 0, churn_rate: 0, directors: '', owners: '', owner_contact: '', metrics_notes: '', status: 'researching', notes: '' });

  const fetchData = async () => { const d = await apiClient.request('/api/vault/acquisitions'); setTargets(d.targets); };
  useEffect(() => { fetchData(); }, []);

  const handleSave = async () => {
    await apiClient.request('/api/vault/acquisitions', { method: 'POST', body: JSON.stringify(form) });
    setShowForm(false); setForm({ company_name: '', website: '', turnover: 0, client_count: 0, churn_rate: 0, directors: '', owners: '', owner_contact: '', metrics_notes: '', status: 'researching', notes: '' });
    fetchData();
  };
  const handleDelete = async (id) => { await apiClient.request(`/api/vault/acquisitions/${id}`, { method: 'DELETE' }); fetchData(); };

  return (
    <div className="space-y-5" data-testid="acquisitions-tab">
      <AcquisitionsInstitutionalBrief />
      <button onClick={() => setShowForm(true)} className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-xl" data-testid="add-acquisition-btn"><Plus size={14} /> Add Target</button>
      {showForm && (
        <div className="bg-white rounded-2xl border-2 border-gray-300 p-5" data-testid="acquisition-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {[
              { k: 'company_name', l: 'Company Name' }, { k: 'website', l: 'Website' }, { k: 'turnover', l: 'Turnover (USD)', t: 'number' },
              { k: 'client_count', l: 'Client Count', t: 'number' }, { k: 'churn_rate', l: 'Churn Rate (%)', t: 'number' }, { k: 'directors', l: 'Directors' },
              { k: 'owners', l: 'Owners' }, { k: 'owner_contact', l: 'Owner Contact' },
            ].map(f => (
              <div key={f.k}><label className="text-xs font-medium text-gray-500 uppercase">{f.l}</label>
                <input type={f.t || 'text'} value={form[f.k] || ''} onChange={e => setForm(p => ({ ...p, [f.k]: f.t === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid={`acq-${f.k}`} />
              </div>
            ))}
            <div><label className="text-xs font-medium text-gray-500 uppercase">Status</label>
              <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="acq-status">
                <option value="researching">Researching</option><option value="contacted">Contacted</option><option value="negotiating">Negotiating</option><option value="closed">Closed</option>
              </select></div>
          </div>
          <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Notes..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-3" rows={2} data-testid="acq-notes" />
          <div className="flex gap-2 mt-3"><button onClick={() => setShowForm(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm">Cancel</button><button onClick={handleSave} disabled={!form.company_name} className="flex-1 py-2 bg-gray-900 text-white font-semibold rounded-lg text-sm disabled:opacity-50" data-testid="save-acquisition-btn">Save</button></div>
        </div>
      )}
      <div className="space-y-3" data-testid="acquisition-list">
        {targets.map(t => (
          <div key={t.target_id} className="bg-white rounded-2xl border border-gray-200 p-4" data-testid={`target-${t.target_id}`}>
            <div className="flex items-center justify-between mb-2">
              <div><p className="text-sm font-bold text-gray-900">{t.company_name}</p>{t.website && <a href={t.website} target="_blank" rel="noreferrer" className="text-xs text-blue-500 flex items-center gap-0.5"><Globe size={8} /> {t.website}</a>}</div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${t.status === 'closed' ? 'bg-green-100 text-green-700' : t.status === 'negotiating' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{t.status}</span>
                <button onClick={() => handleDelete(t.target_id)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <div className="bg-gray-50 rounded-lg p-2"><p className="text-xs text-gray-600">Turnover</p><p className="font-bold">${(t.turnover || 0).toLocaleString()}</p></div>
              <div className="bg-gray-50 rounded-lg p-2"><p className="text-xs text-gray-600">Clients</p><p className="font-bold">{(t.client_count || 0).toLocaleString()}</p></div>
              <div className="bg-gray-50 rounded-lg p-2"><p className="text-xs text-gray-600">Churn</p><p className="font-bold">{t.churn_rate || 0}%</p></div>
              <div className="bg-gray-50 rounded-lg p-2"><p className="text-xs text-gray-600">Directors</p><p className="font-bold truncate">{t.directors || '—'}</p></div>
            </div>
            {t.owners && <p className="text-xs text-gray-500 mt-2">Owners: {t.owners} | Contact: {t.owner_contact}</p>}
          </div>
        ))}
        {targets.length < 3 && <VaultSamplePreview accent="#0D9488" title="Acquisition targets & strategic options" addLabel="Click '+ Add Target' above" samples={SAMPLES_ACQUISITIONS} dataTestid="acquisitions-sample" />}
      </div>
    </div>
  );
};

// ─── PR TRACKER TAB ──────────────────────────────────────────────────────────

const PRTrackerTab = () => {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ platform: 'LinkedIn', activity_type: 'post', title: '', description: '', link: '', reach: 0, engagement: 0, cost: 0, date: '' });

  const fetchData = async () => { const d = await apiClient.request('/api/vault/pr-tracker'); setData(d); };
  useEffect(() => { fetchData(); }, []);

  const handleSave = async () => {
    await apiClient.request('/api/vault/pr-tracker', { method: 'POST', body: JSON.stringify(form) });
    setShowForm(false); setForm({ platform: 'LinkedIn', activity_type: 'post', title: '', description: '', link: '', reach: 0, engagement: 0, cost: 0, date: '' });
    fetchData();
  };

  if (!data) return <div className="text-center py-12 text-gray-600">Loading...</div>;

  return (
    <div className="space-y-5" data-testid="pr-tab">
      <PRInstitutionalBrief />
      <div className="grid grid-cols-3 gap-3">
        <VaultCard icon={Globe} color="#3B82F6" label="Total Reach" value={data.summary.total_reach.toLocaleString()} />
        <VaultCard icon={Star} color="#10B981" label="Total Engagement" value={data.summary.total_engagement.toLocaleString()} />
        <VaultCard icon={DollarSign} color="#EF4444" label="PR Spend" value={`$${data.summary.total_cost.toLocaleString()}`} />
      </div>

      {/* Recommendations */}
      {data.recommendations.length > 0 && (
        <div className="bg-violet-50 border border-violet-200 rounded-2xl p-5" data-testid="pr-recommendations">
          <h3 className="text-sm font-bold text-violet-700 mb-3 flex items-center gap-2"><Lightbulb size={14} /> AI Recommendations</h3>
          <div className="space-y-2">{data.recommendations.map((r, i) => (
            <div key={i} className="p-3 bg-white rounded-xl border border-violet-100"><p className="text-xs font-semibold text-gray-900">{r.title}</p><p className="text-xs text-gray-500 mt-0.5">{r.description}</p></div>
          ))}</div>
        </div>
      )}

      <button onClick={() => setShowForm(true)} className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-xl" data-testid="add-pr-btn"><Plus size={14} /> Log Activity</button>
      {showForm && (
        <div className="bg-white rounded-2xl border-2 border-gray-300 p-5" data-testid="pr-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            <div><label className="text-xs font-medium text-gray-500 uppercase">Platform</label>
              <select value={form.platform} onChange={e => setForm(p => ({ ...p, platform: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="pr-platform">
                {['LinkedIn','Twitter/X','Instagram','Facebook','TikTok','YouTube','Podcast','Press','Blog','Other'].map(p => <option key={p} value={p}>{p}</option>)}
              </select></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">Title</label><input type="text" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="pr-title" /></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">Date</label><input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="pr-date" /></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">Reach</label><input type="number" value={form.reach} onChange={e => setForm(p => ({ ...p, reach: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="pr-reach" /></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">Engagement</label><input type="number" value={form.engagement} onChange={e => setForm(p => ({ ...p, engagement: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="pr-engagement" /></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">Cost (USD)</label><input type="number" value={form.cost} onChange={e => setForm(p => ({ ...p, cost: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="pr-cost" /></div>
          </div>
          <div className="flex gap-2 mt-3"><button onClick={() => setShowForm(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm">Cancel</button><button onClick={handleSave} disabled={!form.title} className="flex-1 py-2 bg-gray-900 text-white font-semibold rounded-lg text-sm disabled:opacity-50" data-testid="save-pr-btn">Save</button></div>
        </div>
      )}

      {data.activities.length > 0 && (
        <div className="space-y-2">{data.activities.map((a, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-3 flex items-center gap-3 text-xs" data-testid={`pr-activity-${i}`}>
            <span className="px-2 py-1 bg-indigo-100 text-indigo-700 font-bold rounded text-xs w-16 text-center flex-shrink-0">{a.platform}</span>
            <div className="flex-1 min-w-0"><p className="font-semibold text-gray-900 truncate">{a.title}</p><p className="text-xs text-gray-600">{a.date}</p></div>
            <div className="text-right flex-shrink-0"><p className="text-xs text-gray-500">Reach: {(a.reach || 0).toLocaleString()}</p><p className="text-xs text-gray-500">Eng: {(a.engagement || 0).toLocaleString()}</p></div>
          </div>
        ))}</div>
      )}
      {data.activities.length < 3 && (
        <VaultSamplePreview
          accent="#EC4899"
          title="PR + social activity log with reach & engagement tracking"
          addLabel="Click 'Log Activity' above to record your first post"
          samples={SAMPLES_PR_SOCIAL}
          dataTestid="pr-sample"
        />
      )}
    </div>
  );
};

// ─── NOTES TAB ───────────────────────────────────────────────────────────────

const NotesTab = () => {
  const [notes, setNotes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', category: 'general', investor_id: '' });
  const [investors, setInvestors] = useState([]);

  const fetchData = async () => { const d = await apiClient.request('/api/vault/notes'); setNotes(d.notes); };
  const fetchInvestors = async () => { try { const d = await apiClient.request('/api/vault/investors'); setInvestors(d.investors || []); } catch { /* noop */ } };
  useEffect(() => { fetchData(); fetchInvestors(); }, []);

  const handleSave = async () => {
    await apiClient.request('/api/vault/notes', { method: 'POST', body: JSON.stringify({ ...form, investor_id: form.investor_id || null }) });
    setShowForm(false); setForm({ title: '', content: '', category: 'general', investor_id: '' }); fetchData();
  };
  const handleDelete = async (id) => { await apiClient.request(`/api/vault/notes/${id}`, { method: 'DELETE' }); fetchData(); };

  return (
    <div className="space-y-5" data-testid="notes-tab">
      <button onClick={() => setShowForm(true)} className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-xl" data-testid="add-note-btn"><Plus size={14} /> New Note</button>
      {showForm && (
        <div className="bg-white rounded-2xl border-2 border-gray-300 p-5" data-testid="note-form">
          <input type="text" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Title" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2" data-testid="note-title" />
          <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2" data-testid="note-category">
            <option value="general">General</option><option value="investor">Investor</option><option value="strategy">Strategy</option><option value="legal">Legal</option><option value="product">Product</option>
          </select>
          <select value={form.investor_id} onChange={e => setForm(p => ({ ...p, investor_id: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-2" data-testid="note-investor">
            <option value="">Link to investor… (optional)</option>
            {investors.map(i => <option key={i.investor_id} value={i.investor_id}>{i.name || i.email} {i.firm ? `· ${i.firm}` : ''}</option>)}
          </select>
          <textarea value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} placeholder="Write your note..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" rows={5} data-testid="note-content" />
          <div className="flex gap-2 mt-3"><button onClick={() => setShowForm(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm">Cancel</button><button onClick={handleSave} disabled={!form.title || !form.content} className="flex-1 py-2 bg-gray-900 text-white font-semibold rounded-lg text-sm disabled:opacity-50" data-testid="save-note-btn">Save</button></div>
        </div>
      )}
      <div className="space-y-2">{notes.map(n => (
        <div key={n.note_id} className="bg-white rounded-xl border border-gray-200 p-4" data-testid={`note-${n.note_id}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2"><span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-bold">{n.category}</span><h3 className="text-sm font-bold text-gray-900">{n.title}</h3></div>
            <div className="flex items-center gap-2"><span className="text-xs text-gray-600">{n.created_at?.slice(0, 10)}</span><button onClick={() => handleDelete(n.note_id)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button></div>
          </div>
          <p className="text-xs text-gray-600 whitespace-pre-wrap">{n.content}</p>
        </div>
      ))}{notes.length < 3 && <VaultSamplePreview accent="#4B5563" title="Strategy notes & founder observations" addLabel="Click '+ Add Note' above" samples={SAMPLES_NOTES} dataTestid="notes-sample" />}</div>
    </div>
  );
};

// ─── CALENDAR TAB ────────────────────────────────────────────────────────────

const CalendarTab = () => {
  const [events, setEvents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', date: '', time: '09:00', description: '', sms_reminder: true, reminder_minutes: 30, investor_id: '' });
  const [investors, setInvestors] = useState([]);

  const fetchData = async () => { const d = await apiClient.request('/api/vault/calendar'); setEvents(d.events); };
  const fetchInvestors = async () => { try { const d = await apiClient.request('/api/vault/investors'); setInvestors(d.investors || []); } catch { /* noop */ } };
  useEffect(() => { fetchData(); fetchInvestors(); }, []);

  const handleSave = async () => {
    await apiClient.request('/api/vault/calendar', { method: 'POST', body: JSON.stringify({ ...form, investor_id: form.investor_id || null }) });
    setShowForm(false); setForm({ title: '', date: '', time: '09:00', description: '', sms_reminder: true, reminder_minutes: 30, investor_id: '' }); fetchData();
  };
  const handleDelete = async (id) => { await apiClient.request(`/api/vault/calendar/${id}`, { method: 'DELETE' }); fetchData(); };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = events.filter(e => e.date >= today);
  const past = events.filter(e => e.date < today);

  return (
    <div className="space-y-5" data-testid="calendar-tab">
      <button onClick={() => setShowForm(true)} className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-xl" data-testid="add-event-btn"><Plus size={14} /> Add Event</button>
      {showForm && (
        <div className="bg-white rounded-2xl border-2 border-gray-300 p-5" data-testid="event-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            <div><label className="text-xs font-medium text-gray-500 uppercase">Title</label><input type="text" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="evt-title" /></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">Date</label><input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="evt-date" /></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">Time</label><input type="time" value={form.time} onChange={e => setForm(p => ({ ...p, time: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="evt-time" /></div>
          </div>
          <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Description..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-3" rows={2} data-testid="evt-desc" />
          <select value={form.investor_id} onChange={e => setForm(p => ({ ...p, investor_id: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-2" data-testid="evt-investor">
            <option value="">Link to investor… (optional)</option>
            {investors.map(i => <option key={i.investor_id} value={i.investor_id}>{i.name || i.email} {i.firm ? `· ${i.firm}` : ''}</option>)}
          </select>
          <div className="flex items-center gap-3 mt-3">
            <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={form.sms_reminder} onChange={e => setForm(p => ({ ...p, sms_reminder: e.target.checked }))} data-testid="evt-sms-toggle" /><span className="text-gray-700">SMS Reminder to +44...834</span></label>
          </div>
          <div className="flex gap-2 mt-3"><button onClick={() => setShowForm(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm">Cancel</button><button onClick={handleSave} disabled={!form.title || !form.date} className="flex-1 py-2 bg-gray-900 text-white font-semibold rounded-lg text-sm disabled:opacity-50" data-testid="save-event-btn">Save</button></div>
        </div>
      )}

      {upcoming.length > 0 && (
        <div><h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-2">Upcoming</h3>
          <div className="space-y-2">{upcoming.map(e => (
            <div key={e.event_id} className="bg-white rounded-xl border border-gray-200 p-3 flex items-center gap-3" data-testid={`event-${e.event_id}`}>
              <div className="w-12 h-12 rounded-xl bg-blue-50 flex flex-col items-center justify-center flex-shrink-0"><span className="text-xs text-blue-500 font-bold">{e.date?.slice(5, 7)}/{e.date?.slice(8)}</span><span className="text-xs text-gray-600">{e.time}</span></div>
              <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-gray-900">{e.title}</p>{e.description && <p className="text-xs text-gray-500 truncate">{e.description}</p>}{e.sms_reminder && <span className="text-xs text-blue-500 font-medium">SMS reminder on</span>}</div>
              <button onClick={() => handleDelete(e.event_id)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
            </div>
          ))}</div>
        </div>
      )}
      {past.length > 0 && (
        <div><h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-2">Past Events</h3>
          <div className="space-y-1">{past.slice(0, 10).map(e => (
            <div key={e.event_id} className="flex items-center gap-2 text-xs text-gray-600 py-1"><Clock size={10} /><span>{e.date}</span><span className="text-gray-600">{e.title}</span></div>
          ))}</div>
        </div>
      )}
      {events.length < 3 && <VaultSamplePreview accent="#2563EB" title="Upcoming meetings & recurring events" addLabel="Click '+ Add Event' above" samples={SAMPLES_CALENDAR} dataTestid="calendar-sample" />}
    </div>
  );
};

// ─── KPI TAB ─────────────────────────────────────────────────────────────────

const KPITab = () => {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ month: '', year: new Date().getFullYear(), mau: 0, mrr: 0, churn_rate: 0, cac: 0, ltv: 0, nps: 0, notes: '' });
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  const fetchData = async () => { const d = await apiClient.request('/api/vault/kpis'); setData(d); };
  useEffect(() => { fetchData(); }, []);

  const handleSave = async () => {
    await apiClient.request('/api/vault/kpis', { method: 'POST', body: JSON.stringify(form) });
    setShowForm(false); setForm({ month: '', year: new Date().getFullYear(), mau: 0, mrr: 0, churn_rate: 0, cac: 0, ltv: 0, nps: 0, notes: '' }); fetchData();
  };

  if (!data) return <div className="text-center py-12 text-gray-600">Loading...</div>;

  const latest = data.records[0] || {};
  const trends = data.trends || {};
  const TrendBadge = ({ metric }) => {
    const t = trends[metric];
    if (!t) return null;
    return <span className={`text-xs font-bold ${t.good ? 'text-emerald-600' : 'text-red-500'}`}>{t.change > 0 ? '+' : ''}{t.change}%</span>;
  };

  const kpiCards = [
    { key: 'mau', label: 'MAU', value: (latest.mau || 0).toLocaleString(), color: '#3B82F6', icon: Users },
    { key: 'mrr', label: 'MRR', value: `$${(latest.mrr || 0).toLocaleString()}`, color: '#10B981', icon: DollarSign },
    { key: 'churn_rate', label: 'Churn Rate', value: `${latest.churn_rate || 0}%`, color: '#EF4444', icon: ArrowDownRight },
    { key: 'cac', label: 'CAC', value: `$${latest.cac || 0}`, color: '#F59E0B', icon: Target },
    { key: 'ltv', label: 'LTV', value: `$${latest.ltv || 0}`, color: '#8B5CF6', icon: TrendingUp },
    { key: 'nps', label: 'NPS', value: latest.nps || 0, color: '#EC4899', icon: Star },
  ];

  return (
    <div className="space-y-5" data-testid="kpis-tab">
      {/* Auto-derive status banner — surfaces whether the headline tiles
          are pulling live db.users / db.subscriptions figures or showing
          a manual override the founder logged. Master Admin can override
          via the existing "Log KPIs" form, which wins automatically. */}
      {data.derived_current && (
        <div
          className="flex items-center gap-3 px-4 py-3 rounded-xl"
          style={{
            backgroundColor: data.has_manual_override ? '#FEF3C7' : '#ECFDF5',
            border: `1px solid ${data.has_manual_override ? '#FCD34D' : '#A7F3D0'}`,
          }}
          data-testid="kpi-source-banner"
        >
          <Activity size={14} style={{ color: data.has_manual_override ? '#92400E' : '#065F46' }} />
          <div className="flex-1 text-xs" style={{ color: data.has_manual_override ? '#92400E' : '#065F46' }}>
            {data.has_manual_override ? (
              <>
                <span className="font-bold">Manual override active</span> for{' '}
                {latest.month} {latest.year}. Auto-derived current snapshot:{' '}
                MAU {(data.derived_current.mau || 0).toLocaleString()} ·
                MRR ${(data.derived_current.mrr || 0).toLocaleString()} ·
                Churn {data.derived_current.churn_rate || 0}%
              </>
            ) : (
              <>
                <span className="font-bold">Live · auto-derived from db.users + db.subscriptions.</span>{' '}
                Computed {new Date(data.derived_current.derived_at).toISOString().slice(11, 16)} UTC.{' '}
                Click <span className="font-semibold">Log KPIs</span> below to lock a manual override.
              </>
            )}
          </div>
        </div>
      )}

      {/* Latest KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {kpiCards.map(k => (
          <div key={k.key} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm relative" data-testid={`kpi-${k.key}`}>
            {latest.source === 'derived' && (
              <span
                className="absolute -top-1.5 left-3 text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                style={{ backgroundColor: '#10B981', color: 'white', boxShadow: '0 1px 3px rgba(16,185,129,0.35)' }}
                title="Auto-derived from live MongoDB"
              >● LIVE</span>
            )}
            <div className="flex items-center justify-between mb-1 mt-1">
              <k.icon size={14} style={{ color: k.color }} />
              <TrendBadge metric={k.key} />
            </div>
            <p className="text-xl font-black text-gray-900">{k.value}</p>
            <p className="text-xs text-gray-500 uppercase tracking-wider">{k.label}</p>
          </div>
        ))}
      </div>

      {/* LTV:CAC Ratio */}
      {latest.ltv > 0 && latest.cac > 0 && (
        <div className={`rounded-2xl border-2 p-5 text-center ${(latest.ltv / latest.cac) >= 3 ? 'bg-emerald-50 border-emerald-200' : (latest.ltv / latest.cac) >= 1 ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`} data-testid="ltv-cac-ratio">
          <p className="text-xs uppercase tracking-wider font-bold text-gray-600 mb-1">LTV : CAC Ratio</p>
          <p className="text-4xl font-black text-gray-900">{(latest.ltv / latest.cac).toFixed(1)}x</p>
          <p className="text-xs text-gray-500 mt-1">{(latest.ltv / latest.cac) >= 3 ? 'Excellent — strong unit economics' : (latest.ltv / latest.cac) >= 1 ? 'Acceptable — room to improve' : 'Warning — losing money per customer'}</p>
        </div>
      )}

      {/* KPI Sparklines */}
      {data.records.length > 1 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="kpi-trends">
          <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2"><Activity size={14} /> KPI Trends</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {['mau', 'mrr'].map(metric => {
              const values = [...data.records].reverse().map(r => r[metric] || 0);
              const max = Math.max(...values, 1);
              return (
                <div key={metric}>
                  <p className="text-xs text-gray-500 uppercase font-semibold mb-2">{metric === 'mau' ? 'Monthly Active Users' : 'Monthly Recurring Revenue'}</p>
                  <div className="flex items-end gap-1 h-20">
                    {values.map((v, i) => (
                      <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                        <div className={`w-full rounded-t transition-all ${metric === 'mau' ? 'bg-blue-400' : 'bg-emerald-400'}`} style={{ height: `${(v / max) * 100}%`, minHeight: '2px' }} title={`${metric === 'mrr' ? '$' : ''}${v.toLocaleString()}`} />
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between text-xs text-gray-600 mt-1">
                    {[...data.records].reverse().map((r, i) => <span key={i}>{r.month?.slice(0, 3)}</span>)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setShowForm(true)} className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-xl" data-testid="add-kpi-btn"><Plus size={14} /> Log KPIs</button>
        {data.derived_current && !data.has_manual_override && (
          <button
            onClick={async () => {
              if (!window.confirm(`Freeze the current auto-derived snapshot into ${data.derived_current.month} ${data.derived_current.year} as a manual row? This locks the figures for board reporting.`)) return;
              try {
                await apiClient.request('/api/vault/kpis/sync-derived', { method: 'POST' });
                await fetchData();
              } catch (e) {
                window.alert(`Sync failed: ${e.message || e}`);
              }
            }}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl"
            style={{ backgroundColor: '#10B981', color: 'white' }}
            data-testid="sync-derived-kpi-btn"
            title="Freeze the live auto-derived snapshot into a manual row"
          >
            <CheckCircle size={14} /> Sync derived → manual
          </button>
        )}
      </div>
      {showForm && (
        <div className="bg-white rounded-2xl border-2 border-gray-300 p-5" data-testid="kpi-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-4 gap-3">
            <div><label className="text-xs font-medium text-gray-500 uppercase">Month</label>
              <select value={form.month} onChange={e => setForm(p => ({ ...p, month: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="kpi-month">
                <option value="">Select</option>{MONTHS.map(m => <option key={m} value={m}>{m}</option>)}
              </select></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">Year</label><input type="number" value={form.year} onChange={e => setForm(p => ({ ...p, year: parseInt(e.target.value) }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="kpi-year" /></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">MAU</label><input type="number" value={form.mau} onChange={e => setForm(p => ({ ...p, mau: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="kpi-mau" /></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">MRR (USD)</label><input type="number" value={form.mrr} onChange={e => setForm(p => ({ ...p, mrr: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="kpi-mrr" /></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">Churn (%)</label><input type="number" step="0.1" value={form.churn_rate} onChange={e => setForm(p => ({ ...p, churn_rate: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="kpi-churn" /></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">CAC (USD)</label><input type="number" value={form.cac} onChange={e => setForm(p => ({ ...p, cac: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="kpi-cac" /></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">LTV (USD)</label><input type="number" value={form.ltv} onChange={e => setForm(p => ({ ...p, ltv: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="kpi-ltv" /></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">NPS</label><input type="number" value={form.nps} onChange={e => setForm(p => ({ ...p, nps: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="kpi-nps" /></div>
          </div>
          <div className="flex gap-2 mt-3"><button onClick={() => setShowForm(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm">Cancel</button><button onClick={handleSave} disabled={!form.month} className="flex-1 py-2 bg-gray-900 text-white font-semibold rounded-lg text-sm disabled:opacity-50" data-testid="save-kpi-btn">Save</button></div>
        </div>
      )}

      {/* KPI History Table */}
      {data.records.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="bg-gray-50 text-gray-500 text-left"><th className="p-3">Period</th><th className="p-3">MAU</th><th className="p-3">MRR</th><th className="p-3">Churn</th><th className="p-3">CAC</th><th className="p-3">LTV</th><th className="p-3">NPS</th></tr></thead>
            <tbody>{data.records.map((r, i) => (
              <tr key={i} className="border-t border-gray-100">
                <td className="p-3 font-medium whitespace-nowrap">{r.month} {r.year}</td>
                <td className="p-3">{(r.mau || 0).toLocaleString()}</td>
                <td className="p-3 text-emerald-600">${(r.mrr || 0).toLocaleString()}</td>
                <td className="p-3 text-red-500">{r.churn_rate || 0}%</td>
                <td className="p-3">${r.cac || 0}</td>
                <td className="p-3 text-blue-600">${r.ltv || 0}</td>
                <td className="p-3">{r.nps || 0}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}

      {/* Sample-preview empty state — only when no historical KPI records exist yet */}
      {data.records.length === 0 && (
        <VaultSamplePreview
          accent="#06B6D4"
          title="Monthly KPI dashboard with trend sparklines"
          addLabel="Click 'Log KPIs' above to add your first month"
          samples={SAMPLES_KPIS}
          dataTestid="kpis-sample"
          cols="kpi"
        />
      )}
    </div>
  );
};

// ─── MILESTONES TAB ──────────────────────────────────────────────────────────

const CATEGORY_COLORS = { product: 'bg-blue-100 text-blue-700', growth: 'bg-emerald-100 text-emerald-700', fundraising: 'bg-amber-100 text-amber-700', revenue: 'bg-purple-100 text-purple-700' };

// Inline editor for a single milestone row — keeps the row in-place so the
// founder never loses context while editing during a board update.
const MilestoneInlineEditor = ({ m, form, setForm, onSave, onCancel, saving }) => (
  <div className="flex-1 min-w-0 space-y-2" data-testid={`ms-edit-form-${m.milestone_id}`}>
    <div className="grid grid-cols-1 sm:grid-cols-12 gap-2">
      <input
        type="text"
        value={form.title}
        onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
        placeholder="Milestone title"
        className="sm:col-span-6 w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
        data-testid={`ms-edit-title-${m.milestone_id}`}
      />
      <input
        type="date"
        value={form.date}
        onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
        className="sm:col-span-3 w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
        data-testid={`ms-edit-date-${m.milestone_id}`}
      />
      <select
        value={form.category}
        onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
        className="sm:col-span-3 w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
        data-testid={`ms-edit-category-${m.milestone_id}`}
      >
        <option value="product">Product</option>
        <option value="growth">Growth</option>
        <option value="revenue">Revenue</option>
        <option value="fundraising">Fundraising</option>
      </select>
    </div>
    <textarea
      value={form.description}
      onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
      rows={2}
      placeholder="Description (optional)"
      className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm"
      data-testid={`ms-edit-desc-${m.milestone_id}`}
    />
    <div className="flex gap-2 justify-end">
      <button
        onClick={onCancel}
        className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200"
        data-testid={`ms-edit-cancel-${m.milestone_id}`}
      >
        Cancel
      </button>
      <button
        onClick={onSave}
        disabled={saving || !form.title.trim()}
        className="px-3 py-1.5 rounded-lg text-xs font-bold bg-gray-900 text-white disabled:opacity-50"
        data-testid={`ms-edit-save-${m.milestone_id}`}
      >
        {saving ? 'Saving…' : 'Save'}
      </button>
    </div>
  </div>
);

const MilestonesTab = () => {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', date: '', category: 'product', description: '', achieved: false });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', date: '', category: 'product', description: '' });
  const [saving, setSaving] = useState(false);
  // Drag-to-reorder for the Upcoming list
  const [dragId, setDragId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [reordering, setReordering] = useState(false);
  const [orderedUpcoming, setOrderedUpcoming] = useState(null); // optimistic override

  const fetchData = async () => {
    const d = await apiClient.request('/api/vault/milestones');
    setData(d);
    setOrderedUpcoming(null);
  };
  useEffect(() => { fetchData(); }, []);

  const handleSave = async () => {
    await apiClient.request('/api/vault/milestones', { method: 'POST', body: JSON.stringify(form) });
    setShowForm(false); setForm({ title: '', date: '', category: 'product', description: '', achieved: false }); fetchData();
  };
  const handleToggle = async (m) => {
    await apiClient.request(`/api/vault/milestones/${m.milestone_id}`, { method: 'PUT', body: JSON.stringify({ ...m, achieved: !m.achieved }) });
    fetchData();
  };
  const handleDelete = async (id) => { await apiClient.request(`/api/vault/milestones/${id}`, { method: 'DELETE' }); fetchData(); };

  const startEdit = (m) => {
    setEditingId(m.milestone_id);
    setEditForm({ title: m.title || '', date: m.date || '', category: m.category || 'product', description: m.description || '' });
  };
  const cancelEdit = () => { setEditingId(null); };
  const saveEdit = async (m) => {
    if (!editForm.title.trim()) return;
    setSaving(true);
    try {
      await apiClient.request(`/api/vault/milestones/${m.milestone_id}`, {
        method: 'PUT',
        body: JSON.stringify({ ...m, ...editForm, title: editForm.title.trim() }),
      });
      setEditingId(null);
      await fetchData();
    } finally {
      setSaving(false);
    }
  };

  // Drag handlers — HTML5 native, no extra deps.
  const onDragStart = (e, mid) => {
    setDragId(mid);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', mid);
  };
  const onDragOver = (e, mid) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetId(mid);
  };
  const onDragLeave = () => setDropTargetId(null);
  const onDragEnd = () => { setDragId(null); setDropTargetId(null); };

  const reorderList = (list, draggedId, dropOnId) => {
    if (draggedId === dropOnId) return list;
    const from = list.findIndex((x) => x.milestone_id === draggedId);
    const to = list.findIndex((x) => x.milestone_id === dropOnId);
    if (from < 0 || to < 0) return list;
    const next = list.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    return next;
  };

  const onDrop = async (e, dropOnId) => {
    e.preventDefault();
    const draggedId = e.dataTransfer.getData('text/plain') || dragId;
    setDragId(null); setDropTargetId(null);
    if (!draggedId || draggedId === dropOnId) return;
    const baseUpcoming = orderedUpcoming || data.milestones.filter((m) => !m.achieved);
    const next = reorderList(baseUpcoming, draggedId, dropOnId);
    setOrderedUpcoming(next);
    setReordering(true);
    try {
      await apiClient.request('/api/vault/milestones/reorder', {
        method: 'POST',
        body: JSON.stringify({ ordered_ids: next.map((m) => m.milestone_id) }),
      });
    } finally {
      setReordering(false);
    }
  };

  if (!data) return <div className="text-center py-12 text-gray-600">Loading...</div>;

  const achieved = data.milestones
    .filter(m => m.achieved)
    .slice()
    .sort((a, b) => (b.date || '').localeCompare(a.date || ''));
  const upcomingFromData = data.milestones.filter(m => !m.achieved);
  const upcoming = orderedUpcoming || upcomingFromData;

  return (
    <div className="space-y-5" data-testid="milestones-tab">
      {/* Sprint 172 — Strategic Roadmap timeline above the milestone CRUD. */}
      <StrategicRoadmap />

      {/* Progress */}
      <div className="bg-gradient-to-r from-amber-50 to-emerald-50 rounded-2xl border border-amber-200 p-6 text-center" data-testid="milestone-progress">
        <p className="text-xs uppercase tracking-wider font-bold text-gray-600 mb-2">Journey Progress</p>
        <p className="text-5xl font-black text-gray-900">{data.achieved_count}<span className="text-lg text-gray-600 font-normal"> / {data.milestones.length}</span></p>
        <div className="w-full max-w-md mx-auto bg-white rounded-full h-3 overflow-hidden mt-3 shadow-sm">
          <div className="bg-gradient-to-r from-amber-400 to-emerald-500 h-full rounded-full transition-all" style={{ width: `${(data.achieved_count / Math.max(data.milestones.length, 1)) * 100}%` }} />
        </div>
        <p className="text-xs text-gray-500 mt-2">{data.upcoming_count} milestones remaining</p>
      </div>

      <button onClick={() => setShowForm(true)} className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-xl" data-testid="add-milestone-btn"><Plus size={14} /> Add Milestone</button>
      {showForm && (
        <div className="bg-white rounded-2xl border-2 border-gray-300 p-5" data-testid="milestone-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            <div><label className="text-xs font-medium text-gray-500 uppercase">Title</label><input type="text" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="ms-title" /></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">Target Date</label><input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="ms-date" /></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">Category</label>
              <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="ms-category">
                <option value="product">Product</option><option value="growth">Growth</option><option value="revenue">Revenue</option><option value="fundraising">Fundraising</option>
              </select></div>
          </div>
          <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Description..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-3" rows={2} data-testid="ms-desc" />
          <label className="flex items-center gap-2 mt-3 text-xs"><input type="checkbox" checked={form.achieved} onChange={e => setForm(p => ({ ...p, achieved: e.target.checked }))} data-testid="ms-achieved" /><span className="text-gray-700">Already achieved</span></label>
          <div className="flex gap-2 mt-3"><button onClick={() => setShowForm(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm">Cancel</button><button onClick={handleSave} disabled={!form.title} className="flex-1 py-2 bg-gray-900 text-white font-semibold rounded-lg text-sm disabled:opacity-50" data-testid="save-milestone-btn">Save</button></div>
        </div>
      )}

      {/* Upcoming Milestones */}
      {upcoming.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center justify-between">
            <span className="flex items-center gap-2"><Zap size={14} /> Upcoming</span>
            <span className="text-xs font-normal normal-case text-gray-600">
              {reordering ? 'Saving order…' : 'Drag a row to reorder'}
            </span>
          </h3>
          <div className="space-y-2" data-testid="milestones-upcoming-list">{upcoming.map(m => (
            <div
              key={m.milestone_id}
              draggable={editingId !== m.milestone_id}
              onDragStart={(e) => onDragStart(e, m.milestone_id)}
              onDragOver={(e) => onDragOver(e, m.milestone_id)}
              onDragLeave={onDragLeave}
              onDragEnd={onDragEnd}
              onDrop={(e) => onDrop(e, m.milestone_id)}
              className={`bg-white rounded-xl border-2 p-4 flex items-center gap-4 transition-all ${
                dropTargetId === m.milestone_id ? 'border-orange-400 bg-orange-50' : 'border-dashed border-gray-300'
              } ${dragId === m.milestone_id ? 'opacity-50' : ''}`}
              data-testid={`ms-${m.milestone_id}`}
            >
              <span
                className="cursor-grab text-gray-600 hover:text-gray-900 flex-shrink-0"
                title="Drag to reorder"
                data-testid={`ms-drag-handle-${m.milestone_id}`}
              >
                <GripVertical size={14} />
              </span>
              <button onClick={() => handleToggle(m)} className="w-8 h-8 rounded-full border-2 border-gray-300 flex items-center justify-center flex-shrink-0 hover:border-emerald-400 hover:bg-emerald-50 transition-colors" data-testid={`ms-toggle-${m.milestone_id}`}>
                <CheckCircle size={16} className="text-gray-300" />
              </button>
              {editingId === m.milestone_id ? (
                <MilestoneInlineEditor m={m} form={editForm} setForm={setEditForm} onSave={() => saveEdit(m)} onCancel={cancelEdit} saving={saving} />
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-sm font-bold text-gray-900">{m.title}</p>
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${CATEGORY_COLORS[m.category] || 'bg-gray-100 text-gray-600'}`}>{m.category}</span>
                    </div>
                    {m.description && <p className="text-xs text-gray-600">{m.description}</p>}
                  </div>
                  <div className="text-right flex-shrink-0 flex items-center gap-2">
                    {m.date && <p className="text-xs text-gray-600">{m.date}</p>}
                    <button onClick={() => startEdit(m)} className="text-gray-600 hover:text-gray-900" data-testid={`ms-edit-${m.milestone_id}`} title="Edit"><Edit2 size={12} /></button>
                    <button onClick={() => handleDelete(m.milestone_id)} className="text-red-400 hover:text-red-600"><Trash2 size={12} /></button>
                  </div>
                </>
              )}
            </div>
          ))}</div>
        </div>
      )}

      {/* Achieved Milestones */}
      {achieved.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2"><CheckCircle size={14} className="text-emerald-500" /> Achieved</h3>
          <div className="space-y-2">{achieved.map(m => (
            <div key={m.milestone_id} className="bg-emerald-50 rounded-xl border border-emerald-200 p-4 flex items-center gap-4" data-testid={`ms-${m.milestone_id}`}>
              <button onClick={() => handleToggle(m)} className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0" data-testid={`ms-toggle-${m.milestone_id}`}>
                <CheckCircle size={16} className="text-white" />
              </button>
              {editingId === m.milestone_id ? (
                <MilestoneInlineEditor m={m} form={editForm} setForm={setEditForm} onSave={() => saveEdit(m)} onCancel={cancelEdit} saving={saving} />
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <p className="text-sm font-bold text-gray-900 line-through opacity-70">{m.title}</p>
                      <span className={`px-2 py-0.5 rounded text-xs font-bold ${CATEGORY_COLORS[m.category] || 'bg-gray-100 text-gray-600'}`}>{m.category}</span>
                    </div>
                    {m.description && <p className="text-xs text-gray-600">{m.description}</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {m.date && <p className="text-xs text-gray-600">{m.date}</p>}
                    <button onClick={() => startEdit(m)} className="text-gray-600 hover:text-gray-900" data-testid={`ms-edit-${m.milestone_id}`} title="Edit"><Edit2 size={12} /></button>
                  </div>
                </>
              )}
            </div>
          ))}</div>
        </div>
      )}
      {data.milestones.length < 3 && <VaultSamplePreview accent="#7C3AED" title="Quarterly milestones & execution roadmap" addLabel="Click '+ Add Milestone' above" samples={SAMPLES_MILESTONES} dataTestid="milestones-sample" cols="stack" />}
    </div>
  );
};

// ─── FUNDRAISE TAB ───────────────────────────────────────────────────────────

const ROUND_COLORS = { 'Pre-Seed': '#94A3B8', 'Seed': '#3B82F6', 'Series A': '#10B981', 'Series B': '#8B5CF6', 'Series C': '#F59E0B', 'Pre-IPO': '#EC4899' };
const STATUS_STYLES = { planning: 'bg-gray-100 text-gray-600', active: 'bg-blue-100 text-blue-700', closed: 'bg-emerald-100 text-emerald-700' };

const FundraiseTab = () => {
  const [data, setData] = useState(null);
  const [simulation, setSimulation] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ round_name: 'Series A', target_raise: 0, pre_money_valuation: 0, status: 'planning', lead_investor: '', terms: '', target_close_date: '', notes: '' });

  const fetchData = async () => {
    const [d, sim] = await Promise.all([
      apiClient.request('/api/vault/fundraise/rounds'),
      apiClient.request('/api/vault/fundraise/simulate', { method: 'POST' }),
    ]);
    setData(d); setSimulation(sim);
  };
  useEffect(() => { fetchData(); }, []);

  const handleSave = async () => {
    await apiClient.request('/api/vault/fundraise/rounds', { method: 'POST', body: JSON.stringify(form) });
    setShowForm(false); fetchData();
  };
  const handleDelete = async (id) => { await apiClient.request(`/api/vault/fundraise/rounds/${id}`, { method: 'DELETE' }); fetchData(); };

  if (!data) return <div className="text-center py-12 text-gray-600">Loading...</div>;

  return (
    <div className="space-y-5" data-testid="fundraise-tab">
      {/* Use-of-Funds institutional brief */}
      <FundraiseInstitutionalBrief />

      {/* Founder Position */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-6 text-white" data-testid="founder-position">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-gray-600 mb-1">Your Current Stake</p>
            <p className="text-5xl font-black">{data.founder_pct}%</p>
            <p className="text-xs text-gray-600 mt-1">{data.founder_shares.toLocaleString()} of {data.current_total_shares.toLocaleString()} shares</p>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wider text-gray-600 mb-1">Rounds</p>
            <p className="text-3xl font-black">{data.rounds.length}</p>
            <p className="text-xs text-gray-600 mt-1">{data.rounds.filter(r => r.status === 'closed').length} closed</p>
          </div>
        </div>
      </div>

      {/* Dilution Waterfall */}
      {simulation && simulation.simulation.length > 1 && (
        <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="dilution-waterfall">
          <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2"><PieChart size={14} /> Dilution Waterfall</h3>
          <div className="flex items-end gap-2 h-48">
            {simulation.simulation.map((s, i) => {
              const barH = (s.founder_pct / 100) * 100;
              const color = ROUND_COLORS[s.stage] || '#6B7280';
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <p className="text-xs font-black text-gray-900">{s.founder_pct}%</p>
                  <div className="w-full rounded-t-lg transition-all" style={{ height: `${barH}%`, backgroundColor: color, minHeight: '8px' }} />
                  <p className="text-xs text-gray-500 text-center leading-tight font-semibold">{s.stage}</p>
                  {s.valuation > 0 && <p className="text-xs text-gray-600">${(s.valuation / 1000000).toFixed(0)}M</p>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <button onClick={() => setShowForm(true)} className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-xl" data-testid="add-round-btn"><Plus size={14} /> Add Round</button>
      {showForm && (
        <div className="bg-white rounded-2xl border-2 border-gray-300 p-5" data-testid="round-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-3 gap-3">
            <div><label className="text-xs font-medium text-gray-500 uppercase">Round</label>
              <select value={form.round_name} onChange={e => setForm(p => ({ ...p, round_name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="round-name">
                {['Pre-Seed','Seed','Series A','Series B','Series C','Pre-IPO','Bridge','Convertible Note'].map(r => <option key={r} value={r}>{r}</option>)}
              </select></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">Target Raise (USD)</label><input type="number" value={form.target_raise} onChange={e => setForm(p => ({ ...p, target_raise: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="round-raise" /></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">Pre-Money Valuation (USD)</label><input type="number" value={form.pre_money_valuation} onChange={e => setForm(p => ({ ...p, pre_money_valuation: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="round-premoney" /></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">Lead Investor</label><input type="text" value={form.lead_investor} onChange={e => setForm(p => ({ ...p, lead_investor: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="round-lead" /></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">Status</label>
              <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="round-status">
                <option value="planning">Planning</option><option value="active">Active</option><option value="closed">Closed</option>
              </select></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">Target Close Date</label><input type="date" value={form.target_close_date} onChange={e => setForm(p => ({ ...p, target_close_date: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="round-date" /></div>
          </div>
          <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Terms, notes..." className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-3" rows={2} data-testid="round-notes" />
          <div className="flex gap-2 mt-3"><button onClick={() => setShowForm(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm">Cancel</button><button onClick={handleSave} disabled={!form.target_raise} className="flex-1 py-2 bg-gray-900 text-white font-semibold rounded-lg text-sm disabled:opacity-50" data-testid="save-round-btn">Save</button></div>
        </div>
      )}

      {/* Rounds */}
      <div className="space-y-3">
        {data.rounds.map(r => (
          <div key={r.round_id} className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm" data-testid={`round-${r.round_id}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: ROUND_COLORS[r.round_name] || '#6B7280' }} />
                <h3 className="text-sm font-black text-gray-900">{r.round_name}</h3>
                <span className={`px-2 py-0.5 rounded text-xs font-bold ${STATUS_STYLES[r.status] || 'bg-gray-100 text-gray-600'}`}>{r.status}</span>
              </div>
              <button onClick={() => handleDelete(r.round_id)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div><p className="text-xs text-gray-600 uppercase">Raise</p><p className="font-bold text-gray-900">${(r.target_raise / 1000000).toFixed(1)}M</p></div>
              <div><p className="text-xs text-gray-600 uppercase">Pre-Money</p><p className="font-bold text-gray-900">${(r.pre_money_valuation / 1000000).toFixed(1)}M</p></div>
              <div><p className="text-xs text-gray-600 uppercase">Post-Money</p><p className="font-bold text-emerald-600">${((r.post_money_valuation || 0) / 1000000).toFixed(1)}M</p></div>
              <div><p className="text-xs text-gray-600 uppercase">Dilution</p><p className="font-bold text-red-500">{r.dilution_pct || 0}%</p></div>
              {r.share_price > 0 && <div><p className="text-xs text-gray-600 uppercase">Share Price</p><p className="font-bold text-gray-900">${r.share_price?.toFixed(2)}</p></div>}
              {r.new_shares_issued > 0 && <div><p className="text-xs text-gray-600 uppercase">New Shares</p><p className="font-bold text-gray-900">{(r.new_shares_issued || 0).toLocaleString()}</p></div>}
              {r.lead_investor && <div className="col-span-2"><p className="text-xs text-gray-600 uppercase">Lead Investor</p><p className="font-bold text-gray-900">{r.lead_investor}</p></div>}
            </div>
            {r.notes && <p className="text-xs text-gray-500 mt-3 border-t border-gray-100 pt-3">{r.notes}</p>}
          </div>
        ))}
      </div>
      {data.rounds.length < 3 && <VaultSamplePreview accent="#10B981" title="Capital raise plan & round-by-round tracking" addLabel="Click '+ Add Round' above" samples={SAMPLES_FUNDRAISE} dataTestid="fundraise-sample" />}
    </div>
  );
};

// ─── NASDAQ / IPO TAB ────────────────────────────────────────────────────────

const NasdaqTab = () => {
  const [checklist, setChecklist] = useState(null);
  const [board, setBoard] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [showBoardForm, setShowBoardForm] = useState(false);
  const [showPhaseForm, setShowPhaseForm] = useState(false);
  const [boardForm, setBoardForm] = useState({ name: '', role: 'independent', committee: '', bio: '', since: '' });
  const [phaseForm, setPhaseForm] = useState({ phase: 'Pre-IPO Foundation', title: '', target_date: '', status: 'not_started', notes: '' });

  const fetchAll = async () => {
    const [c, b, t] = await Promise.all([
      apiClient.request('/api/vault/nasdaq/checklist'),
      apiClient.request('/api/vault/nasdaq/board'),
      apiClient.request('/api/vault/nasdaq/timeline'),
    ]);
    setChecklist(c); setBoard(b); setTimeline(t);
  };
  useEffect(() => { fetchAll(); }, []);

  const toggleReq = async (reqId, currentMet) => {
    await apiClient.request('/api/vault/nasdaq/checklist', { method: 'POST', body: JSON.stringify({ requirement_id: reqId, met: !currentMet }) });
    fetchAll();
  };
  const addBoardMember = async () => {
    await apiClient.request('/api/vault/nasdaq/board', { method: 'POST', body: JSON.stringify(boardForm) });
    setShowBoardForm(false); setBoardForm({ name: '', role: 'independent', committee: '', bio: '', since: '' }); fetchAll();
  };
  const removeBoardMember = async (id) => { await apiClient.request(`/api/vault/nasdaq/board/${id}`, { method: 'DELETE' }); fetchAll(); };
  const addPhase = async () => {
    await apiClient.request('/api/vault/nasdaq/timeline', { method: 'POST', body: JSON.stringify(phaseForm) });
    setShowPhaseForm(false); setPhaseForm({ phase: 'Pre-IPO Foundation', title: '', target_date: '', status: 'not_started', notes: '' }); fetchAll();
  };
  const cyclePhaseStatus = async (p) => {
    const next = { not_started: 'in_progress', in_progress: 'complete', complete: 'not_started' };
    await apiClient.request(`/api/vault/nasdaq/timeline/${p.phase_id}`, { method: 'PUT', body: JSON.stringify({ ...p, status: next[p.status] || 'not_started' }) });
    fetchAll();
  };
  const deletePhase = async (id) => { await apiClient.request(`/api/vault/nasdaq/timeline/${id}`, { method: 'DELETE' }); fetchAll(); };

  if (!checklist) return <div className="text-center py-12 text-gray-600">Loading...</div>;

  const catColors = { Financial: 'border-blue-300 bg-blue-50', Governance: 'border-amber-300 bg-amber-50', Compliance: 'border-purple-300 bg-purple-50', 'SEC Filings': 'border-red-300 bg-red-50', 'IPO Process': 'border-emerald-300 bg-emerald-50' };
  const phaseStatusStyles = { not_started: 'bg-gray-100 text-gray-500', in_progress: 'bg-blue-100 text-blue-700', complete: 'bg-emerald-100 text-emerald-700' };

  return (
    <div className="space-y-5" data-testid="nasdaq-tab">
      {/* Sprint 173 — Liquidity Horizon panel above the IPO readiness CRUD. */}
      <LiquidityHorizonPanel />

      {/* Readiness Score */}
      <div className="bg-gradient-to-r from-indigo-900 to-purple-900 rounded-2xl p-6 text-white" data-testid="readiness-score">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-wider text-indigo-300 mb-1">NASDAQ IPO Readiness</p>
            <p className="text-6xl font-black">{checklist.readiness_pct}%</p>
            <p className="text-xs text-indigo-300 mt-1">{checklist.met} of {checklist.total} requirements met</p>
          </div>
          <div className="w-28 h-28 relative">
            <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
              <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="3" />
              <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#A78BFA" strokeWidth="3" strokeDasharray={`${checklist.readiness_pct}, 100`} strokeLinecap="round" />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
              <Landmark size={24} className="text-indigo-300" />
            </div>
          </div>
        </div>
      </div>

      {/* Category Progress */}
      <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-5 gap-2">
        {Object.entries(checklist.by_category).map(([cat, v]) => (
          <div key={cat} className={`rounded-xl border-2 p-3 text-center ${catColors[cat] || 'border-gray-200 bg-gray-50'}`}>
            <p className="text-xs font-bold text-gray-900">{v.met}/{v.total}</p>
            <p className="text-xs text-gray-500 uppercase">{cat}</p>
          </div>
        ))}
      </div>

      {/* Checklist */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="nasdaq-checklist">
        <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2"><CheckSquare size={14} /> Requirements Checklist</h3>
        {Object.entries(checklist.by_category).map(([cat]) => (
          <div key={cat} className="mb-4">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{cat}</h4>
            <div className="space-y-1">
              {checklist.checklist.filter(c => c.category === cat).map(c => (
                <button key={c.id} onClick={() => toggleReq(c.id, c.met)} className="w-full flex items-center gap-3 p-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left" data-testid={`nasdaq-${c.id}`}>
                  {c.met ? <CheckSquare size={16} className="text-emerald-500 flex-shrink-0" /> : <Square size={16} className="text-gray-300 flex-shrink-0" />}
                  <span className={`text-xs ${c.met ? 'text-gray-600 line-through' : 'text-gray-700'}`}>{c.requirement}</span>
                  <span className="text-xs text-gray-600 ml-auto flex-shrink-0">{c.tier}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Board Composition */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="board-composition">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2"><UserCheck size={14} /> Board Composition</h3>
          <button onClick={() => setShowBoardForm(true)} className="flex items-center gap-1 px-3 py-1.5 bg-gray-900 text-white text-xs font-semibold rounded-lg" data-testid="add-board-btn"><Plus size={12} /> Add</button>
        </div>
        {board && (
          <>
            <div className={`rounded-lg p-3 mb-3 text-center text-xs font-bold ${board.majority_independent ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`} data-testid="board-status">
              {board.majority_independent ? 'Majority Independent — NASDAQ Compliant' : `Need ${Math.max(0, board.executive_count + 1 - board.independent_count)} more independent directors`}
            </div>
            <div className="space-y-2">
              {board.members.map(m => (
                <div key={m.member_id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-testid={`board-${m.member_id}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${m.role === 'independent' ? 'bg-emerald-500' : m.role === 'executive' ? 'bg-blue-500' : 'bg-gray-400'}`}>{m.name[0]}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-gray-900">{m.name}</p>
                    <p className="text-xs text-gray-500">{m.role}{m.committee ? ` — ${m.committee} committee` : ''}</p>
                  </div>
                  <button onClick={() => removeBoardMember(m.member_id)} className="text-red-400 hover:text-red-600"><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
          </>
        )}
        {showBoardForm && (
          <div className="border-t border-gray-200 pt-4 mt-4" data-testid="board-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-3 gap-3">
              <div><label className="text-xs font-medium text-gray-500 uppercase">Name</label><input type="text" value={boardForm.name} onChange={e => setBoardForm(p => ({ ...p, name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="board-name" /></div>
              <div><label className="text-xs font-medium text-gray-500 uppercase">Role</label>
                <select value={boardForm.role} onChange={e => setBoardForm(p => ({ ...p, role: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="board-role">
                  <option value="independent">Independent</option><option value="executive">Executive</option><option value="chair">Chair</option><option value="advisor">Advisor</option>
                </select></div>
              <div><label className="text-xs font-medium text-gray-500 uppercase">Committee</label>
                <select value={boardForm.committee} onChange={e => setBoardForm(p => ({ ...p, committee: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="board-committee">
                  <option value="">None</option><option value="audit">Audit</option><option value="compensation">Compensation</option><option value="nominating">Nominating</option>
                </select></div>
            </div>
            <div className="flex gap-2 mt-3"><button onClick={() => setShowBoardForm(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm">Cancel</button><button onClick={addBoardMember} disabled={!boardForm.name} className="flex-1 py-2 bg-gray-900 text-white font-semibold rounded-lg text-sm disabled:opacity-50" data-testid="save-board-btn">Save</button></div>
          </div>
        )}
      </div>

      {/* IPO Timeline */}
      <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="ipo-timeline">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Clock size={14} /> IPO Timeline</h3>
          <button onClick={() => setShowPhaseForm(true)} className="flex items-center gap-1 px-3 py-1.5 bg-gray-900 text-white text-xs font-semibold rounded-lg" data-testid="add-phase-btn"><Plus size={12} /> Add Phase</button>
        </div>
        {showPhaseForm && (
          <div className="bg-gray-50 rounded-xl p-4 mb-4" data-testid="phase-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-3 gap-3">
              <div><label className="text-xs font-medium text-gray-500 uppercase">Phase</label>
                <select value={phaseForm.phase} onChange={e => setPhaseForm(p => ({ ...p, phase: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="phase-name">
                  <option value="Pre-IPO Foundation">Pre-IPO Foundation</option><option value="Growth Phase">Growth Phase</option><option value="IPO Preparation">IPO Preparation</option><option value="IPO Execution">IPO Execution</option>
                </select></div>
              <div><label className="text-xs font-medium text-gray-500 uppercase">Title</label><input type="text" value={phaseForm.title} onChange={e => setPhaseForm(p => ({ ...p, title: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="phase-title" /></div>
              <div><label className="text-xs font-medium text-gray-500 uppercase">Target Date</label><input type="date" value={phaseForm.target_date} onChange={e => setPhaseForm(p => ({ ...p, target_date: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="phase-date" /></div>
            </div>
            <div className="flex gap-2 mt-3"><button onClick={() => setShowPhaseForm(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm">Cancel</button><button onClick={addPhase} disabled={!phaseForm.title} className="flex-1 py-2 bg-gray-900 text-white font-semibold rounded-lg text-sm disabled:opacity-50" data-testid="save-phase-btn">Save</button></div>
          </div>
        )}
        {timeline && (
          <div className="space-y-4">
            {['Pre-IPO Foundation', 'Growth Phase', 'IPO Preparation', 'IPO Execution'].map(phase => {
              const items = timeline.phases.filter(p => p.phase === phase);
              if (items.length === 0) return null;
              return (
                <div key={phase}>
                  <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{phase}</h4>
                  <div className="space-y-1.5">
                    {items.map(p => (
                      <div key={p.phase_id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-testid={`phase-${p.phase_id}`}>
                        <button onClick={() => cyclePhaseStatus(p)} className={`px-2 py-1 rounded text-xs font-bold ${phaseStatusStyles[p.status] || phaseStatusStyles.not_started}`} data-testid={`phase-status-${p.phase_id}`}>
                          {p.status === 'complete' ? 'Done' : p.status === 'in_progress' ? 'Active' : 'Todo'}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs font-semibold ${p.status === 'complete' ? 'text-gray-600 line-through' : 'text-gray-900'}`}>{p.title}</p>
                          {p.notes && <p className="text-xs text-gray-600">{p.notes}</p>}
                        </div>
                        <span className="text-xs text-gray-600 flex-shrink-0">{p.target_date}</span>
                        <button onClick={() => deletePhase(p.phase_id)} className="text-red-400 hover:text-red-600"><Trash2 size={12} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Empty-state preview — fires only when the IPO checklist has no
          checked items + the timeline is empty so brand-new vaults see
          what a real NASDAQ readiness card will look like. */}
      {checklist && (checklist.requirements || []).every(r => !r.met) && timeline && (timeline.phases || []).length === 0 && (
        <VaultSamplePreview
          accent="#7C3AED"
          title="NASDAQ readiness · checklist + governance + IPO timeline"
          addLabel="Tick a requirement above or add a phase to begin"
          samples={SAMPLES_NASDAQ}
          dataTestid="nasdaq-sample"
        />
      )}
    </div>
  );
};

// ─── DATA ROOM TAB ───────────────────────────────────────────────────────────

/**
 * DataRoomIntroEditor — inline editor for the 5 fields rendered at the top
 * of every public /dataroom?code=… page (company name, tagline, description,
 * market, website). Saves to vault_settings.dataroom_company_intro via
 * the owner-gated PUT endpoint. Changes go live the moment an investor
 * refreshes the public Data Room page — no redeploy needed.
 */
const DataRoomIntroEditor = () => {
  const [intro, setIntro] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    apiClient.request('/api/vault/dataroom-intro')
      .then(d => setIntro(d))
      .catch(() => setIntro({ name: '', tagline: '', description: '', market: '', website: '' }));
  }, []);

  const set = (k, v) => setIntro(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await apiClient.request('/api/vault/dataroom-intro', {
        method: 'PUT',
        body: JSON.stringify({
          name: intro.name, tagline: intro.tagline, description: intro.description,
          market: intro.market, website: intro.website,
        }),
      });
      setIntro({ ...intro, ...updated });
      setSavedAt(new Date().toLocaleTimeString());
    } catch {}
    setSaving(false);
  };

  const handleReset = async () => {
    if (!window.confirm('Reset the public Data Room intro to the codebase defaults? Your current overrides will be deleted.')) return;
    setSaving(true);
    try {
      const reverted = await apiClient.request('/api/vault/dataroom-intro/reset', { method: 'POST' });
      setIntro(reverted);
      setSavedAt(new Date().toLocaleTimeString());
    } catch {}
    setSaving(false);
  };

  if (!intro) return null;

  return (
    <div className="bg-white rounded-2xl border-2 border-amber-300 shadow-sm" data-testid="dataroom-intro-editor">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-5 py-3 text-left"
        data-testid="dataroom-intro-toggle"
      >
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-amber-700">Public Data Room — Company Intro</p>
          <p className="text-sm text-gray-700 mt-0.5">
            <span className="font-semibold">{intro.name || '—'}</span>
            <span className="text-gray-500"> · {intro.tagline || '—'}</span>
          </p>
        </div>
        <span className="text-xs font-semibold text-amber-700">{expanded ? 'Hide' : 'Edit'} ▾</span>
      </button>
      {expanded && (
        <div className="px-5 pb-5 space-y-3 border-t border-amber-100 pt-4">
          <p className="text-xs text-gray-600">
            These five fields render at the top of every passcode-protected <code className="bg-gray-100 px-1 py-0.5 rounded text-[11px]">/dataroom</code> the prospect or investor enters. Changes go live immediately — no redeploy required.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Company Name</label>
              <input type="text" value={intro.name || ''} onChange={e => set('name', e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                data-testid="intro-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Tagline</label>
              <input type="text" value={intro.tagline || ''} onChange={e => set('tagline', e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                data-testid="intro-tagline" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Market</label>
              <input type="text" value={intro.market || ''} onChange={e => set('market', e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                data-testid="intro-market" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">Website</label>
              <input type="text" value={intro.website || ''} onChange={e => set('website', e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                data-testid="intro-website" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Description (1–3 sentences)</label>
            <textarea value={intro.description || ''} onChange={e => set('description', e.target.value)} rows={4}
              className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm resize-y"
              data-testid="intro-description" />
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg disabled:opacity-50"
              data-testid="intro-save-btn">
              {saving ? 'Saving…' : 'Save Intro'}
            </button>
            <button onClick={handleReset} disabled={saving}
              className="px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50"
              data-testid="intro-reset-btn">
              Reset to defaults
            </button>
            {savedAt && <span className="text-xs text-emerald-700 font-medium">Saved at {savedAt}</span>}
          </div>
        </div>
      )}
    </div>
  );
};

const DataRoomTab = () => {
  const [rooms, setRooms] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', room_type: 'prospect', recipient_name: '', recipient_email: '', expires_hours: 72, notes: '', send_email: true });
  const [copied, setCopied] = useState(null);
  const [creating, setCreating] = useState(false);
  const [activity, setActivity] = useState({ feed: [], stats: {} });
  const [showFeed, setShowFeed] = useState(true);
  const [scores, setScores] = useState([]);
  const [settings, setSettings] = useState({ digest_enabled: false, digest_frequency: 'weekly' });
  const [digestSending, setDigestSending] = useState(false);

  const fetchRooms = async () => {
    const [d, a, e, s] = await Promise.all([
      apiClient.request('/api/vault/datarooms'),
      apiClient.request('/api/vault/datarooms/activity').catch(() => ({ feed: [], stats: {} })),
      apiClient.request('/api/vault/datarooms/engagement').catch(() => ({ scores: [] })),
      apiClient.request('/api/vault/settings').catch(() => ({})),
    ]);
    setRooms(d.datarooms || []);
    setActivity(a);
    setScores(e.scores || []);
    setSettings(prev => ({ ...prev, ...s }));
  };
  useEffect(() => { fetchRooms(); }, []);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await apiClient.request('/api/vault/datarooms', { method: 'POST', body: JSON.stringify(form) });
      setShowForm(false);
      setForm({ name: '', room_type: 'prospect', recipient_name: '', recipient_email: '', expires_hours: 72, notes: '', send_email: true });
      fetchRooms();
    } catch {}
    setCreating(false);
  };
  const handleRevoke = async (id) => { await apiClient.request(`/api/vault/datarooms/${id}`, { method: 'DELETE' }); fetchRooms(); };
  const handleExtend = async (id) => { await apiClient.request(`/api/vault/datarooms/${id}/extend`, { method: 'PUT' }); fetchRooms(); };
  const [replayed, setReplayed] = useState(null);
  const handleReplayWelcome = async (id) => {
    try {
      await apiClient.request(`/api/vault/datarooms/${id}/replay-welcome`, { method: 'PUT' });
      setReplayed(id);
      setTimeout(() => setReplayed(null), 3000);
    } catch { /* noop */ }
  };
  const copyLink = (passcode) => {
    // Passcode is never embedded in the URL — copy the clean link; the code is shared separately.
    navigator.clipboard.writeText(`${window.location.origin}/dataroom`);
    setCopied(passcode);
    setTimeout(() => setCopied(null), 2000);
  };
  const [engagement, setEngagement] = useState(null);
  const [engagementLoading, setEngagementLoading] = useState(null);
  const openEngagement = async (roomId) => {
    setEngagementLoading(roomId);
    try {
      const d = await apiClient.request(`/api/vault/datarooms/${roomId}/engagement`);
      setEngagement(d);
    } catch {}
    setEngagementLoading(null);
  };
  const fmtDwell = (secs) => {
    if (!secs) return '0m';
    const h = Math.floor(secs / 3600);
    const m = Math.round((secs % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const active = rooms.filter(r => !r.is_expired && r.active !== false);
  const expired = rooms.filter(r => r.is_expired || r.active === false);
  const prospectRooms = active.filter(r => r.room_type !== 'investor');
  const investorRooms = active.filter(r => r.room_type === 'investor');

  return (
    <div className="space-y-5" data-testid="dataroom-tab">
      {/* Qualification Inbox — grade leads + one-click room invites */}
      <QualificationInbox />

      {/* Sprint 172 — Diligence Library tree above the Data Room CRUD. */}
      <DiligenceLibrary />

      {/* Sprint 189 — Editable public Data Room intro (company name,
          tagline, description, market, website). Persists to
          vault_settings.dataroom_company_intro and overrides the
          codebase defaults on every shareable-link read. */}
      <DataRoomIntroEditor />

      <div className="bg-gradient-to-r from-blue-900 to-indigo-900 rounded-2xl p-6 text-white" data-testid="dataroom-hero">
        <h3 className="text-lg font-black mb-1">Investor Data Rooms</h3>
        <p className="text-xs text-blue-300 max-w-lg">Create passcode-protected data rooms with two access tiers. <strong>Prospect</strong> rooms are time-limited and view-only (no printing/downloading). <strong>Investor</strong> rooms provide permanent access with live metric updates.</p>
        <div className="flex gap-4 mt-4">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
            <span className="text-xs font-semibold text-amber-300">Prospect — Time-limited, view only</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
            <span className="text-xs font-semibold text-emerald-300">Investor — Permanent, live metrics</span>
          </div>
        </div>
      </div>

      {/* Engagement Stats */}
      {activity.stats && (activity.stats.total_views > 0 || activity.feed?.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-4 gap-3" data-testid="engagement-stats">
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm text-center">
            <p className="text-2xl font-black text-gray-900">{activity.stats.today_views || 0}</p>
            <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Views Today</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm text-center">
            <p className="text-2xl font-black text-gray-900">{activity.stats.week_views || 0}</p>
            <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">This Week</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm text-center">
            <p className="text-2xl font-black text-[#7B4FD8]">{activity.stats.total_views || 0}</p>
            <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Total Views</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm text-center">
            <p className="text-2xl font-black text-emerald-600">{activity.stats.active_rooms || 0}</p>
            <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Active Rooms</p>
          </div>
        </div>
      )}

      {/* Activity Feed */}
      {activity.feed?.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-testid="activity-feed">
          <button onClick={() => setShowFeed(p => !p)}
            className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
            <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <Activity size={14} className="text-[#7B4FD8]" /> Engagement Activity
              <span className="text-xs font-normal text-gray-600 ml-1">({activity.feed.length} events)</span>
            </h4>
            {showFeed ? <ChevronUp size={16} className="text-gray-600" /> : <ChevronDown size={16} className="text-gray-600" />}
          </button>
          {showFeed && (
            <div className="border-t border-gray-100 max-h-64 overflow-y-auto" data-testid="activity-feed-list">
              {activity.feed.slice(0, 50).map((ev, i) => {
                const isAccess = ev.type === 'accessed';
                const timeAgo = (() => {
                  try {
                    const d = new Date(ev.timestamp);
                    const diff = Date.now() - d.getTime();
                    const mins = Math.floor(diff / 60000);
                    if (mins < 1) return 'just now';
                    if (mins < 60) return `${mins}m ago`;
                    const hrs = Math.floor(mins / 60);
                    if (hrs < 24) return `${hrs}h ago`;
                    const days = Math.floor(hrs / 24);
                    if (days < 7) return `${days}d ago`;
                    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
                  } catch { return ''; }
                })();
                return (
                  <div key={i} className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isAccess ? 'bg-blue-50 text-blue-500' : 'bg-emerald-50 text-emerald-500'
                    }`}>
                      {isAccess ? <ArrowUpRight size={12} /> : <Plus size={12} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${
                          ev.room_type === 'investor' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                        }`}>{ev.room_type === 'investor' ? 'INV' : 'PRO'}</span>
                        <span className="text-xs font-semibold text-gray-900 truncate">{ev.room_name}</span>
                      </div>
                      <p className="text-xs text-gray-600">
                        {isAccess ? 'Viewed by' : 'Room created for'} {ev.recipient || 'Unknown'}
                      </p>
                    </div>
                    <span className="text-xs text-gray-600 flex-shrink-0 w-14 text-right">{timeAgo}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Digest & Engagement Controls */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Email Digest Panel */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm" data-testid="digest-panel">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Bell size={14} className="text-amber-500" /> Email Digest</h4>
            <button onClick={async () => {
              const newVal = !settings.digest_enabled;
              await apiClient.request('/api/vault/settings', { method: 'POST', body: JSON.stringify({ digest_enabled: newVal }) });
              setSettings(p => ({ ...p, digest_enabled: newVal }));
            }} className="flex items-center gap-1.5" data-testid="digest-toggle">
              {settings.digest_enabled ? <CheckSquare size={16} className="text-[#7B4FD8]" /> : <Square size={16} className="text-gray-300" />}
              <span className="text-xs font-semibold text-gray-600">{settings.digest_enabled ? 'Active' : 'Off'}</span>
            </button>
          </div>
          <div className="flex gap-2 mb-3">
            {['daily', 'weekly'].map(freq => (
              <button key={freq} onClick={async () => {
                await apiClient.request('/api/vault/settings', { method: 'POST', body: JSON.stringify({ digest_frequency: freq }) });
                setSettings(p => ({ ...p, digest_frequency: freq }));
              }}
                className={`flex-1 py-1.5 text-xs font-semibold rounded-lg ${settings.digest_frequency === freq ? 'bg-[#7B4FD8] text-white' : 'bg-gray-100 text-gray-500'}`}
                data-testid={`digest-${freq}`}>{freq.charAt(0).toUpperCase() + freq.slice(1)}</button>
            ))}
          </div>
          <button onClick={async () => {
            setDigestSending(true);
            await apiClient.request('/api/vault/datarooms/digest/send', { method: 'POST' }).catch(() => {});
            setDigestSending(false);
          }} disabled={digestSending}
            className="w-full py-2 bg-gray-900 text-white text-xs font-semibold rounded-lg disabled:opacity-50" data-testid="send-digest-btn">
            {digestSending ? 'Sending...' : 'Send Digest Now'}
          </button>
        </div>

        {/* Engagement Scores */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4 shadow-sm" data-testid="engagement-scores">
          <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2 mb-3"><Zap size={14} className="text-amber-500" /> Engagement Scores</h4>
          {scores.length > 0 ? (
            <div className="space-y-2">
              {scores.slice(0, 5).map(s => (
                <div key={s.room_id} className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs font-black ${
                    s.score >= 70 ? 'bg-emerald-100 text-emerald-700' : s.score >= 40 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'
                  }`}>{s.score}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className={`px-1 py-0.5 rounded text-xs font-bold ${s.room_type === 'investor' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
                        {s.room_type === 'investor' ? 'INV' : 'PRO'}
                      </span>
                      <span className="text-xs font-semibold text-gray-900 truncate">{s.name}</span>
                    </div>
                    <div className="flex gap-3 text-xs text-gray-600">
                      <span>{s.total_views} total</span>
                      <span>{s.recent_views} this week</span>
                      {s.last_accessed && <span>Last: {new Date(s.last_accessed).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>}
                    </div>
                  </div>
                  <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${s.score >= 70 ? 'bg-emerald-400' : s.score >= 40 ? 'bg-amber-400' : 'bg-gray-300'}`}
                      style={{ width: `${s.score}%` }} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-600 text-center py-4">No engagement data yet</p>
          )}
        </div>
      </div>

      {/* Engagement insight modal — wide dashboard layout */}
      {engagement && (
        <div className="fixed inset-0 z-[10001] bg-black/60 flex items-center justify-center p-4" onClick={() => setEngagement(null)} data-testid="engagement-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[88vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            {/* Header strip */}
            <div className="sticky top-0 z-10 px-6 py-4 rounded-t-2xl text-white flex items-center justify-between" style={{ background: 'linear-gradient(120deg, #1A1209 0%, #2D1B69 70%, #4C2A9E 100%)' }}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="text-base font-black truncate">{engagement.room_name}</h4>
                  <span className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase flex-shrink-0 ${engagement.room_type === 'investor' ? 'bg-emerald-400/20 text-emerald-300' : 'bg-amber-400/20 text-amber-300'}`}>
                    {engagement.room_type === 'investor' ? 'Investor' : 'Prospect'}
                  </span>
                </div>
                <p className="text-[11px] text-white/70 mt-0.5">{engagement.recipient || '—'} · Engagement insight</p>
              </div>
              <button onClick={() => setEngagement(null)} className="text-white/60 hover:text-white flex-shrink-0 ml-4" data-testid="engagement-close"><X size={20} /></button>
            </div>

            <div className="p-6 space-y-6">
              {/* Headline stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="rounded-xl p-4 bg-[#F4F1FB] border border-[#7B4FD8]/20" data-testid="engagement-stat-visits">
                  <p className="text-[9px] font-bold text-[#7B4FD8] uppercase tracking-wider flex items-center gap-1"><Zap size={11} /> Visits</p>
                  <p className="text-2xl font-black text-gray-900 mt-1">{engagement.access_count || 0}</p>
                </div>
                <div className="rounded-xl p-4 bg-emerald-50 border border-emerald-200/60" data-testid="engagement-stat-dwell">
                  <p className="text-[9px] font-bold text-emerald-700 uppercase tracking-wider flex items-center gap-1"><Clock size={11} /> Time in Room</p>
                  <p className="text-2xl font-black text-gray-900 mt-1">{fmtDwell(engagement.total_dwell_seconds)}</p>
                  {(engagement.access_count > 0 && engagement.total_dwell_seconds > 0) && (
                    <p className="text-[10px] text-emerald-700/80 mt-0.5">≈ {fmtDwell(Math.round(engagement.total_dwell_seconds / engagement.access_count))} per visit</p>
                  )}
                </div>
                <div className="rounded-xl p-4 bg-amber-50 border border-amber-200/60" data-testid="engagement-stat-device">
                  <p className="text-[9px] font-bold text-amber-700 uppercase tracking-wider flex items-center gap-1">{engagement.last_device === 'Mobile' ? <Smartphone size={11} /> : <Globe size={11} />} Device</p>
                  <p className="text-xl font-black text-gray-900 mt-1.5">{engagement.last_device || '—'}</p>
                </div>
                <div className="rounded-xl p-4 bg-rose-50 border border-rose-200/60" data-testid="engagement-stat-location">
                  <p className="text-[9px] font-bold text-rose-600 uppercase tracking-wider flex items-center gap-1"><MapPin size={11} /> Location</p>
                  <p className="text-sm font-black text-gray-900 mt-2 leading-tight">{engagement.last_location || 'Unknown'}</p>
                </div>
              </div>

              <div className="grid lg:grid-cols-5 gap-6">
                {/* Documents opened — ranked with interest bars */}
                <div className="lg:col-span-3">
                  <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5"><FileText size={12} /> Documents Opened ({engagement.documents_opened?.length || 0}) — ranked by interest</p>
                  {engagement.documents_opened?.length ? (
                    <div className="space-y-2">
                      {[...engagement.documents_opened].sort((a, b) => (b.opens || 0) - (a.opens || 0)).map((d, i, arr) => {
                        const maxOpens = Math.max(...arr.map(x => x.opens || 0), 1);
                        return (
                          <div key={i} className="rounded-lg border border-gray-100 px-3 py-2 hover:border-[#7B4FD8]/30 transition-colors" data-testid={`engagement-doc-${d.file_id}`}>
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-2.5 min-w-0">
                                <span className={`w-5 h-5 rounded-md flex items-center justify-center text-[10px] font-black flex-shrink-0 ${i === 0 ? 'bg-[#7B4FD8] text-white' : 'bg-gray-100 text-gray-500'}`}>{i + 1}</span>
                                <span className="text-xs font-semibold text-gray-800 truncate">{d.label}</span>
                              </div>
                              <span className="text-[10px] font-black text-[#7B4FD8] flex-shrink-0">{d.opens}×</span>
                            </div>
                            <div className="mt-1.5 ml-[30px] h-1 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full rounded-full bg-gradient-to-r from-[#7B4FD8] to-[#A78BFA]" style={{ width: `${((d.opens || 0) / maxOpens) * 100}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 italic">No documents opened yet.</p>
                  )}
                </div>

                {/* Visit history — timeline */}
                <div className="lg:col-span-2">
                  <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5"><Clock size={12} /> Visit History ({engagement.visits?.length || 0})</p>
                  {engagement.visits?.length ? (
                    <div className="relative max-h-[340px] overflow-y-auto pr-1">
                      <div className="absolute left-[5px] top-1 bottom-1 w-px bg-gray-200" />
                      <div className="space-y-3">
                        {engagement.visits.map((v, i) => (
                          <div key={i} className="relative pl-5">
                            <span className={`absolute left-0 top-1 w-[11px] h-[11px] rounded-full border-2 border-white ring-1 ${i === 0 ? 'bg-[#7B4FD8] ring-[#7B4FD8]/40' : 'bg-gray-300 ring-gray-200'}`} />
                            <p className="text-xs font-bold text-gray-800">{new Date(v.timestamp).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</p>
                            <p className="text-[10px] text-gray-500 mt-0.5 flex items-center gap-1">
                              {v.device === 'Mobile' ? <Smartphone size={10} /> : v.device ? <Globe size={10} /> : null}
                              {[v.device, v.location].filter(x => x && x !== 'Unknown').join(' · ') || '—'}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400 italic">Not visited yet.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <button onClick={() => setShowForm(true)} className="flex items-center gap-1.5 px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-xl" data-testid="create-room-btn"><Plus size={14} /> Create Data Room</button>

      {showForm && (
        <div className="bg-white rounded-2xl border-2 border-gray-300 p-5" data-testid="room-form">
          <h4 className="text-sm font-bold text-gray-900 mb-3">New Data Room</h4>

          {/* Room Type Selector */}
          <div className="mb-4" data-testid="room-type-selector">
            <label className="text-xs font-medium text-gray-500 uppercase mb-2 block">Room Type</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button type="button" onClick={() => setForm(p => ({ ...p, room_type: 'prospect' }))}
                className={`p-4 rounded-xl border-2 text-left transition-all ${form.room_type === 'prospect' ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                data-testid="type-prospect-btn">
                <div className="flex items-center gap-2 mb-1">
                  <Clock size={14} className={form.room_type === 'prospect' ? 'text-amber-600' : 'text-gray-600'} />
                  <span className={`text-sm font-bold ${form.room_type === 'prospect' ? 'text-amber-800' : 'text-gray-700'}`}>Prospect</span>
                </div>
                <p className="text-xs text-gray-500">Time-limited access. No downloads, no printing. Perfect for potential investors during due diligence.</p>
              </button>
              <button type="button" onClick={() => setForm(p => ({ ...p, room_type: 'investor' }))}
                className={`p-4 rounded-xl border-2 text-left transition-all ${form.room_type === 'investor' ? 'border-emerald-400 bg-emerald-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                data-testid="type-investor-btn">
                <div className="flex items-center gap-2 mb-1">
                  <UserCheck size={14} className={form.room_type === 'investor' ? 'text-emerald-600' : 'text-gray-600'} />
                  <span className={`text-sm font-bold ${form.room_type === 'investor' ? 'text-emerald-800' : 'text-gray-700'}`}>Investor</span>
                </div>
                <p className="text-xs text-gray-500">Permanent access with live KPI updates. For confirmed investors who need ongoing visibility.</p>
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            <div><label className="text-xs font-medium text-gray-500 uppercase">Room Name</label><input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder={form.room_type === 'investor' ? 'e.g. John Smith — Personal Room' : 'e.g. Series A Due Diligence'} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="room-name" /></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">Recipient Name</label><input type="text" value={form.recipient_name} onChange={e => setForm(p => ({ ...p, recipient_name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="room-recipient" /></div>
            <div><label className="text-xs font-medium text-gray-500 uppercase">Recipient Email</label><input type="email" value={form.recipient_email} onChange={e => setForm(p => ({ ...p, recipient_email: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="room-email" /></div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
            {form.room_type === 'prospect' && (
              <div><label className="text-xs font-medium text-gray-500 uppercase">Expires In</label>
                <select value={form.expires_hours} onChange={e => setForm(p => ({ ...p, expires_hours: parseInt(e.target.value) }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="room-expiry">
                  <option value={24}>24 hours</option><option value={48}>48 hours</option><option value={72}>72 hours (3 days)</option><option value={168}>1 week</option><option value={336}>2 weeks</option><option value={720}>30 days</option>
                </select></div>
            )}
            {form.room_type === 'investor' && (
              <div className="flex items-end">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 w-full">
                  <p className="text-xs text-emerald-700 font-semibold">Permanent Access — No expiry</p>
                </div>
              </div>
            )}
            <div><label className="text-xs font-medium text-gray-500 uppercase">Notes</label><input type="text" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mt-0.5" data-testid="room-notes" /></div>
          </div>

          {/* Email toggle */}
          <div className="flex items-center gap-3 mt-4 p-3 bg-gray-50 rounded-xl" data-testid="email-toggle-wrap">
            <button type="button" onClick={() => setForm(p => ({ ...p, send_email: !p.send_email }))}
              className="flex items-center gap-2" data-testid="email-toggle">
              {form.send_email ? <CheckSquare size={18} className="text-[#7B4FD8]" /> : <Square size={18} className="text-gray-300" />}
              <span className="text-xs font-semibold text-gray-700">Send passcode via email</span>
            </button>
            {form.send_email && !form.recipient_email && (
              <span className="text-xs text-amber-600 font-medium">Enter recipient email above</span>
            )}
          </div>

          <div className="flex gap-2 mt-3">
            <button onClick={() => setShowForm(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm">Cancel</button>
            <button onClick={handleCreate} disabled={!form.name || creating}
              className={`flex-1 py-2 text-white font-semibold rounded-lg text-sm disabled:opacity-50 ${form.room_type === 'investor' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-gray-900 hover:bg-gray-800'}`}
              data-testid="save-room-btn">
              {creating ? 'Creating...' : `Create ${form.room_type === 'investor' ? 'Investor' : 'Prospect'} Room`}
            </button>
          </div>
        </div>
      )}

      {/* Investor Rooms */}
      {investorRooms.length > 0 && (
        <div>
          <h4 className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-3 flex items-center gap-1.5">
            <UserCheck size={12} /> Investor Rooms — Permanent ({investorRooms.length})
          </h4>
          <div className="space-y-2">{investorRooms.map(r => (
            <div key={r.room_id} className="bg-white rounded-xl border-l-4 border-l-emerald-400 border border-gray-200 p-4 shadow-sm" data-testid={`room-${r.room_id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                  <h5 className="text-sm font-bold text-gray-900">{r.name}</h5>
                  <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-bold rounded">INVESTOR</span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleReplayWelcome(r.room_id)} className="px-2 py-1 bg-amber-50 text-amber-700 text-xs font-semibold rounded hover:bg-amber-100" title="Re-arm the green welcome ceremony for this room's next entry" data-testid={`replay-welcome-${r.room_id}`}>{replayed === r.room_id ? 'Armed ✓' : 'Replay Welcome'}</button>
                  <button onClick={() => openEngagement(r.room_id)} className="px-2 py-1 bg-purple-50 text-[#7B4FD8] text-xs font-semibold rounded hover:bg-purple-100" data-testid={`engagement-${r.room_id}`}>{engagementLoading === r.room_id ? '…' : 'Engagement'}</button>
                  <button onClick={() => handleRevoke(r.room_id)} className="px-2 py-1 bg-red-50 text-red-600 text-xs font-semibold rounded hover:bg-red-100" data-testid={`revoke-${r.room_id}`}>Revoke</button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-gray-500">
                {r.recipient_name && <div><span className="text-gray-600">For: </span><span className="font-semibold text-gray-700">{r.recipient_name}</span></div>}
                <div><span className="text-gray-600">Passcode: </span><span className="font-mono font-bold text-gray-900">{r.passcode}</span></div>
                <div><span className="text-gray-600">Access: </span><span className="font-semibold text-emerald-600">Permanent</span></div>
                <div><span className="text-gray-600">Views: </span><span className="font-semibold text-gray-700">{r.access_count || 0}</span></div>
              </div>
              <button onClick={() => copyLink(r.passcode)} className="mt-2 w-full py-2 bg-emerald-50 text-emerald-700 text-xs font-semibold rounded-lg hover:bg-emerald-100 transition-colors" data-testid={`copy-${r.room_id}`}>
                {copied === r.passcode ? 'Copied!' : 'Copy Shareable Link'}
              </button>
            </div>
          ))}</div>
        </div>
      )}

      {/* Prospect Rooms */}
      {prospectRooms.length > 0 && (
        <div>
          <h4 className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-3 flex items-center gap-1.5">
            <Clock size={12} /> Prospect Rooms — Time-Limited ({prospectRooms.length})
          </h4>
          <div className="space-y-2">{prospectRooms.map(r => (
            <div key={r.room_id} className="bg-white rounded-xl border-l-4 border-l-amber-400 border border-gray-200 p-4 shadow-sm" data-testid={`room-${r.room_id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
                  <h5 className="text-sm font-bold text-gray-900">{r.name}</h5>
                  <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded">PROSPECT</span>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => openEngagement(r.room_id)} className="px-2 py-1 bg-purple-50 text-[#7B4FD8] text-xs font-semibold rounded hover:bg-purple-100" data-testid={`engagement-${r.room_id}`}>{engagementLoading === r.room_id ? '…' : 'Engagement'}</button>
                  <button onClick={() => handleExtend(r.room_id)} className="px-2 py-1 bg-blue-50 text-blue-600 text-xs font-semibold rounded hover:bg-blue-100" data-testid={`extend-${r.room_id}`}>Extend</button>
                  <button onClick={() => handleRevoke(r.room_id)} className="px-2 py-1 bg-red-50 text-red-600 text-xs font-semibold rounded hover:bg-red-100" data-testid={`revoke-${r.room_id}`}>Revoke</button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-gray-500">
                {r.recipient_name && <div><span className="text-gray-600">For: </span><span className="font-semibold text-gray-700">{r.recipient_name}</span></div>}
                <div><span className="text-gray-600">Passcode: </span><span className="font-mono font-bold text-gray-900">{r.passcode}</span></div>
                <div><span className="text-gray-600">Expires: </span><span className="font-semibold text-gray-700">{r.expires_at ? (() => {
                  const diff = new Date(r.expires_at) - Date.now();
                  if (diff <= 0) return 'Expired';
                  const h = Math.floor(diff / 3600000);
                  const d = Math.floor(h / 24);
                  return d > 0 ? `${d}d ${h % 24}h left` : `${h}h ${Math.floor((diff % 3600000) / 60000)}m left`;
                })() : '—'}</span></div>
                <div><span className="text-gray-600">Views: </span><span className="font-semibold text-gray-700">{r.access_count || 0}</span></div>
              </div>
              <button onClick={() => copyLink(r.passcode)} className="mt-2 w-full py-2 bg-gray-100 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-200 transition-colors" data-testid={`copy-${r.room_id}`}>
                {copied === r.passcode ? 'Copied!' : 'Copy Shareable Link'}
              </button>
            </div>
          ))}</div>
        </div>
      )}

      {/* Expired / Revoked */}
      {expired.length > 0 && (
        <div>
          <h4 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-3" data-testid="dataroom-expired-heading">Expired / Revoked ({expired.length})</h4>
          <div className="space-y-1">{expired.map(r => (
            <div key={r.room_id} className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 bg-gray-400 rounded-full" />
                <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${r.room_type === 'investor' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{r.room_type === 'investor' ? 'INV' : 'PRO'}</span>
                <span className="text-sm font-medium text-gray-800">{r.name}</span>
                {r.recipient_name && <span className="text-sm text-gray-700">({r.recipient_name})</span>}
              </div>
              <span className="text-sm text-gray-700 font-medium">{r.access_count || 0} views</span>
            </div>
          ))}</div>
        </div>
      )}

      {rooms.length < 3 && <VaultSamplePreview accent="#DC2626" title="Investor-shared documents & access tracking" addLabel="Click 'Create Data Room' above" samples={SAMPLES_DATA_ROOM} dataTestid="dataroom-sample" />}
    </div>
  );
};

// ─── SHARED COMPONENTS ───────────────────────────────────────────────────────

const VaultCard = ({ icon: Icon, color, label, value, sub }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
    <Icon size={16} style={{ color }} className="mb-1" />
    <p className="text-xl font-black text-gray-900">{value}</p>
    <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
    {sub && <p className="text-xs text-gray-600 mt-0.5">{sub}</p>}
  </div>
);

// ─── CAP TABLE TAB ───────────────────────────────────────────────────────────

const CapTableTab = () => {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState('');
  const [showModelInfo, setShowModelInfo] = useState(false);

  const load = useCallback(async () => {
    try {
      const d = await apiClient.request('/api/vault/cap-table/config');
      setCfg(d);
      setDraft(JSON.parse(JSON.stringify(d)));
    } catch (e) { setErr(e.message || 'Failed to load cap table'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!draft) return <div className="p-6 text-sm text-gray-500" data-testid="captable-loading">Loading cap table…</div>;

  const totalPct = (draft.holders || []).reduce((s, h) => s + (Number(h.pct) || 0), 0);

  const updHolder = (i, patch) => setDraft(p => ({ ...p, holders: p.holders.map((h, idx) => idx === i ? { ...h, ...patch } : h) }));
  const addHolder = () => setDraft(p => ({ ...p, holders: [...(p.holders || []), { name: '', role: '', pct: 0, notes: '' }] }));
  const delHolder = (i) => setDraft(p => ({ ...p, holders: p.holders.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true); setErr('');
    try {
      const body = {
        holders: draft.holders.map(h => ({ ...h, pct: Number(h.pct) || 0 })),
        total_shares: Number(draft.total_shares) || 0,
        valuation_usd: Number(draft.valuation_usd) || 0,
        valuation_basis: draft.valuation_basis || '',
        monthly_per_user_usd: Number(draft.monthly_per_user_usd) || 0,
        user_anchors: (typeof draft.user_anchors === 'string' ? draft.user_anchors.split(/[\s,]+/).map(n => Number(n)).filter(Boolean) : (draft.user_anchors || [])),
        revenue_multiple: Number(draft.revenue_multiple) || 0,
        source_note: draft.source_note || '',
      };
      // Live-Users override is sent as one of three states:
      //   number  → set override
      //   ''      → no change in this PUT
      //   '__CLEAR__' → unset override (use auto count)
      if (draft.live_active_users_override === '__CLEAR__') {
        body.clear_live_users_override = true;
      } else if (draft.live_active_users_override !== '' && draft.live_active_users_override !== null && draft.live_active_users_override !== undefined) {
        body.live_active_users_override = Number(draft.live_active_users_override) || 0;
      }
      const updated = await apiClient.request('/api/vault/cap-table/config', { method: 'PUT', body: JSON.stringify(body) });
      setCfg(updated);
      setDraft(JSON.parse(JSON.stringify(updated)));
      setSavedAt(new Date().toLocaleTimeString());
      // Cross-tab broadcast: ping any other open HiBFF tab (Active Investor
      // Vault, Pitch Deck iframe, Data Room) to refetch canonical values.
      try { localStorage.setItem('hibff_engine_v', String(Date.now())); } catch (_e) {}
    } catch (e) { setErr(e.message || 'Save failed'); }
    setSaving(false);
  };

  // Live engine output (read-only display)
  const computed = cfg?.computed || {};
  const fmtUsdShort = (n) => {
    if (n === null || n === undefined) return '—';
    if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
    return `$${Number(n).toLocaleString()}`;
  };
  const fmtMoneyFull = (n) => (n === null || n === undefined) ? '—' : `$${Number(n).toLocaleString()}`;

  // Anchor Reference Matrix — derived live from current inputs so the table
  // updates in real-time when the founder changes $/user, multiple, anchors,
  // or the asset floor. The first row whose raw revenue valuation exceeds
  // the floor is tagged "Unclamps from Floor" per the spec.
  const _anchorsArr = Array.isArray(draft.user_anchors)
    ? draft.user_anchors.map(Number).filter(Boolean)
    : String(draft.user_anchors || '').split(/[\s,]+/).map(n => Number(n)).filter(Boolean);
  const _perUser = Number(draft.monthly_per_user_usd) || 0;
  const _multiple = Number(draft.revenue_multiple) || 0;
  const _floor = Number(draft.valuation_usd) || 0;
  let _firstUnclamp = false;
  const anchorMatrix = _anchorsArr.map(u => {
    const mrr = u * _perUser;
    const arr = mrr * 12;
    const raw_val = arr * _multiple;
    const is_unclamp = raw_val > _floor;
    const first_unclamp = is_unclamp && !_firstUnclamp;
    if (first_unclamp) _firstUnclamp = true;
    return { users: u, mrr, arr, raw_val, is_unclamp, first_unclamp };
  });

  return (
    <div className="space-y-5" data-testid="captable-tab">
      <div className="bg-gradient-to-r from-sky-900 to-blue-900 rounded-2xl p-6 text-white">
        <h3 className="text-lg font-black mb-1">Canonical Cap Table</h3>
        <p className="text-xs text-sky-200 max-w-2xl">Single source of truth. The public <code>/investors</code> page and the Active Investor Vault both read from this document.</p>
        {cfg?.source_note && <p className="text-xs text-sky-300 mt-2">{cfg.source_note}</p>}
      </div>

      {/* Two-Step Capital Path — live-priced from canonical cap-table inputs. */}
      {(cfg?.rounds_plan_live || []).length > 0 && (
        <RoundsPlanCard plan={cfg.rounds_plan_live} variant="admin" />
      )}

      {/* Investment Instruments — SAFE / ASA / CLN register + growth simulator. */}
      <InstrumentsPanel />

      {err && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700" data-testid="captable-error">{err}</div>}

      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-gray-900">Ownership</h4>
          <span className={`text-xs font-semibold px-2 py-1 rounded-lg ${Math.abs(totalPct - 100) < 0.5 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-testid="captable-pct-total">
            Total: {totalPct.toFixed(2)}%
          </span>
        </div>
        <div className="space-y-2">
          {(draft.holders || []).map((h, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`captable-holder-${i}`}>
              <input className="col-span-4 px-2 py-1.5 text-sm border border-gray-200 rounded-lg" placeholder="Name" value={h.name} onChange={e => updHolder(i, { name: e.target.value })} data-testid={`captable-holder-name-${i}`} />
              <input className="col-span-3 px-2 py-1.5 text-sm border border-gray-200 rounded-lg" placeholder="Role" value={h.role || ''} onChange={e => updHolder(i, { role: e.target.value })} data-testid={`captable-holder-role-${i}`} />
              <input type="number" step="0.01" className="col-span-2 px-2 py-1.5 text-sm border border-gray-200 rounded-lg" placeholder="%" value={h.pct} onChange={e => updHolder(i, { pct: e.target.value })} data-testid={`captable-holder-pct-${i}`} />
              <input className="col-span-2 px-2 py-1.5 text-sm border border-gray-200 rounded-lg" placeholder="Notes" value={h.notes || ''} onChange={e => updHolder(i, { notes: e.target.value })} />
              <button onClick={() => delHolder(i)} className="col-span-1 text-red-500 hover:bg-red-50 rounded-lg py-1.5" data-testid={`captable-holder-del-${i}`}><Trash2 size={14} className="mx-auto" /></button>
            </div>
          ))}
          <button onClick={addHolder} className="text-xs font-semibold text-sky-700 hover:text-sky-900 inline-flex items-center gap-1" data-testid="captable-add-holder"><Plus size={12} /> Add holder</button>
        </div>
        {(draft.holders || []).filter(h => h.name && h.name.trim()).length < 2 && (
          <div className="mt-4">
            <VaultSamplePreview
              accent="#10B981"
              title="Equity ownership · founder, investors, ESOP, advisors"
              addLabel="Click '+ Add holder' above to add your first row"
              samples={SAMPLES_CAP_TABLE}
              dataTestid="captable-sample"
            />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <h4 className="text-sm font-bold text-gray-900 mb-3">Valuation</h4>
          <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Valuation (USD)</label>
          <input type="number" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg mb-3" value={draft.valuation_usd} onChange={e => setDraft(p => ({ ...p, valuation_usd: e.target.value }))} data-testid="captable-valuation" />
          <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Basis</label>
          <input className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg mb-3" value={draft.valuation_basis || ''} onChange={e => setDraft(p => ({ ...p, valuation_basis: e.target.value }))} data-testid="captable-basis" />
          <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Total Shares</label>
          <input type="number" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" value={draft.total_shares} onChange={e => setDraft(p => ({ ...p, total_shares: e.target.value }))} data-testid="captable-totalshares" />
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <h4 className="text-sm font-bold text-gray-900 mb-3">Revenue at Scale</h4>
          <div className="flex items-center gap-1.5 mb-1">
            <label className="block text-xs uppercase tracking-wider text-gray-500">$ / user / month</label>
            <span className="relative group" data-testid="captable-per-user-tooltip">
              <Info size={13} className="text-gray-400 cursor-help" />
              <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-72 bg-gray-900 text-gray-100 text-[11px] leading-relaxed rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity z-20 shadow-xl">
                <strong className="text-white">Blended revenue per paying family</strong> — a forecast
                assumption from FOUR_PRODUCTS_AT_SCALE.md: the weighted mix of the paid plans
                (Steady £16/mo, Together £28/mo, Brave £188/yr) converted at £1 ≈ $1.29.
                It drives the whole engine: Valuation = max(floor, Users × $/user × 12 × multiple).
                Adjusting it moves the floor-unlock user count and every anchor row below in real time.
                Compare it against the Live ARPU measured from real paying subscribers.
              </span>
            </span>
          </div>
          <input type="number" step="0.01" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg mb-2" value={draft.monthly_per_user_usd} onChange={e => setDraft(p => ({ ...p, monthly_per_user_usd: e.target.value }))} data-testid="captable-per-user" />
          <input
            type="range" min="5" max="60" step="0.25"
            value={Math.min(60, Math.max(5, Number(draft.monthly_per_user_usd) || 0))}
            onChange={e => setDraft(p => ({ ...p, monthly_per_user_usd: e.target.value }))}
            className="w-full mb-1 accent-sky-600 cursor-pointer"
            data-testid="captable-per-user-slider"
          />
          <div className="flex justify-between text-[10px] text-gray-400 mb-3"><span>$5</span><span>$60</span></div>
          <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Revenue multiple (×)</label>
          <input type="number" step="0.1" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg mb-3" value={draft.revenue_multiple} onChange={e => setDraft(p => ({ ...p, revenue_multiple: e.target.value }))} data-testid="captable-multiple" />
          <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1">User anchors (comma-separated)</label>
          <input className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" value={Array.isArray(draft.user_anchors) ? draft.user_anchors.join(', ') : draft.user_anchors} onChange={e => setDraft(p => ({ ...p, user_anchors: e.target.value }))} data-testid="captable-anchors" />
        </div>
      </div>

      {/* ─── Forecast vs Live ARPU comparison ───────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="arpu-comparison">
        <div className="flex items-center gap-1.5 mb-1">
          <h4 className="text-sm font-bold text-gray-900">Forecast vs Live ARPU</h4>
          <span className="relative group" data-testid="arpu-comparison-tooltip">
            <Info size={13} className="text-gray-400 cursor-help" />
            <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-72 bg-gray-900 text-gray-100 text-[11px] leading-relaxed rounded-lg p-3 opacity-0 group-hover:opacity-100 transition-opacity z-20 shadow-xl">
              <strong className="text-white">Live ARPU</strong> is measured from real paying
              subscriptions only — test, gifted, insider and orphaned subs are excluded —
              then converted £→$ at the documented rate ({computed.gbp_usd_rate || 1.29}).
              Once the live number stabilises on a real subscriber base, consider replacing
              the forecast assumption with it so investor materials show measured truth.
            </span>
          </span>
        </div>
        <p className="text-xs text-gray-500 mb-4">How the $/user/month assumption compares to what real paying subscribers actually pay.</p>
        {(() => {
          const forecast = Number(draft.monthly_per_user_usd) || 0;
          const live = computed.live_arpu_usd;
          const paying = computed.live_paying_subscribers || 0;
          const perAnchor = (pu) => (pu > 0 && _multiple > 0 && _floor > 0) ? Math.ceil(_floor / (pu * 12 * _multiple)) : null;
          const unlockForecast = perAnchor(forecast);
          const unlockLive = live ? perAnchor(live) : null;
          const deltaPct = (live && forecast) ? ((live - forecast) / forecast) * 100 : null;
          return (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-xl border border-sky-200 bg-sky-50 p-4" data-testid="arpu-forecast-card">
                <p className="text-[10px] uppercase tracking-wider font-bold text-sky-700">Forecast (assumption)</p>
                <p className="text-2xl font-black text-gray-900 mt-1">${forecast.toFixed(2)}<span className="text-xs font-semibold text-gray-500">/user/mo</span></p>
                {unlockForecast && <p className="text-[11px] text-sky-800 mt-1.5">Floor unlocks at <strong>{unlockForecast.toLocaleString()}</strong> users</p>}
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="arpu-live-card">
                <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Live measured ARPU</p>
                {live ? (
                  <>
                    <p className="text-2xl font-black text-gray-900 mt-1">${live.toFixed(2)}<span className="text-xs font-semibold text-gray-500">/user/mo</span></p>
                    <p className="text-[11px] text-emerald-800 mt-1.5">£{computed.live_arpu_gbp?.toFixed(2)} × {computed.gbp_usd_rate} · {paying.toLocaleString()} paying subscriber{paying === 1 ? '' : 's'}{unlockLive ? <> · floor at <strong>{unlockLive.toLocaleString()}</strong> users</> : null}</p>
                  </>
                ) : (
                  <p className="text-sm font-semibold text-gray-500 mt-2">No real paying subscribers yet — shows automatically once the first subscription is live.</p>
                )}
              </div>
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4" data-testid="arpu-delta-card">
                <p className="text-[10px] uppercase tracking-wider font-bold text-gray-600">Live vs Forecast</p>
                {deltaPct !== null ? (
                  <>
                    <p className={`text-2xl font-black mt-1 ${deltaPct >= 0 ? 'text-emerald-700' : 'text-amber-700'}`}>{deltaPct >= 0 ? '+' : ''}{deltaPct.toFixed(1)}%</p>
                    <p className="text-[11px] text-gray-600 mt-1.5">{deltaPct >= 0 ? 'Real subscribers pay above the forecast blend.' : 'Real subscribers pay below the forecast blend — consider revising the assumption.'}</p>
                  </>
                ) : (
                  <p className="text-sm font-semibold text-gray-500 mt-2">—</p>
                )}
              </div>
            </div>
          );
        })()}
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Source note (shown to investors)</label>
        <input className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" value={draft.source_note || ''} onChange={e => setDraft(p => ({ ...p, source_note: e.target.value }))} data-testid="captable-source-note" />
      </div>

      {/* ─── Anchor Reference Matrix ──────────────────────────────────────
          Translates the raw "USER ANCHORS" comma-separated input into a
          structured User → MRR → ARR → Valuation reference table. Computed
          live so any change to anchors / $/user / multiple flows here. */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="anchor-reference-matrix">
        <div className="mb-4">
          <h4 className="text-sm font-bold text-gray-900">Anchor Reference Matrix</h4>
          <p className="text-xs text-gray-500 mt-0.5">
            User milestone → MRR → ARR → Valuation at <strong>{_multiple}×</strong> multiple. Computed live from the inputs above; the first row whose revenue-based valuation surpasses the asset floor is flagged.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <tr>
                <th className="text-left py-2 px-3">User Anchor Target</th>
                <th className="text-right py-2 px-3">Monthly Revenue (MRR)</th>
                <th className="text-right py-2 px-3">Annual Revenue (ARR)</th>
                <th className="text-right py-2 px-3">Valuation @ {_multiple}× Multiple</th>
              </tr>
            </thead>
            <tbody>
              {anchorMatrix.map((row, i) => (
                <tr key={row.users} className={i % 2 === 0 ? 'bg-gray-50' : ''} data-testid={`anchor-row-${row.users}`}>
                  <td className="py-2.5 px-3 text-gray-900 font-medium">{row.users.toLocaleString()} Users</td>
                  <td className="py-2.5 px-3 text-right text-gray-700">{fmtMoneyFull(row.mrr)} / mo</td>
                  <td className="py-2.5 px-3 text-right text-gray-700">{fmtMoneyFull(row.arr)} / yr</td>
                  <td className="py-2.5 px-3 text-right">
                    {row.first_unclamp && (
                      <span className="mr-2 px-2 py-0.5 bg-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-wider rounded-full whitespace-nowrap" data-testid={`anchor-unclamp-${row.users}`}>
                        Unclamps from Floor
                      </span>
                    )}
                    <span className="text-gray-900 font-bold">{fmtMoneyFull(row.raw_val)}</span>
                  </td>
                </tr>
              ))}
              {anchorMatrix.length === 0 && (
                <tr><td colSpan={4} className="py-4 px-3 text-center text-xs text-gray-500" data-testid="anchor-matrix-empty">No anchors configured.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-500 mt-3 leading-relaxed">
          Indicative forward run-rates at <strong>${_perUser}/user/month</strong> across the milestone anchors. Not an offer of securities, not financial guidance. Investors should review the methodological framework below.
        </p>
      </div>

      {/* ─── Dynamic Valuation Engine ─────────────────────────────────────
          Spec: V_E = max(floor, U × $/user × 12 × multiplier). Founder sets
          U here (override) OR uses the live auto-count from db.users where
          role=teen AND subscription_status=active. Everything downstream
          (Pitch Deck, Active Investor Vault, /investors, Monday Pulse) reads
          from the same compute_dynamic_valuation() call. */}
      <div className="bg-gradient-to-br from-indigo-900 via-purple-900 to-fuchsia-900 rounded-2xl p-6 text-white shadow-xl" data-testid="captable-engine">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-base font-black tracking-tight">Dynamic Valuation Engine</h4>
            <p className="text-xs text-indigo-200 mt-0.5">{computed.formula || 'V_E = max(floor, U × $/user × 12 × multiple)'}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
              computed.is_floor_active ? 'bg-amber-400/20 text-amber-200 border border-amber-300/30' : 'bg-emerald-400/20 text-emerald-200 border border-emerald-300/30'
            }`} data-testid="engine-status-pill">
              {computed.is_floor_active ? 'Floor active' : 'Revenue-scaled'}
            </span>
            <button
              onClick={() => setShowModelInfo(s => !s)}
              className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-white/10 hover:bg-white/20 text-indigo-100 border border-white/20 transition"
              data-testid="engine-model-info-toggle">
              {showModelInfo ? '× Close model info' : 'ℹ Model info'}
            </button>
          </div>
        </div>

        {/* Live Active Users (U) — founder override + auto */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div className="md:col-span-2 bg-white/10 rounded-xl p-4">
            <label className="block text-xs uppercase tracking-wider text-indigo-200 mb-2">Paying Subscribers (U)</label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                placeholder={`auto: ${computed.live_users_auto ?? 0}`}
                value={draft.live_active_users_override === '__CLEAR__' ? '' : (draft.live_active_users_override ?? (cfg?.live_active_users_override ?? ''))}
                onChange={e => setDraft(p => ({ ...p, live_active_users_override: e.target.value === '' ? '' : Number(e.target.value) }))}
                className="flex-1 px-3 py-2 text-base font-bold text-gray-900 rounded-lg"
                data-testid="captable-live-users-override"
              />
              <button
                onClick={() => setDraft(p => ({ ...p, live_active_users_override: '__CLEAR__' }))}
                className="px-3 py-2 bg-white/15 hover:bg-white/25 text-xs font-semibold rounded-lg"
                title="Clear override and use the live auto source"
                data-testid="captable-use-db-count"
              >
                Use live ({computed.live_users_auto_source === 'stripe_active_subscriptions' ? 'Stripe paid subs' : computed.live_users_auto_source === 'paying_subscriptions' ? 'paid subs' : computed.live_users_auto_source === 'subscription_count' ? 'paid subs' : 'auto'}: {(computed.live_users_auto ?? 0).toLocaleString()})
              </button>
            </div>
            <p className="text-xs text-indigo-200 mt-2">
              Currently using <strong>{
                computed.live_users_source === 'override' ? 'founder override'
                : computed.live_users_source === 'stripe_active_subscriptions' ? 'Stripe — live active paid subscriptions'
                : computed.live_users_source === 'paying_subscriptions' ? 'billing records — paying subscriptions'
                : computed.live_users_source === 'subscription_count' ? 'payment-gateway active count'
                : 'no live source yet'
              }</strong> · U = <strong>{(computed.live_users ?? 0).toLocaleString()}</strong> · auto = {(computed.live_users_auto ?? 0).toLocaleString()}
            </p>
          </div>
          <div className="bg-white/10 rounded-xl p-4">
            <label className="block text-xs uppercase tracking-wider text-indigo-200 mb-1">Milestone</label>
            <div className="text-2xl font-black" data-testid="captable-milestone-level">Level {computed.milestone_level ?? 0}</div>
            <div className="text-xs text-indigo-200 mt-1">{computed.milestone_label || '—'}</div>
          </div>
        </div>

        {/* Computed valuation worked example */}
        <div className="bg-black/30 rounded-xl p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-center">
            <div data-testid="engine-mrr">
              <div className="text-xs uppercase tracking-wider text-indigo-200">MRR</div>
              <div className="text-lg font-bold">{fmtUsdShort(computed.mrr_usd)}</div>
              <div className="text-xs text-indigo-300">U × ${computed.monthly_per_user_usd ?? 0}</div>
            </div>
            <div data-testid="engine-arr">
              <div className="text-xs uppercase tracking-wider text-indigo-200">ARR</div>
              <div className="text-lg font-bold">{fmtUsdShort(computed.total_arr_usd ?? computed.arr_usd)}</div>
              <div className="text-xs text-indigo-300">subs ARR + licence ARR</div>
            </div>
            <div data-testid="engine-raw-rev-val">
              <div className="text-xs uppercase tracking-wider text-indigo-200">Raw rev × {computed.revenue_multiple ?? 0}×</div>
              <div className="text-lg font-bold">{fmtUsdShort(computed.raw_revenue_valuation_usd)}</div>
              <div className="text-xs text-indigo-300">ARR × multiple</div>
            </div>
            <div data-testid="engine-floor">
              <div className="text-xs uppercase tracking-wider text-indigo-200">Asset Floor</div>
              <div className="text-lg font-bold">{fmtUsdShort(computed.floor_usd)}</div>
              <div className="text-xs text-indigo-300">Intrinsic moat</div>
            </div>
          </div>
          <div className="border-t border-white/20 mt-4 pt-4 flex items-center justify-between">
            <div className="text-xs uppercase tracking-wider text-indigo-200">Benchmark Valuation</div>
            <div className="text-3xl font-black" data-testid="engine-enterprise-value">{fmtUsdShort(computed.enterprise_valuation_usd)}</div>
          </div>
        </div>

        <p className="text-xs text-indigo-300 mt-3 leading-relaxed">
          The engine never drops below the asset floor (intrinsic capital-efficiency value). U counts paying B2C subscribers only — never free users or licence seats; licence revenue is added separately as ARR. When (U × $/user × 12 + licence ARR) × multiple exceeds the floor, the engine unclamps and scales upward{computed.pivot_subscribers ? ` — the floor releases at ~${Number(computed.pivot_subscribers).toLocaleString()} paying subscribers` : ''}.
          This value flows automatically into the Pitch Deck, Active Investor Vault, public <code>/investors</code> page, and the Monday 8am HiBFF Pulse.
        </p>

        {/* ─── Methodological Framework disclosure ────────────────────────
            Toggleable, investor-readable copy that justifies the $11.5M
            benchmark under the Berkus Method (pre-revenue) with IP and
            investor-infrastructure premiums. The seven bucket values sum
            to the configured floor. */}
        {showModelInfo && (
          <div className="mt-5 bg-white/95 text-gray-900 rounded-xl p-5 shadow-inner" data-testid="engine-model-info">
            <h5 className="text-sm font-black tracking-tight mb-1">📘 Methodological Framework: Berkus Method + IP &amp; Infrastructure Premiums</h5>
            <p className="text-xs text-gray-600 mb-4">For compliance officers, legal counsel, and institutional fund managers conducting due diligence.</p>

            <div className="mb-4">
              <h6 className="text-xs font-bold uppercase tracking-wider text-gray-800 mb-1.5">What this Framework Represents</h6>
              <p className="text-xs text-gray-700 leading-relaxed">
                The Berkus Method is a recognised valuation framework that prices risk reduction rather than projected revenue. Each bucket reflects a concrete, verifiable de-risking milestone already achieved. {(computed.live_users ?? 0) > 0
                  ? 'HiBFF is now post-revenue — parents pay after First Steps —'
                  : 'HiBFF is Stripe live and taking payments (first paid subscriptions pending) —'} and applies the extended form: five classic Berkus buckets plus an IP premium and an investor-infrastructure premium, intentionally set at the fair-to-optimistic end of the defensible range, cross-checked against a revenue multiple. This is an honest benchmark, not a forward-multiple gamble.
              </p>
            </div>

            <div className="mb-4">
              <h6 className="text-xs font-bold uppercase tracking-wider text-gray-800 mb-1.5">The Seven Buckets Behind the {fmtMoneyFull(_floor)} Benchmark</h6>
              <p className="text-xs text-gray-700 leading-relaxed mb-3">
                The benchmark valuation engine enforces a floor of <strong>{fmtMoneyFull(_floor)}</strong> — the basis for the $11.5M ASA/SAFE cap — built from seven founder-approved buckets. The dollar values are locked; the supporting evidence beneath each refreshes from live platform data:
              </p>
              <div className="space-y-2.5">
                {[
                  { name: 'Soundness of the Idea', value: 1_500_000, desc: 'Safeguarding-first social-skills practice for teens — a post-Covid confidence gap identified with schools and parents. Warm Handover (consent-based crisis support) specified and live; HiBFF Charter, Parent Compact and Teen Digital Compact published. 72% of US teens have used an AI companion; 1 in 3 chose AI over a person for a serious matter (Common Sense Media, July 2025). Crisis-protocol laws arriving: New York (Nov 2025), California SB 243 (Jan 2026), EU AI Act Article 50 (Aug 2026), UK (end 2026), Oregon and Washington (Jan 2027).' },
                  { name: 'Prototype / Working Product', value: 2_000_000, desc: 'Not a prototype — a live production platform at hibff.com: teen, parent and adult tracks, AvA conversation library with animated companions, voice + text stack (Claude, Postmark, Twilio, Stripe) all wired and operating. 1 live crisis handover completed through the Warm Handover. Live users in 4 countries, all organic; 3,343 organic leads; $0 acquisition cost. Critical response under 900ms; 25 AI-failure tests run on every code change. B2B/B2G white-label dashboards 95% ready.' },
                  { name: 'Quality of the Team', value: 1_500_000, desc: 'Founder-led with full-stack engineering execution. Rebecca Parson, co-founder (20%), with full operational access for continuity. A dedicated Emergent support engineer who monitors the platform. Cap table: Robert 70%, Rebecca 20%, 10% reserved for the future team. Independent advisory inputs on schools, charities and governance, and charter / safety / crisis-handover governance in place.' },
                  { name: 'Strategic Relationships', value: 1_500_000, desc: 'Crisis-responder partnerships: Samaritans plus 2 further support groups. Army pilot (armed forces), Sunway University leadership engaged, FCDO Association founding members. Warm Handover white paper pitched to UK and US regulators, including the 5Rights Foundation. US Schools Outreach lists prepared (4 regions, 124 cities), and the HiBFF Gives charity programme structured around mayors and community leaders.' },
                  { name: 'Product Rollout / Go-To-Market', value: 1_500_000, desc: 'Plans: First Steps (free), Steady $19.99/£16, Together $35.99/£28, Brave $247.99/£188 a year, plus Hearts packs. Licences: B2B and B2G licences of 200 seats at $12 per seat per month, paid yearly in advance, on 3-to-5-year terms. Live worldwide in local currencies; the US is the phase-one market. Full subscription + parent-approval payment stack operating on Stripe live mode.' },
                  { name: 'IP Premium', value: 3_000_000, desc: 'Three live UK patent applications filed at the UK IPO (GB2617027.4 · GB2622892.4 · GB2622997.1), the proprietary Warm Handover, the published "Warm Handover" white paper — a safety standard for AI conversations with young people, licensable to other platforms — the Education Challenge Engine (70 OCEAN-mapped variants), and the COPPA/FERPA-aligned safety architecture governed by NVIDIA NeMo Guardrails.' },
                  { name: 'Investor Infrastructure Premium', value: 500_000, desc: 'TOTP-gated Secure Vault with founder command tabs, Data Room engine (prospect + permanent investor rooms), weekly Investor Pulse and Monday Founder Pulse reporting — institutional-grade reporting rails already running.' },
                ].map((m, i) => (
                  <div key={i} className="border-l-2 border-indigo-300 pl-3 py-1" data-testid={`moat-component-${i}`}>
                    <div className="flex items-center justify-between mb-0.5">
                      <span className="text-xs font-bold text-gray-900">{i + 1}. {m.name}</span>
                      <span className="text-xs font-bold text-indigo-700">{fmtMoneyFull(m.value)} Bucket Value</span>
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed">{m.desc}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-3 italic">
                Sum of bucket values: <strong>{fmtMoneyFull(1_500_000 + 2_000_000 + 1_500_000 + 1_500_000 + 1_500_000 + 3_000_000 + 500_000)}</strong> — reconciles to the configured benchmark floor.
              </p>
              <p className="text-xs text-gray-600 mt-2 leading-relaxed" data-testid="berkus-transparency-line">
                The classic Berkus method values five factors at up to ~$500K each. HiBFF&apos;s extended version applies higher values plus IP and infrastructure premiums, and is cross-checked against a revenue multiple: at ~4,000 paying subscribers (~$1.27M ARR), a 9× multiple gives the same $11.5M.
              </p>
            </div>

            <div className="mb-2">
              <h6 className="text-xs font-bold uppercase tracking-wider text-gray-800 mb-1.5">How the Valuation System Dynamically Scales</h6>
              <p className="text-xs text-gray-700 leading-relaxed mb-2">
                The system engine monitors paying B2C subscribers (<em>U</em>) via the secure payment gateway APIs — free users and licence seats are never counted as <em>U</em>. Licence revenue (B2B/B2G and schools &amp; universities licences, seats × $12 × 12) is added separately as ARR. The baseline verification loop:
              </p>
              <div className="bg-gray-900 text-indigo-100 rounded-lg px-4 py-3 font-mono text-[12px] text-center mb-2" data-testid="model-info-formula">
                V<sub>E</sub> = max( {fmtMoneyFull(_floor)},  (U × ${_perUser} × 12 + licence ARR) × {_multiple} )
              </div>
              <p className="text-xs text-gray-700 leading-relaxed">
                While live ARR totals remain below the asset threshold, the engine clamps the platform&apos;s value to the {fmtMoneyFull(_floor)} floor, defending the baseline value created by the technical build. The floor releases at approximately <strong>{(_perUser > 0 && _multiple > 0) ? Math.ceil(_floor / (_perUser * 12 * _multiple)).toLocaleString() : '—'} paying subscribers</strong> (at zero licence ARR); beyond that point the calculation automatically unclamps into an active ARR multiple-expansion framework.
              </p>
            </div>

            <p className="text-xs text-gray-500 mt-4 leading-relaxed border-t border-gray-200 pt-3">
              This methodological disclosure is for institutional due diligence only. Not an offer of securities. Not financial guidance. Numbers update in real time as the founder edits the canonical cap table inputs.
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          Last updated: {cfg?.updated_at ? new Date(cfg.updated_at).toLocaleString() : '—'}{cfg?.updated_by ? ` · by ${cfg.updated_by}` : ''}
          {savedAt && <span className="text-emerald-600 ml-2">Saved at {savedAt}</span>}
        </p>
        <button onClick={save} disabled={saving} className="px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-lg disabled:opacity-50" data-testid="captable-save">
          {saving ? 'Saving…' : 'Save & broadcast'}
        </button>
      </div>
    </div>
  );
};

// ─── CONTACT LOG TAB ─────────────────────────────────────────────────────────

const ContactLogTab = () => {
  const [logs, setLogs] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [filterId, setFilterId] = useState('');
  const [form, setForm] = useState({ investor_id: '', investor_name: '', method: 'email', subject: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const q = filterId ? `?investor_id=${encodeURIComponent(filterId)}` : '';
      const [l, inv] = await Promise.all([
        apiClient.request(`/api/vault/contact-log${q}`),
        apiClient.request('/api/vault/investors').catch(() => ({ investors: [] })),
      ]);
      setLogs(l.logs || []);
      setInvestors(inv.investors || []);
    } catch (e) { setErr(e.message || 'Load failed'); }
  }, [filterId]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.investor_id) { setErr('Pick an investor'); return; }
    setSaving(true); setErr('');
    try {
      await apiClient.request('/api/vault/contact-log', { method: 'POST', body: JSON.stringify(form) });
      setForm({ investor_id: '', investor_name: '', method: 'email', subject: '', notes: '' });
      load();
    } catch (e) { setErr(e.message || 'Save failed'); }
    setSaving(false);
  };

  return (
    <div className="space-y-5" data-testid="contactlog-tab">
      <div className="bg-gradient-to-r from-orange-900 to-amber-900 rounded-2xl p-6 text-white">
        <h3 className="text-lg font-black mb-1">Contact Log</h3>
        <p className="text-xs text-amber-200 max-w-2xl">Chronological record of investor / BD interactions. Filter by investor to see a single relationship&apos;s history.</p>
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <h4 className="text-sm font-bold text-gray-900 mb-3">Log a new interaction</h4>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
          <select className="md:col-span-3 px-2 py-2 text-sm border border-gray-200 rounded-lg" value={form.investor_id} onChange={e => {
            const i = investors.find(x => x.investor_id === e.target.value);
            setForm(p => ({ ...p, investor_id: e.target.value, investor_name: i?.name || '' }));
          }} data-testid="contactlog-investor-select">
            <option value="">— Pick investor —</option>
            {investors.map(i => <option key={i.investor_id} value={i.investor_id}>{i.name}</option>)}
          </select>
          <select className="md:col-span-2 px-2 py-2 text-sm border border-gray-200 rounded-lg" value={form.method} onChange={e => setForm(p => ({ ...p, method: e.target.value }))} data-testid="contactlog-method">
            <option value="email">Email</option>
            <option value="call">Call</option>
            <option value="meeting">Meeting</option>
            <option value="conference">Conference</option>
            <option value="demo">Demo</option>
            <option value="other">Other</option>
          </select>
          <input className="md:col-span-3 px-2 py-2 text-sm border border-gray-200 rounded-lg" placeholder="Subject" value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} data-testid="contactlog-subject" />
          <input className="md:col-span-3 px-2 py-2 text-sm border border-gray-200 rounded-lg" placeholder="Notes / outcome" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} data-testid="contactlog-notes" />
          <button onClick={submit} disabled={saving} className="md:col-span-1 px-3 py-2 bg-gray-900 text-white text-sm font-semibold rounded-lg disabled:opacity-50" data-testid="contactlog-add">
            {saving ? '…' : 'Add'}
          </button>
        </div>
        {err && <p className="text-xs text-red-600 mt-2" data-testid="contactlog-error">{err}</p>}
      </div>

      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-bold text-gray-900">History ({logs.length})</h4>
          <select className="px-2 py-1.5 text-xs border border-gray-200 rounded-lg" value={filterId} onChange={e => setFilterId(e.target.value)} data-testid="contactlog-filter">
            <option value="">All investors</option>
            {investors.map(i => <option key={i.investor_id} value={i.investor_id}>{i.name}</option>)}
          </select>
        </div>
        {logs.length > 0 && (
          <ul className="divide-y divide-gray-100">
            {logs.map(l => (
              <li key={l.log_id} className="py-3 grid grid-cols-12 gap-2 text-sm" data-testid={`contactlog-row-${l.log_id}`}>
                <span className="col-span-3 text-gray-900 font-medium">{l.investor_name || l.investor_id}</span>
                <span className="col-span-2 text-gray-500 uppercase text-xs font-semibold">{l.method}</span>
                <span className="col-span-3 text-gray-700">{l.subject || '—'}</span>
                <span className="col-span-3 text-gray-500 text-xs">{l.notes || ''}</span>
                <span className="col-span-1 text-gray-600 text-xs text-right">{l.created_at ? new Date(l.created_at).toLocaleDateString() : ''}</span>
              </li>
            ))}
          </ul>
        )}
        {logs.length < 3 && (
          <VaultSamplePreview accent="#F59E0B" title="Investor & partner interaction history" addLabel="Click '+ Log Interaction' above" samples={SAMPLES_CONTACT_LOG} dataTestid="contactlog-empty" />
        )}
      </div>
    </div>
  );
};

// ─── SETTINGS TAB ────────────────────────────────────────────────────────────

const SettingsTab = () => {
  const [settings, setSettings] = useState({ digest_enabled: false, digest_frequency: 'weekly' });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState('');
  const [emergencyCode, setEmergencyCode] = useState(null);
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);

  useEffect(() => {
    apiClient.request('/api/vault/settings').then(setSettings).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true); setErr('');
    try {
      await apiClient.request('/api/vault/settings', { method: 'POST', body: JSON.stringify(settings) });
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e) { setErr(e.message || 'Save failed'); }
    setSaving(false);
  };

  return (
    <div className="space-y-5" data-testid="settings-tab">
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-6 text-white">
        <h3 className="text-lg font-black mb-1">Vault Settings</h3>
        <p className="text-xs text-slate-300 max-w-2xl">Digest schedule, default Data Room expiration, and notification preferences. The TOTP auto-lock timeout is configured per session at unlock-time.</p>
      </div>

      {err && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700" data-testid="settings-error">{err}</div>}

      {/* Sprint 189 — Co-Operator slot (Rebecca Parson). Pre-provisioned 2nd
          Master Admin with identical permissions; one green button fires the
          two-email onboarding flow (stub or LIVE). */}
      <CoOperatorCard />

      <div className="bg-white border border-gray-200 rounded-2xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-bold text-gray-900">Vault Digest emails</p>
            <p className="text-xs text-gray-500">Automated digest of Data Room activity + investor engagement scores.</p>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="settings-digest-toggle">
            <input type="checkbox" checked={!!settings.digest_enabled} onChange={e => setSettings(p => ({ ...p, digest_enabled: e.target.checked }))} className="w-4 h-4" />
            <span className="text-xs font-semibold">{settings.digest_enabled ? 'On' : 'Off'}</span>
          </label>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Digest frequency</label>
          <select className="px-3 py-2 text-sm border border-gray-200 rounded-lg" value={settings.digest_frequency || 'weekly'} onChange={e => setSettings(p => ({ ...p, digest_frequency: e.target.value }))} data-testid="settings-digest-freq">
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Default Data Room expiration (hours, prospect rooms)</label>
          <input type="number" min="1" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" value={settings.default_dataroom_expires_hours || 72} onChange={e => setSettings(p => ({ ...p, default_dataroom_expires_hours: Number(e.target.value) || 72 }))} data-testid="settings-default-dr-expiry" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Vault Daily Pulse · cron hour (UTC, 0–23)</label>
          <input type="number" min="0" max="23" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" value={settings.vault_pulse_hour_utc ?? 7} onChange={e => setSettings(p => ({ ...p, vault_pulse_hour_utc: Math.max(0, Math.min(23, Number(e.target.value) || 0)) }))} data-testid="settings-pulse-hour" />
          <p className="text-[10px] text-gray-500 mt-0.5">Overrides the static VAULT_PULSE_HOUR_UTC env. Picked up on the next 30-min worker tick.</p>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-wider text-gray-500 mb-1">TOTP auto-lock (idle minutes)</label>
          <input type="number" min="1" max="120" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" value={settings.totp_auto_lock_minutes ?? 15} onChange={e => setSettings(p => ({ ...p, totp_auto_lock_minutes: Math.max(1, Math.min(120, Number(e.target.value) || 15)) }))} data-testid="settings-totp-autolock" />
        </div>
      </div>

      <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4" data-testid="settings-emergency-block">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <div className="text-xs uppercase tracking-wider text-amber-700 font-bold">Emergency Access Code</div>
            <p className="text-xs text-amber-900 mt-0.5">One-shot recovery code for the Vault. Generated, hashed, and displayed once — never persisted in plaintext.</p>
          </div>
          <button
            type="button"
            onClick={async () => {
              try {
                const r = await apiClient.request('/api/vault/settings/rotate-emergency-code', { method: 'POST', body: JSON.stringify({}) });
                setEmergencyCode(r.emergency_code);
              } catch (e) { setEmergencyCode(`error: ${e.message || 'rotate failed'}`); }
            }}
            className="px-3 py-1.5 bg-amber-600 text-white text-xs font-bold rounded-lg hover:bg-amber-700"
            data-testid="settings-rotate-emergency"
          >
            Rotate code
          </button>
        </div>
        {emergencyCode && (
          <pre className="bg-white border border-amber-200 rounded-md p-2 text-xs font-mono select-all" data-testid="settings-emergency-code">{emergencyCode}</pre>
        )}
      </div>

      <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4" data-testid="settings-diagnostics-block">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-700 font-bold">Stage Live Credentials — Diagnostic Panel</div>
            <p className="text-xs text-slate-700 mt-0.5">One-shot smoke test of DocuSign · Slack · Telegram · Postmark. Sends a real ping with whatever creds are currently in <code>backend/.env</code>. Use this the moment you flip a live-credential flag.</p>
          </div>
          <button
            type="button"
            disabled={diagLoading}
            onClick={async () => {
              setDiagLoading(true);
              try {
                const r = await apiClient.request('/api/vault/settings/staging-diagnostics', { method: 'POST', body: JSON.stringify({}) });
                setDiagnostics(r);
              } catch (e) {
                setDiagnostics({ error: e.message || 'diagnostic failed' });
              } finally {
                setDiagLoading(false);
              }
            }}
            className="px-3 py-1.5 bg-slate-900 text-white text-xs font-bold rounded-lg hover:bg-slate-800 disabled:opacity-50"
            data-testid="settings-run-diagnostics"
          >
            {diagLoading ? 'Pinging…' : 'Run diagnostics'}
          </button>
        </div>
        {diagnostics && diagnostics.channels && (
          <div className="mt-2 space-y-1.5" data-testid="settings-diagnostics-results">
            {Object.entries(diagnostics.channels).map(([channel, res]) => {
              const bg = res.status === 'live' ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                : res.status === 'stub' ? 'bg-amber-50 border-amber-200 text-amber-900'
                : res.status === 'missing' ? 'bg-slate-100 border-slate-200 text-slate-700'
                : 'bg-rose-50 border-rose-200 text-rose-900';
              return (
                <div key={channel} className={`flex items-start justify-between gap-3 p-2 rounded-md border ${bg}`} data-testid={`diagnostics-row-${channel}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold uppercase tracking-wider">{channel}</div>
                    <div className="text-xs mt-0.5 truncate" title={res.detail}>{res.detail}</div>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/70 border border-current/30" data-testid={`diagnostics-status-${channel}`}>
                    {res.status}
                  </span>
                </div>
              );
            })}
            <p className="text-[10px] text-slate-500 mt-1">Checked at {diagnostics.checked_at} by {diagnostics.checked_by}.</p>
          </div>
        )}
        {diagnostics && diagnostics.error && (
          <p className="text-xs text-rose-600 mt-2" data-testid="diagnostics-error">Error: {diagnostics.error}</p>
        )}
      </div>

      <div className="flex items-center justify-end gap-3">
        {savedAt && <span className="text-xs text-emerald-600">Saved at {savedAt}</span>}
        <button onClick={save} disabled={saving} className="px-4 py-2 bg-gray-900 text-white text-sm font-semibold rounded-lg disabled:opacity-50" data-testid="settings-save">
          {saving ? 'Saving…' : 'Save settings'}
        </button>
      </div>
    </div>
  );
};

// ─── PITCH DECK TAB ──────────────────────────────────────────────────────────
// Embeds the existing /admin/vault/export page inside the Vault shell so the
// founder never leaves the TOTP-gated surface. The standalone route remains
// available for direct PDF export.

const VAULT_DECKS = [
  { id: 'overview_manifesto', label: 'Investor Manifesto — Nov 2026', sub: '2-page tease document' },
  { id: 'overview_safe_deck', label: 'SAFE Pitch Deck', sub: 'Nov 2026 final · 14 slides' },
  { id: 'overview_asa_deck', label: 'ASA Pitch Deck', sub: 'Nov 2026 final · 14 slides' },
];

const PitchDeckTab = () => {
  const [busyDeck, setBusyDeck] = useState(null);
  const [deckError, setDeckError] = useState('');
  const openDeck = async (id) => {
    setBusyDeck(id);
    setDeckError('');
    try {
      const token = localStorage.getItem('hibff_auth_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vault/diligence/files/${id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('download failed');
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank', 'noopener');
    } catch {
      setDeckError('Could not open that deck — please try again.');
    } finally {
      setBusyDeck(null);
    }
  };
  return (
  <div className="space-y-4" data-testid="pitchdeck-tab">
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="uploaded-decks-card">
      <h3 className="text-sm font-black text-gray-900 mb-1">Uploaded Investor Decks — Nov 2026 finals</h3>
      <p className="text-xs text-gray-500 mb-3">The official investor documents — the same files served in every investor Data Room under &quot;a · Pitch Deck &amp; Overview&quot; and linked from the Investor Portal. Click to open the original PDF.</p>
      <div className="grid sm:grid-cols-3 gap-2">
        {VAULT_DECKS.map(d => (
          <button key={d.id} onClick={() => openDeck(d.id)} disabled={busyDeck === d.id}
            className="flex items-start gap-2 text-left border border-gray-200 rounded-xl px-3 py-2.5 hover:border-purple-400 hover:bg-purple-50/40 transition-colors disabled:opacity-60"
            data-testid={`vault-deck-${d.id}`}>
            {busyDeck === d.id
              ? <Loader2 size={14} className="text-purple-600 mt-0.5 flex-shrink-0 animate-spin" />
              : <FileText size={14} className="text-purple-600 mt-0.5 flex-shrink-0" />}
            <span>
              <span className="block text-[11px] font-semibold text-gray-800 leading-snug">{d.label}</span>
              <span className="block text-[10px] text-gray-400 mt-0.5">{d.sub}</span>
            </span>
          </button>
        ))}
      </div>
      {deckError && <p className="text-[11px] text-rose-500 mt-2 font-semibold" data-testid="vault-deck-error">{deckError}</p>}
    </div>
  </div>
  );
};

// ─── SCHOOLS PROGRAMME TAB ───────────────────────────────────────────────────
// Same pattern — embeds /admin/schools-engine inside the Vault shell so
// founder operations stay TOTP-gated and the founder shell remains visible.

const SchoolsTab = () => (
  <div className="space-y-4" data-testid="schools-tab">
    <div className="bg-gradient-to-r from-fuchsia-900 to-purple-900 rounded-2xl p-6 text-white">
      <h3 className="text-lg font-black mb-1">Schools Programme</h3>
      <p className="text-xs text-fuchsia-200 max-w-2xl">Full Schools Command Centre rendered inside the Vault. Use &quot;Open in new tab&quot; for full-screen operations work.</p>
      <a href="/admin/schools-engine" target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 bg-white/15 hover:bg-white/25 text-xs font-semibold rounded-lg"
        data-testid="schools-open-standalone">
        Open in new tab →
      </a>
    </div>
    <div className="rounded-2xl border border-gray-200 overflow-hidden bg-white" style={{ minHeight: '78vh' }}>
      <iframe
        src="/admin/schools-engine?embed=1"
        title="Schools Programme"
        className="w-full"
        style={{ height: '78vh', border: 0 }}
        data-testid="schools-iframe"
      />
    </div>
  </div>
);

export default SecureVault;
